/* global BigInt */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { formatEther, formatUnits, isAddress, parseEther } from "viem";
import { ethers } from "ethers";
import { Link, useLocation } from "react-router-dom";

import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import useWallet from "../../hooks/useWallet";
import launchpadAbi from "../../contracts/bobPresaleLaunchpadAbi";
import "./launchpad.scss";

const CHAIN_ID = 143;
const DEFAULT_RPC = process.env.REACT_APP_MONAD_MAINNET_RPC_URL || "https://rpc.monad.xyz";
const DEFAULT_LAUNCHPAD_ADDRESS =
  process.env.REACT_APP_LAUNCHPAD_CONTRACT_ADDRESS ||
  process.env.REACT_APP_BOB_LAUNCHPAD_ADDRESS ||
  "";

const PINATA_GATEWAY = (process.env.REACT_APP_PINATA_GATEWAY || "https://gateway.pinata.cloud/ipfs").trim();
const DASHBOARD_API_BASE = (process.env.REACT_APP_DASHBOARD_API_BASE || "").trim();
const TOOLS_API_BASE = (process.env.REACT_APP_TOOLS_API_BASE || "").trim();
const LAUNCHPAD_BACKEND_URL = (
  process.env.REACT_APP_LAUNCHPAD_BACKEND_URL ||
  DASHBOARD_API_BASE ||
  TOOLS_API_BASE
).trim();
const RESOLVED_TOOLS_API_BASE = (
  TOOLS_API_BASE ||
  DASHBOARD_API_BASE ||
  LAUNCHPAD_BACKEND_URL
).trim();

const BPS = 10_000n;
const DEFAULT_PRESALE_PARTICIPATION_FEE_BPS = 200;
const DEFAULT_CURVE_PROTOCOL_FEE_BPS = 100n;
const NAD_CURVE_CONFIG = {
  // From Nad V2 integration docs (mainnet quote config).
  virtualQuoteReserveWei: 70000000000000000000000n,
  virtualTokenReserveWei: 1060569000000000000000000000n,
  minTokenReserveWei: 251660440677966101694915255n
};
const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const HOUR_12_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
const MERIDIEM_OPTIONS = ["AM", "PM"];
const DEFAULT_PRESALE_END_OFFSET_MS = 24 * 60 * 60 * 1000;
const LAUNCHPAD_DEV_WALLET = "0x334270D541d4F9a8717d30690C72159724094323".trim().toLowerCase();
const LAUNCHPAD_DEV_WALLET_SET = new Set([LAUNCHPAD_DEV_WALLET]);
const LAUNCHPAD_ACTIVITY_RAIL_PLACEHOLDER = false;
const LAUNCHPAD_ACTIVITY_RAIL_PLACEHOLDER_ITEMS = [
  { actor: "0x23...2384", action: "bought", token: "TEST", image: "/bass/bobtrade.png" },
  { actor: "0x91...7abc", action: "created", token: "BOBX", image: "/bass/bobmainmascot.webp" },
  { actor: "0xa4...19de", action: "finalized", token: "WOOF", image: "/bass/boblegacy.png" },
  { actor: "0x7b...4421", action: "bought", token: "MOON", image: "/bass/bobbonfire.png" },
  { actor: "0x5f...e903", action: "created", token: "MONCAT", image: "/bass/boblove.png" },
  { actor: "0xc8...11aa", action: "finalized", token: "TEST1", image: "/bass/bobrl.png" }
];
const CREATE_FEE_OPTION_DESCRIPTIONS = {
  burn: "Uses a portion of trading fees to buy back tokens and permanently remove them from supply.",
  lp: "Uses a portion of trading fees to strengthen liquidity and support more stable market conditions.",
  creator: "Allocates a portion of trading fees to support the creator behind the token over time.",
  gift: "Routes a portion of trading fees to a designated recipient chosen by the token creator.",
  dividend: "Uses a portion of trading fees to buy reward tokens and distribute them proportionally to eligible holders."
};
const DEFAULT_NAD_QUOTE_TOKEN = "0x3bd359C1119dA7Da1D913D1C4D2B7c461115433A";
const MAX_DIVIDEND_REWARD_TOKENS = 5;
let dividendRewardRowId = 0;
const MOBILE_SORT_LABEL_BREAKPOINT = 500;
const LAUNCHPAD_BANNER_ASSET_VERSION = "2026-06-25-1";
const LAUNCHPAD_ROOT_HOSTS = new Set(["gbob.fun", "www.gbob.fun"]);
const LAUNCHPAD_ACTIVITY_CACHE_KEY = "bob_launchpad_activity_cache_v1";
const LAUNCHPAD_RECENT_SEARCHES_KEY = "bob_launchpad_recent_searches_v1";
const LAUNCHPAD_RECENT_VIEWED_KEY = "bob_launchpad_recent_viewed_v1";
const LAUNCHPAD_SEARCH_DEBOUNCE_MS = 1000;
const LAUNCHPAD_SEARCH_RESULT_LIMIT = 5;
const LAUNCHPAD_SEARCH_RESULT_LIMIT_EXPANDED = 50;
const HIDDEN_LAUNCHPAD_POOL_IDS = new Set(["24"]);

function normalizeLaunchpadPoolIdValue(value) {
  return String(value == null ? "" : value).trim();
}

function isHiddenLaunchpadPoolId(poolId) {
  return HIDDEN_LAUNCHPAD_POOL_IDS.has(normalizeLaunchpadPoolIdValue(poolId));
}

function isLaunchpadRootHost() {
  if (typeof window === "undefined") return false;
  return LAUNCHPAD_ROOT_HOSTS.has(String(window.location.hostname || "").toLowerCase());
}

function readEdgeHydratedLaunchpadActivityFeed() {
  if (typeof window === "undefined") return;
  try {
    const hydrated = Array.isArray(window.__BOB_LAUNCHPAD_INITIAL_ACTIVITY__)
      ? window.__BOB_LAUNCHPAD_INITIAL_ACTIVITY__
      : [];
    return hydrated
      .filter((item) => item && typeof item === "object" && item.id)
      .slice(-20);
  } catch {
    return [];
  }
}

function normalizeCachedLaunchpadActivity(items) {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => item && typeof item === "object" && item.id)
    .slice(-20);
}

function readCachedLaunchpadActivityFeed() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(LAUNCHPAD_ACTIVITY_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return normalizeCachedLaunchpadActivity(parsed);
  } catch {
    return [];
  }
}

function seedInitialLaunchpadActivityFeed() {
  const edgeHydrated = readEdgeHydratedLaunchpadActivityFeed();
  if (edgeHydrated?.length) return edgeHydrated;
  return readCachedLaunchpadActivityFeed();
}

function writeCachedLaunchpadActivityFeed(items) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      LAUNCHPAD_ACTIVITY_CACHE_KEY,
      JSON.stringify(normalizeCachedLaunchpadActivity(items))
    );
  } catch {}
}

function normalizeRecentSearches(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .slice(0, 6);
}

function readRecentLaunchpadSearches() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(LAUNCHPAD_RECENT_SEARCHES_KEY);
    if (!raw) return [];
    return normalizeRecentSearches(JSON.parse(raw));
  } catch {
    return [];
  }
}

function writeRecentLaunchpadSearches(items) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      LAUNCHPAD_RECENT_SEARCHES_KEY,
      JSON.stringify(normalizeRecentSearches(items))
    );
  } catch {}
}

function normalizeRecentViewedPresales(items) {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => item && typeof item === "object" && item.id)
    .slice(0, 6);
}

function readRecentViewedPresales() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(LAUNCHPAD_RECENT_VIEWED_KEY);
    if (!raw) return [];
    return normalizeRecentViewedPresales(JSON.parse(raw));
  } catch {
    return [];
  }
}

function writeRecentViewedPresales(items) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      LAUNCHPAD_RECENT_VIEWED_KEY,
      JSON.stringify(normalizeRecentViewedPresales(items))
    );
  } catch {}
}

function isLaunchpadAddress(value) {
  try {
    return isAddress(value);
  } catch {
    return false;
  }
}

function extractLaunchpadErrorText(error) {
  const seen = new Set();
  const queue = [error];
  while (queue.length) {
    const current = queue.shift();
    if (!current || seen.has(current)) continue;
    if (typeof current === "object") {
      seen.add(current);
    }
    const message = typeof current === "string"
      ? current
      : typeof current?.shortMessage === "string" && current.shortMessage
        ? current.shortMessage
        : typeof current?.reason === "string" && current.reason
          ? current.reason
          : typeof current?.data?.message === "string" && current.data.message
            ? current.data.message
            : typeof current?.error?.message === "string" && current.error.message
              ? current.error.message
              : typeof current?.info?.error?.message === "string" && current.info.error.message
                ? current.info.error.message
                : typeof current?.message === "string" && current.message
                  ? current.message
                  : "";
    if (message && !/could not coalesce error/i.test(message)) {
      return message;
    }
    if (current && typeof current === "object") {
      queue.push(current.error, current.data, current.info, current.info?.error, current.cause);
    }
  }
  return "Failed";
}

async function readNamedLaunchpadCall(label, reader) {
  try {
    return await reader();
  } catch (error) {
    const reason = extractLaunchpadErrorText(error);
    throw new Error(`${label} failed: ${reason}`);
  }
}

function getLaunchpadSortLabel(mode) {
  if (mode === "raisedAmount") return "Raised";
  if (mode === "newestCreated") return "Newest";
  if (mode === "oldestCreated") return "Oldest";
  return "Activity";
}

function normalizeLaunchpadChainId(value) {
  if (value == null) return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const raw = String(value).trim();
  if (!raw) return 0;
  if (/^0x/i.test(raw)) {
    const parsed = Number.parseInt(raw, 16);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

async function readLaunchpadProviderChainId(provider) {
  if (!provider?.request) return 0;
  try {
    return normalizeLaunchpadChainId(await provider.request({ method: "eth_chainId" }));
  } catch {}
  try {
    return normalizeLaunchpadChainId(await provider.request({ method: "net_version" }));
  } catch {}
  return 0;
}

async function readLaunchpadProviderAccounts(provider, options = {}) {
  if (!provider?.request) return [];
  const requestAccess = !!options?.requestAccess;
  const normalizeAccounts = (accounts) => {
    if (!Array.isArray(accounts)) return [];
    return accounts
      .map((account) => String(account || "").trim().toLowerCase())
      .filter(Boolean);
  };
  try {
    const accounts = normalizeAccounts(await provider.request({ method: "eth_accounts" }));
    if (accounts.length || !requestAccess) return accounts;
  } catch {}
  if (requestAccess) {
    try {
      return normalizeAccounts(await provider.request({ method: "eth_requestAccounts" }));
    } catch {}
  }
  return [];
}

async function readLaunchpadProviderBalance(provider, address) {
  if (!provider || !address) return 0n;
  try {
    const browserProvider = new ethers.BrowserProvider(provider);
    return await browserProvider.getBalance(address);
  } catch {}
  return 0n;
}

function getLaunchpadInjectedProviderCandidates() {
  if (typeof window === "undefined") return [];
  const candidates = [];
  const pushProvider = (provider, label = "") => {
    if (!provider) return;
    if (candidates.some((entry) => entry.provider === provider)) return;
    candidates.push({ provider, label });
  };

  const injectedProvider = window.ethereum || null;
  pushProvider(injectedProvider, "window.ethereum");

  const injectedProviders = Array.isArray(injectedProvider?.providers)
    ? injectedProvider.providers
    : [];
  for (const provider of injectedProviders) {
    let providerLabel = "injected";
    if (provider?.isPhantom) providerLabel = "injected.phantom";
    else if (provider?.isMetaMask) providerLabel = "injected.metamask";
    else if (provider?.isCoinbaseWallet) providerLabel = "injected.coinbase";
    pushProvider(provider, providerLabel);
  }

  pushProvider(window.phantom?.ethereum || null, "window.phantom.ethereum");

  return candidates;
}

function getPoolListSnapshotSignature(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map(({ id, pool }) => [
      String(id ?? ""),
      String(pool?.displayState || ""),
      String(pool?.state ?? ""),
      String(pool?.totalRaisedWei ?? ""),
      String(pool?.participantCount ?? ""),
      String(pool?.createdAt ?? ""),
      String(pool?.lastActivityAt ?? ""),
      String(pool?.launchGracePeriod ?? "")
    ].join("::"))
    .join("|");
}

function mergeActivityRailFeed(previousItems, nextItems, maxItems = 20) {
  const map = new Map();
  for (const item of Array.isArray(previousItems) ? previousItems : []) {
    if (!item?.id) continue;
    map.set(String(item.id), item);
  }
  for (const item of Array.isArray(nextItems) ? nextItems : []) {
    if (!item?.id) continue;
    map.set(String(item.id), { ...(map.get(String(item.id)) || {}), ...item });
  }
  return Array.from(map.values())
    .sort((a, b) => Number(a?.ts || 0) - Number(b?.ts || 0))
    .slice(-Math.max(1, Number(maxItems) || 20));
}

function getLaunchpadFilterLabel(mode) {
  if (mode === "upcoming") return "Upcoming";
  if (mode === "live") return "Live";
  if (mode === "completed") return "Completed";
  if (mode === "launched") return "Launched";
  if (mode === "failed") return "Failed";
  return "All";
}

function fmtMon(v, max = 4) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString(undefined, { maximumFractionDigits: max });
}

function fmtCreateMonInputSummary(v, max = 4) {
  if (v == null) return "-";
  const raw = String(v).trim();
  if (!raw) return "-";
  return fmtMon(raw, max);
}

function parseCreateMonInputNumber(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const num = Number(raw);
  if (!Number.isFinite(num) || num < 0) return NaN;
  return num;
}

function createDividendRewardRow(seed = {}) {
  dividendRewardRowId += 1;
  return {
    id: seed.id || `reward_${dividendRewardRowId}`,
    tokenType: seed.tokenType || "quoteToken",
    tokenAddress: String(seed.tokenAddress || "").trim(),
    ratioPct: String(seed.ratioPct ?? "100")
  };
}

function buildInitialCreateForm() {
  return {
    name: "",
    symbol: "",
    description: "",
    website: "",
    twitter: "",
    telegram: "",
    tokenURI: "",
    salt: "",
    predictedAddress: "",
    softCap: "",
    hardCap: "",
    devBuy: "",
    minContribution: "",
    maxContribution: "",
    deployFee: "10",
    startsInSec: "0",
    durationSec: "900",
    burnPct: "0",
    lpPct: "0",
    creatorPct: "0",
    giftPct: "0",
    dividendPct: "0",
    burnEnabled: false,
    lpEnabled: false,
    creatorEnabled: false,
    giftEnabled: false,
    dividendEnabled: false,
    giftXHandle: "",
    dividendRewards: [createDividendRewardRow()],
    whitelistEnabled: false,
    whitelistAddresses: ""
  };
}

function normalizeDividendRewardRows(rows) {
  if (!Array.isArray(rows) || !rows.length) return [createDividendRewardRow()];
  return rows.map((row) => createDividendRewardRow(row));
}

function resolveDividendRewardsForCreate(rows, quoteTokenAddress, predictedTokenAddress) {
  const quoteToken = String(quoteTokenAddress || "").trim();
  const predictedToken = String(predictedTokenAddress || "").trim();
  if (!Array.isArray(rows) || !rows.length) {
    throw new Error("Add at least one dividend reward token");
  }
  if (rows.length > MAX_DIVIDEND_REWARD_TOKENS) {
    throw new Error(`Dividend rewards are limited to ${MAX_DIVIDEND_REWARD_TOKENS} tokens`);
  }

  const resolvedTokens = [];
  const resolvedRatios = [];
  const seen = new Set();

  for (const row of rows) {
    const tokenType = String(row?.tokenType || "quoteToken");
    const ratioRaw = parseCreateMonInputNumber(row?.ratioPct);
    if (ratioRaw == null || Number.isNaN(ratioRaw) || ratioRaw <= 0) {
      throw new Error("Dividend reward ratios must be greater than 0");
    }
    const ratioBps = Math.round(ratioRaw * 100);
    if (ratioBps <= 0) {
      throw new Error("Dividend reward ratios must be greater than 0");
    }

    let tokenAddress = "";
    if (tokenType === "quoteToken") {
      tokenAddress = quoteToken;
    } else if (tokenType === "thisToken") {
      tokenAddress = predictedToken;
    } else {
      tokenAddress = String(row?.tokenAddress || "").trim();
    }

    if (!isLaunchpadAddress(tokenAddress)) {
      throw new Error(
        tokenType === "thisToken"
          ? "Dividend 'This Token' address unavailable. Prepare the Nad token first."
          : "Enter a valid dividend reward token address"
      );
    }
    const normalized = tokenAddress.toLowerCase();
    if (seen.has(normalized)) throw new Error("Duplicate dividend reward token");
    seen.add(normalized);
    resolvedTokens.push(tokenAddress);
    resolvedRatios.push(ratioBps);
  }

  const ratioTotal = resolvedRatios.reduce((sum, value) => sum + value, 0);
  if (ratioTotal !== 10_000) {
    throw new Error("Dividend reward ratios must total 100%");
  }

  return {
    tokens: resolvedTokens,
    ratiosBps: resolvedRatios
  };
}

function fmtCompactAmount(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  const trimCompact = (value, fractionDigits) =>
    Number(value.toFixed(fractionDigits)).toString();
  if (abs >= 1_000_000) {
    const value = abs / 1_000_000;
    const digits = value >= 10 ? 0 : 1;
    return `${sign}${trimCompact(value, digits)}mil`;
  }
  if (abs >= 1_000) {
    const value = abs / 1_000;
    const digits = value >= 100 ? 0 : 1;
    return `${sign}${trimCompact(value, digits)}k`;
  }
  return `${sign}${abs.toLocaleString(undefined, { maximumFractionDigits: abs >= 100 ? 0 : 2 })}`;
}

function fmtCompactMonLabel(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  const trimCompact = (value, fractionDigits) => Number(value.toFixed(fractionDigits)).toString();
  if (abs >= 1_000_000) {
    const value = abs / 1_000_000;
    return `${sign}${trimCompact(value, value >= 10 ? 0 : 1)}mil`;
  }
  if (abs >= 1_000) {
    const value = abs / 1_000;
    return `${sign}${trimCompact(value, value >= 10 ? 0 : 1)}k`;
  }
  return `${sign}${Math.round(abs).toString()}`;
}

function fmtTs(ts) {
  const n = Number(ts || 0);
  if (!n) return "-";
  return new Date(n * 1000).toLocaleString();
}

function fmtTsNoSeconds(ts) {
  const n = Number(ts || 0);
  if (!n) return "-";
  return new Date(n * 1000).toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatScheduleDisplay(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  }).format(date);
}

function formatMonthYearLabel(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric"
  }).format(date);
}

function toDateInputValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getHour12Value(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "12";
  const hours = date.getHours();
  const normalized = hours % 12 || 12;
  return String(normalized).padStart(2, "0");
}

function getMinuteValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "00";
  return String(date.getMinutes()).padStart(2, "0");
}

function getMeridiemValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "AM";
  return date.getHours() >= 12 ? "PM" : "AM";
}

function startOfMonth(date) {
  const next = new Date(date);
  next.setDate(1);
  next.setHours(0, 0, 0, 0);
  return next;
}

function shiftMonth(date, delta) {
  const next = new Date(date);
  next.setMonth(next.getMonth() + delta, 1);
  next.setHours(0, 0, 0, 0);
  return next;
}

function buildCalendarDays(viewMonthDate) {
  const monthStart = startOfMonth(viewMonthDate);
  const firstDay = monthStart.getDay();
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - firstDay);
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    day.setHours(0, 0, 0, 0);
    return {
      key: `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`,
      date: day,
      inMonth: day.getMonth() === monthStart.getMonth()
    };
  });
}

function applyScheduleTimeParts(date, hour12Value, minuteValue, meridiemValue) {
  const next = new Date(date);
  const hour12 = Math.max(1, Math.min(12, Number(hour12Value || 12)));
  const minute = Math.max(0, Math.min(59, Number(minuteValue || 0)));
  let hour24 = hour12 % 12;
  if (String(meridiemValue || "AM").toUpperCase() === "PM") hour24 += 12;
  next.setHours(hour24, minute, 0, 0);
  return next;
}

function buildDefaultScheduleStart() {
  const now = new Date();
  now.setSeconds(0, 0);
  return now;
}

function buildDefaultScheduleEnd(startAt) {
  return new Date(startAt.getTime() + DEFAULT_PRESALE_END_OFFSET_MS);
}

function fmtRelativeDurationLong(totalSeconds) {
  const secs = Math.max(0, Math.floor(Number(totalSeconds || 0)));
  if (secs <= 0) return "0 Seconds";
  const units = [
    { size: 7 * 24 * 60 * 60, singular: "Week", plural: "Weeks" },
    { size: 24 * 60 * 60, singular: "Day", plural: "Days" },
    { size: 60 * 60, singular: "Hour", plural: "Hours" },
    { size: 60, singular: "Minute", plural: "Minutes" },
    { size: 1, singular: "Second", plural: "Seconds" }
  ];
  for (const unit of units) {
    if (secs >= unit.size) {
      const count = Math.floor(secs / unit.size);
      return `${count.toLocaleString()} ${count === 1 ? unit.singular : unit.plural}`;
    }
  }
  return `${secs.toLocaleString()} Seconds`;
}

function fmtRelativeAge(totalSeconds) {
  const secs = Math.max(0, Math.floor(Number(totalSeconds || 0)));
  if (secs <= 0) return "0 seconds ago";
  const units = [
    { size: 365 * 24 * 60 * 60, singular: "year", plural: "years" },
    { size: 30 * 24 * 60 * 60, singular: "month", plural: "months" },
    { size: 7 * 24 * 60 * 60, singular: "week", plural: "weeks" },
    { size: 24 * 60 * 60, singular: "day", plural: "days" },
    { size: 60 * 60, singular: "hour", plural: "hours" },
    { size: 60, singular: "minute", plural: "minutes" },
    { size: 1, singular: "second", plural: "seconds" }
  ];
  for (const unit of units) {
    if (secs >= unit.size) {
      const count = Math.floor(secs / unit.size);
      return `${count.toLocaleString()} ${count === 1 ? unit.singular : unit.plural} ago`;
    }
  }
  return `${secs.toLocaleString()} seconds ago`;
}

function fmtAmountInput(v) {
  const raw = String(v || "").trim();
  if (!raw) return "";
  if (!raw.includes(".")) return raw;
  return raw.replace(/\.?0+$/, "");
}

function stateLabel(s) {
  return ["Active", "Finalized", "Cancelled", "Failed"][Number(s)] || "-";
}

function presentPresaleDisplayState(state) {
  const value = String(state || "").trim();
  if (value === "Finalized") return "Launched";
  return value || "-";
}

function resolveLaunchpadGracePeriod(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 86400;
}

function resolvePresaleLaunchReadyAt(pool) {
  const launchReadyAt = Number(pool?.launchReadyAt || 0);
  if (launchReadyAt > 0) return launchReadyAt;
  return Number(pool?.endTime || 0);
}

function resolvePresaleLaunchDeadlineTs(pool, launchGracePeriod) {
  const launchRefTs = resolvePresaleLaunchReadyAt(pool);
  if (!launchRefTs) return 0;
  return launchRefTs + resolveLaunchpadGracePeriod(pool?.launchGracePeriod ?? launchGracePeriod);
}

function resolveParticipationFeeBps(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return DEFAULT_PRESALE_PARTICIPATION_FEE_BPS;
  return Math.min(9999, Math.floor(parsed));
}

function getPresaleRaisedWei(pool) {
  try {
    return BigInt(pool?.totalRaisedWei?.toString?.() ?? pool?.totalRaisedWei ?? 0n);
  } catch {
    return 0n;
  }
}

function quoteParticipationCost(allocationWei, feeBps) {
  try {
    const allocation = BigInt(allocationWei?.toString?.() ?? allocationWei ?? 0n);
    const bps = BigInt(resolveParticipationFeeBps(feeBps));
    if (allocation <= 0n) return { feeWei: 0n, totalCostWei: 0n };
    const feeWei = (allocation * bps) / BPS;
    return { feeWei, totalCostWei: allocation + feeWei };
  } catch {
    return { feeWei: 0n, totalCostWei: 0n };
  }
}

function isPresaleLaunchExpired(pool, nowTs, launchGracePeriod) {
  const stateNum = Number(pool?.state ?? -1);
  const effectiveStateNum = Number(pool?.effectiveState ?? stateNum);
  if (effectiveStateNum === 3) return true;
  if (stateNum !== 0) return false;
  const launchDeadlineTs = resolvePresaleLaunchDeadlineTs(pool, launchGracePeriod);
  if (!launchDeadlineTs || nowTs <= launchDeadlineTs) return false;
  const softCapWei = BigInt(pool?.softCapWei?.toString?.() ?? pool?.softCapWei ?? 0n);
  const raisedWei = getPresaleRaisedWei(pool);
  if (softCapWei > 0n && raisedWei < softCapWei) return false;
  const launchedToken = String(pool?.launchedToken || "").trim();
  return !launchedToken || launchedToken === ethers.ZeroAddress;
}

function isPresaleRefundable(pool, nowTs, launchGracePeriod) {
  const stateNum = Number(pool?.state ?? -1);
  const effectiveStateNum = Number(pool?.effectiveState ?? stateNum);
  if (stateNum === 2) return true;
  if (effectiveStateNum === 3) return true;
  if (stateNum !== 0) return false;
  if (isPresaleLaunchExpired(pool, nowTs, launchGracePeriod)) return true;
  const endTs = Number(pool?.endTime || 0);
  const softCapWei = BigInt(pool?.softCapWei?.toString?.() ?? pool?.softCapWei ?? 0n);
  const raisedWei = getPresaleRaisedWei(pool);
  return nowTs > endTs && softCapWei > 0n && raisedWei < softCapWei;
}

function computePresaleDisplayState(pool, nowTs, launchGracePeriod) {
  const stateNum = Number(pool?.state ?? -1);
  const effectiveStateNum = Number(pool?.effectiveState ?? stateNum);
  const startTs = Number(pool?.startTime || 0);
  const endTs = Number(pool?.endTime || 0);
  const softCapWei = BigInt(pool?.softCapWei?.toString?.() ?? pool?.softCapWei ?? 0n);
  const hardCapWei = BigInt(pool?.hardCapWei?.toString?.() ?? pool?.hardCapWei ?? 0n);
  const raisedWei = getPresaleRaisedWei(pool);
  const softCapEnabled = softCapWei > 0n;

  if (effectiveStateNum === 2) return "Cancelled";
  if (effectiveStateNum === 1) return "Finalized";
  if (effectiveStateNum === 3) return "Failed";
  if (isPresaleLaunchExpired(pool, nowTs, launchGracePeriod)) return "Failed";
  if (nowTs < startTs) return "Upcoming";
  if (nowTs <= endTs && (!hardCapWei || raisedWei < hardCapWei)) return "Live";
  if (!softCapEnabled || raisedWei >= softCapWei) return "Completed";
  return "Failed";
}

function shortAddr(v, lead = 2, tail = 4) {
  const value = String(v || "").trim();
  if (!value) return "-";
  if (value.length <= lead + tail + 2) return value;
  return `${value.slice(0, lead + 2)}...${value.slice(-tail)}`;
}

function safeResultField(result, key, fallback) {
  try {
    const value = result?.[key];
    return value === undefined || value === null ? fallback : value;
  } catch {
    return fallback;
  }
}

function normalizeAddressList(text) {
  return String(text || "")
    .split(/[\s,;\n\r\t]+/)
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => x.toLowerCase());
}

function hashAddressLeaf(address) {
  return ethers.keccak256(ethers.solidityPacked(["address"], [address]));
}

function hashPairSorted(a, b) {
  const [x, y] = a.toLowerCase() <= b.toLowerCase() ? [a, b] : [b, a];
  return ethers.keccak256(ethers.concat([x, y]));
}

function buildMerkleRootFromAddresses(addresses) {
  const leaves = [...new Set(addresses.map(hashAddressLeaf))];
  if (!leaves.length) return ethers.ZeroHash;
  let level = leaves;
  while (level.length > 1) {
    const next = [];
    for (let i = 0; i < level.length; i += 2) {
      const left = level[i];
      const right = i + 1 < level.length ? level[i + 1] : level[i];
      next.push(hashPairSorted(left, right));
    }
    level = next;
  }
  return level[0];
}

function isIpfsUri(v) {
  return /^ipfs:\/\//i.test(String(v || "").trim());
}

function ipfsToHttp(uri) {
  if (!isIpfsUri(uri)) return uri;
  return `${PINATA_GATEWAY}/${String(uri).replace(/^ipfs:\/\//i, "")}`;
}

function normalizeExternalUrl(raw, kind = "generic") {
  const v = String(raw || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  if (kind === "twitter") {
    const normalized = v
      .replace(/^www\./i, "")
      .replace(/^x\.com\//i, "")
      .replace(/^twitter\.com\//i, "")
      .replace(/^@+/, "");
    return `https://x.com/${normalized}`;
  }
  if (kind === "telegram") {
    const normalized = v
      .replace(/^www\./i, "")
      .replace(/^t\.me\//i, "")
      .replace(/^telegram\.me\//i, "")
      .replace(/^@+/, "");
    return `https://t.me/${normalized}`;
  }
  return `https://${v}`;
}

function ceilDiv(a, b) {
  if (b === 0n) throw new Error("division by zero");
  if (a <= 0n) return 0n;
  return (a + b - 1n) / b;
}

function applyCurveBuyFees(amountInWei, creatorFeeRateBps, curveProtocolFeeRateBps = DEFAULT_CURVE_PROTOCOL_FEE_BPS) {
  const amountIn = BigInt(amountInWei || 0n);
  if (amountIn <= 0n) return 0n;
  const creatorFee = BigInt(creatorFeeRateBps || 0n);
  const curveFee = BigInt(curveProtocolFeeRateBps || 0n);
  const totalFeeBps = creatorFee + curveFee;
  const feeWei = ceilDiv(amountIn * totalFeeBps, BPS);
  return amountIn > feeWei ? amountIn - feeWei : 0n;
}

function deriveNadMaxInitialBuyWei(creatorFeeRateBps, curveProtocolFeeRateBps = DEFAULT_CURVE_PROTOCOL_FEE_BPS) {
  try {
    const creatorFee = BigInt(creatorFeeRateBps || 0n);
    const curveFee = BigInt(curveProtocolFeeRateBps || 0n);
    const totalFeeBps = creatorFee + curveFee;
    if (totalFeeBps >= BPS) return 0n;

    const { virtualQuoteReserveWei, virtualTokenReserveWei, minTokenReserveWei } = NAD_CURVE_CONFIG;
    if (virtualTokenReserveWei <= minTokenReserveWei) return 0n;

    const k = virtualQuoteReserveWei * virtualTokenReserveWei;
    const reserveInAfter = ceilDiv(k, minTokenReserveWei);
    if (reserveInAfter <= virtualQuoteReserveWei) return 0n;

    const quoteInAfterFeesNeeded = reserveInAfter - virtualQuoteReserveWei;
    let grossQuoteInWei = ceilDiv(quoteInAfterFeesNeeded * BPS, BPS - totalFeeBps);

    while (grossQuoteInWei > 0n && applyCurveBuyFees(grossQuoteInWei, creatorFee, curveFee) < quoteInAfterFeesNeeded) {
      grossQuoteInWei += 1n;
    }
    while (grossQuoteInWei > 0n && applyCurveBuyFees(grossQuoteInWei - 1n, creatorFee, curveFee) >= quoteInAfterFeesNeeded) {
      grossQuoteInWei -= 1n;
    }

    return grossQuoteInWei;
  } catch {
    return 0n;
  }
}

function estimateNadInitialBuyTokensOut(quoteInWei, creatorFeeRateBps, curveProtocolFeeRateBps = DEFAULT_CURVE_PROTOCOL_FEE_BPS) {
  const amountIn = BigInt(quoteInWei || 0n);
  if (amountIn <= 0n) return 0n;

  const quoteInAfterFees = applyCurveBuyFees(amountIn, creatorFeeRateBps, curveProtocolFeeRateBps);
  if (quoteInAfterFees <= 0n) return 0n;

  const { virtualQuoteReserveWei, virtualTokenReserveWei, minTokenReserveWei } = NAD_CURVE_CONFIG;
  const k = virtualQuoteReserveWei * virtualTokenReserveWei;
  const reserveInAfter = virtualQuoteReserveWei + quoteInAfterFees;
  const tokenReserveAfter = ceilDiv(k, reserveInAfter);
  let tokenOut = virtualTokenReserveWei > tokenReserveAfter ? virtualTokenReserveWei - tokenReserveAfter : 0n;

  const maxOutBeforeGraduate = virtualTokenReserveWei - minTokenReserveWei;
  if (tokenOut > maxOutBeforeGraduate) tokenOut = maxOutBeforeGraduate;
  return tokenOut;
}

function getLaunchpadDevPreviewTemplates(preset = "mixed") {
  if (preset === "overflow") {
    return [
      {
        name: "The Absurdly Long Bob Launch Experiment With Extra Words For Overflow Testing",
        symbol: "BOBOVERFLOW",
        softCap: "125000",
        hardCap: "229591",
        raised: "228888",
        state: 0,
        startDelta: -6 * 24 * 60 * 60,
        endDelta: 2 * 24 * 60 * 60,
        website: "www.super-long-domain-for-bob-launchpad-preview-example.com",
        twitter: "www.x.com/BobLaunchOverflowCase",
        telegram: "www.t.me/BobLaunchOverflowArmy"
      },
      {
        name: "Micro Cap No Socials",
        symbol: "MICRO",
        softCap: "0",
        hardCap: "7",
        raised: "0.3",
        state: 0,
        startDelta: 5 * 60 * 60,
        endDelta: 9 * 24 * 60 * 60,
        website: "",
        twitter: "",
        telegram: ""
      },
      {
        name: "Launched Giant Numbers",
        symbol: "GIGA",
        softCap: "100000",
        hardCap: "200000",
        raised: "200000",
        state: 1,
        startDelta: -12 * 24 * 60 * 60,
        endDelta: -10 * 24 * 60 * 60,
        website: "test.com",
        twitter: "@gigabobtoken",
        telegram: "gigabobholders"
      },
      {
        name: "Failed Soft Cap Miss",
        symbol: "MISSED",
        softCap: "90000",
        hardCap: "120000",
        raised: "1222",
        state: 2,
        startDelta: -9 * 24 * 60 * 60,
        endDelta: -7 * 24 * 60 * 60,
        website: "https://failed.example",
        twitter: "x.com/failedcase",
        telegram: ""
      },
      {
        name: "Whitelist Only Community Round",
        symbol: "WLROUND",
        softCap: "2500",
        hardCap: "7500",
        raised: "7499",
        state: 0,
        startDelta: -12 * 60 * 60,
        endDelta: 12 * 60 * 60,
        website: "alpha.example",
        twitter: "",
        telegram: "@alpha_wl_room"
      },
      {
        name: "Completed Awaiting Launch With Long Description Support",
        symbol: "READY",
        softCap: "50000",
        hardCap: "100000",
        raised: "100000",
        state: 0,
        startDelta: -5 * 24 * 60 * 60,
        endDelta: -30 * 60,
        website: "https://ready.example",
        twitter: "readyfornad",
        telegram: "readyfornad"
      }
    ];
  }

  if (preset === "states") {
    return [
      { name: "Upcoming Sample", symbol: "UPNEXT", softCap: "75000", hardCap: "180000", raised: "0", state: 0, startDelta: 3 * 24 * 60 * 60, endDelta: 6 * 24 * 60 * 60, website: "", twitter: "", telegram: "" },
      { name: "Live Sample", symbol: "LIVE", softCap: "90000", hardCap: "210000", raised: "142000", state: 0, startDelta: -6 * 60 * 60, endDelta: 2 * 24 * 60 * 60, website: "test.com", twitter: "@livecase", telegram: "livechat" },
      { name: "Completed Sample", symbol: "DONE", softCap: "100000", hardCap: "200000", raised: "168500", state: 0, startDelta: -4 * 24 * 60 * 60, endDelta: -60 * 60, website: "", twitter: "", telegram: "" },
      { name: "Launched Sample", symbol: "MOON", softCap: "110000", hardCap: "229591", raised: "229591", state: 1, startDelta: -7 * 24 * 60 * 60, endDelta: -6 * 24 * 60 * 60, website: "www.moon.example", twitter: "x.com/mooncase", telegram: "t.me/mooncase" },
      { name: "Failed Sample", symbol: "FAIL", softCap: "85000", hardCap: "150000", raised: "12222", state: 2, startDelta: -5 * 24 * 60 * 60, endDelta: -4 * 24 * 60 * 60, website: "", twitter: "", telegram: "" },
      { name: "Hard Cap Hit", symbol: "FULL", softCap: "125000", hardCap: "229591", raised: "229591", state: 0, startDelta: -2 * 60 * 60, endDelta: 5 * 24 * 60 * 60, website: "https://full.example", twitter: "fullcase", telegram: "" }
    ];
  }

  return [
    {
      name: "Bob Inu",
      symbol: "BOBINU",
      softCap: "40000",
      hardCap: "120000",
      raised: "82000",
      state: 0,
      startDelta: -60 * 60,
      endDelta: 3 * 24 * 60 * 60,
      website: "https://bobverse.xyz",
      twitter: "bobverse",
      telegram: "bobverseportal"
    },
    {
      name: "Monad Utility Token",
      symbol: "MUT",
      softCap: "85000",
      hardCap: "180000",
      raised: "26000",
      state: 0,
      startDelta: 2 * 60 * 60,
      endDelta: 5 * 24 * 60 * 60,
      website: "https://monad.example",
      twitter: "monadutility",
      telegram: ""
    },
    {
      name: "Super Long Placeholder Meme Coin Name",
      symbol: "LONGBOB",
      softCap: "60000",
      hardCap: "200000",
      raised: "17777",
      state: 0,
      startDelta: -2 * 24 * 60 * 60,
      endDelta: -3 * 60 * 60,
      website: "",
      twitter: "longbobtoken",
      telegram: "longbobarmy"
    },
    {
      name: "Whitelist Alpha",
      symbol: "WLALPHA",
      softCap: "75000",
      hardCap: "175000",
      raised: "175000",
      state: 1,
      startDelta: -8 * 24 * 60 * 60,
      endDelta: -7 * 24 * 60 * 60,
      website: "https://alpha.example",
      twitter: "",
      telegram: "alphahub"
    },
    {
      name: "Cancelled Presale",
      symbol: "NOPE",
      softCap: "50000",
      hardCap: "140000",
      raised: "3333",
      state: 2,
      startDelta: -4 * 24 * 60 * 60,
      endDelta: -3 * 24 * 60 * 60,
      website: "",
      twitter: "",
      telegram: ""
    },
    {
      name: "Completed Test",
      symbol: "ALT",
      softCap: "100000",
      hardCap: "210000",
      raised: "154000",
      state: 0,
      startDelta: -3 * 24 * 60 * 60,
      endDelta: -60 * 60,
      website: "https://awaiting.example",
      twitter: "awaitinglaunch",
      telegram: "awaitinglaunch"
    }
  ];
}

function buildLaunchpadDevPreviewPools(preset = "mixed", count = 6) {
  const now = Math.floor(Date.now() / 1000);
  const templates = getLaunchpadDevPreviewTemplates(preset);

  const pools = Array.from({ length: count }, (_, index) => {
    const template = templates[index % templates.length];
    const id = `dev-${index + 1}`;
    return {
      id,
      pool: {
        creator: LAUNCHPAD_DEV_WALLET,
        name: template.name,
        symbol: template.symbol,
        website: template.website,
        twitter: template.twitter,
        telegram: template.telegram,
        softCapWei: parseEther(template.softCap),
        hardCapWei: parseEther(template.hardCap),
        totalRaisedWei: parseEther(template.raised),
        state: BigInt(template.state),
        startTime: BigInt(now + template.startDelta),
        endTime: BigInt(now + template.endDelta)
      }
    };
  });

  const tokenMeta = Object.fromEntries(
    pools.map(({ id, pool }, index) => [
      String(id),
      {
        image: "",
        image_uri: "",
        description:
          index % 2 === 0
            ? "Placeholder preview card for responsive launchpad testing."
            : "Longer placeholder copy to help test overflow, truncation, card spacing, and how the list behaves when descriptions, links, and numbers get ugly at smaller breakpoints."
      }
    ])
  );

  return { pools, tokenMeta };
}

function buildLaunchpadDetailPreviewConfig(mode = "default") {
  const now = Math.floor(Date.now() / 1000);
  if (mode === "overflow") {
    return {
      pool: {
        name: "The Very Long Detail Preview Token Name Used To Stress Every Horizontal And Vertical Layout Constraint",
        symbol: "DETAILMAX",
        description: "Extremely long placeholder content for the detail page to stress description scrolling, social alignment, hub boxes, progress stats, summary rows, and responsiveness at awkward breakpoints. This intentionally uses a lot of words so you can quickly catch spacing and overflow issues without needing live data.",
        website: "www.detail-preview-example-domain.com",
        twitter: "www.x.com/DetailPreviewOverflowCase",
        telegram: "www.t.me/DetailPreviewOverflowCase",
        creator: LAUNCHPAD_DEV_WALLET,
        softCapWei: parseEther("100000"),
        hardCapWei: parseEther("229591"),
        minContributionWei: parseEther("250"),
        maxContributionWei: parseEther("25000"),
        devBuyWei: parseEther("12500"),
        totalRaisedWei: parseEther("188888"),
        startTime: BigInt(now - 5 * 24 * 60 * 60),
        endTime: BigInt(now + 2 * 24 * 60 * 60),
        state: 0n
      },
      meta: {
        image: "",
        image_uri: "",
        description: "Overflow-mode metadata for detail-page QA."
      },
      participantCount: 4821
    };
  }
  if (mode === "minimal") {
    return {
      pool: {
        name: "Minimal Sample",
        symbol: "MINI",
        description: "",
        website: "",
        twitter: "",
        telegram: "",
        creator: LAUNCHPAD_DEV_WALLET,
        softCapWei: 0n,
        hardCapWei: parseEther("35000"),
        minContributionWei: parseEther("1"),
        maxContributionWei: parseEther("2"),
        devBuyWei: 0n,
        totalRaisedWei: parseEther("4200"),
        startTime: BigInt(now + 3 * 24 * 60 * 60),
        endTime: BigInt(now + 10 * 24 * 60 * 60),
        state: 0n
      },
      meta: {
        image: "",
        image_uri: "",
        description: ""
      },
      participantCount: 1
    };
  }
  return {
    pool: {
      name: "Placeholder Launch Token",
      symbol: "PLHOLD",
      description: "Placeholder detail-page content for testing the token summary, market data, and grouped presale details responsiveness without depending on live metadata shape.",
      website: "https://placeholder.example",
      twitter: "placeholdertoken",
      telegram: "placeholderportal",
      creator: LAUNCHPAD_DEV_WALLET,
      softCapWei: parseEther("95000"),
      hardCapWei: parseEther("200000"),
      minContributionWei: parseEther("250"),
      maxContributionWei: parseEther("5000"),
      devBuyWei: parseEther("2500"),
      totalRaisedWei: parseEther("128000"),
      startTime: BigInt(now - 2 * 24 * 60 * 60),
      endTime: BigInt(now + 5 * 24 * 60 * 60),
      state: 0n
    },
    meta: {
      image: "",
      image_uri: "",
      description: "Preview metadata used to sanity-check the detail page layout with predictable values."
    },
    participantCount: 24
  };
}

async function validateSquareImage(file) {
  if (!file) throw new Error("Image file required");
  if (file.size > 5 * 1024 * 1024) throw new Error("Image must be <= 5MB");
  const img = document.createElement("img");
  const url = URL.createObjectURL(file);
  try {
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
    // Non-square is allowed; backend will center-crop to 1:1 before Pinata upload.
    return { width: img.width, height: img.height };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function prepareNadTokenViaBackend({
  file,
  creator,
  name,
  symbol,
  description,
  website,
  twitter,
  telegram
}) {
  if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
  const fd = new FormData();
  fd.append("file", file);
  fd.append("creator", creator);
  fd.append("name", name);
  fd.append("symbol", symbol);
  fd.append("description", String(description || ""));
  fd.append("website", String(website || ""));
  fd.append("twitter", String(twitter || ""));
  fd.append("telegram", String(telegram || ""));
  const base = LAUNCHPAD_BACKEND_URL.replace(/\/$/, "");
  const res = await fetch(`${base}/api/launchpad/nad/prepare-token`, {
    method: "POST",
    body: fd
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok || !json?.metadata_uri || !json?.salt) {
    throw new Error(json?.error || "Nad prepare token failed");
  }
  return json;
}

function normalizeScopedContractAddress(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    return ethers.getAddress(raw);
  } catch {
    return "";
  }
}

function buildLaunchpadDetailPath(poolId, contractAddress = "") {
  const id = String(poolId || "").trim();
  const launchpadBase = isLaunchpadRootHost() ? "" : "/launchpad";
  if (!id) return launchpadBase || "/";
  return `${launchpadBase}/presales/${id}`;
}

function buildLaunchpadBasePath(segment = "") {
  const launchpadBase = isLaunchpadRootHost() ? "" : "/launchpad";
  const cleanSegment = String(segment || "").trim().replace(/^\/+/, "");
  if (!cleanSegment) return launchpadBase || "/";
  return `${launchpadBase}/${cleanSegment}`;
}

function buildMonadVisionAddressUrl(address) {
  const normalized = normalizeScopedContractAddress(address);
  return normalized ? `https://monadvision.com/address/${normalized}` : "";
}

async function registerWhitelistViaBackend({ poolId, contractAddress = "", whitelistRoot, addresses }) {
  if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
  const base = LAUNCHPAD_BACKEND_URL.replace(/\/$/, "");
  const res = await fetch(`${base}/api/launchpad/whitelist/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      poolId: String(poolId),
      contractAddress: normalizeScopedContractAddress(contractAddress),
      whitelistRoot,
      addresses
    })
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok) throw new Error(json?.error || "Whitelist register failed");
  return json;
}

async function fetchWhitelistProofViaBackend({ poolId, contractAddress = "", wallet }) {
  if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
  const base = LAUNCHPAD_BACKEND_URL.replace(/\/$/, "");
  const qs = new URLSearchParams({ poolId: String(poolId), wallet: String(wallet) });
  const scoped = normalizeScopedContractAddress(contractAddress);
  if (scoped) qs.set("contractAddress", scoped);
  const res = await fetch(`${base}/api/launchpad/whitelist/proof?${qs.toString()}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok) throw new Error(json?.error || "Whitelist proof fetch failed");
  return json;
}

async function fetchPublicPresalesViaBackend({ force = false } = {}) {
  if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
  const base = LAUNCHPAD_BACKEND_URL.replace(/\/$/, "");
  const qs = force ? "?force=1" : "";
  const res = await fetch(`${base}/api/launchpad/public/presales${qs}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok) throw new Error(json?.error || "Public presales fetch failed");
  return (Array.isArray(json?.presales) ? json.presales : []).filter(
    (row) => !isHiddenLaunchpadPoolId(row?.id || row?.poolId)
  );
}

async function fetchPublicPresaleViaBackend(poolId, contractAddress = "") {
  if (isHiddenLaunchpadPoolId(poolId)) throw new Error("Presale not found");
  if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
  const base = LAUNCHPAD_BACKEND_URL.replace(/\/$/, "");
  const scoped = normalizeScopedContractAddress(contractAddress);
  const path = scoped
    ? `/api/launchpad/public/presales/${encodeURIComponent(scoped)}/${encodeURIComponent(String(poolId))}`
    : `/api/launchpad/public/presales/${encodeURIComponent(String(poolId))}`;
  const res = await fetch(`${base}${path}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok || !json?.presale) throw new Error(json?.error || "Public presale fetch failed");
  if (isHiddenLaunchpadPoolId(json.presale?.id || json.presale?.poolId || poolId)) {
    throw new Error("Presale not found");
  }
  return json.presale;
}

async function fetchPublicLaunchpadProfileViaBackend(wallet) {
  if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
  const base = LAUNCHPAD_BACKEND_URL.replace(/\/$/, "");
  const res = await fetch(`${base}/api/launchpad/public/profile/${encodeURIComponent(String(wallet))}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok) throw new Error(json?.error || "Public launchpad profile fetch failed");
  return (Array.isArray(json?.presales) ? json.presales : []).filter(
    (row) => !isHiddenLaunchpadPoolId(row?.id || row?.poolId)
  );
}

async function fetchLaunchpadActivityViaBackend({ force = false } = {}) {
  if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
  const base = LAUNCHPAD_BACKEND_URL.replace(/\/$/, "");
  const qs = force ? "?force=1" : "";
  const res = await fetch(`${base}/api/launchpad/public/activity${qs}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok) throw new Error(json?.error || "Launchpad activity fetch failed");
  return (Array.isArray(json?.activity) ? json.activity : []).filter(
    (item) => !isHiddenLaunchpadPoolId(item?.poolId)
  );
}

async function searchPublicPresalesViaBackend(query, { force = false, limit = LAUNCHPAD_SEARCH_RESULT_LIMIT } = {}) {
  if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
  const base = LAUNCHPAD_BACKEND_URL.replace(/\/$/, "");
  const url = new URL(`${base}/api/launchpad/public/search`);
  url.searchParams.set("q", String(query || "").trim());
  url.searchParams.set("limit", String(limit));
  if (force) url.searchParams.set("force", "1");
  const res = await fetch(url.toString());
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok) throw new Error(json?.error || "Launchpad search failed");
  const presales = (Array.isArray(json?.presales) ? json.presales : []).filter(
    (row) => !isHiddenLaunchpadPoolId(row?.id || row?.poolId)
  );
  return {
    presales,
    count: presales.length
  };
}

async function fetchLaunchpadPlatformFeeQuote({ user = "", nonce = "" } = {}) {
  if (!RESOLVED_TOOLS_API_BASE) throw new Error("Tools API base not configured");
  const base = RESOLVED_TOOLS_API_BASE.replace(/\/$/, "");
  const url = new URL(`${base}/api/tools/launchpad-fee-quote`);
  if (user) url.searchParams.set("user", String(user));
  if (nonce !== "" && nonce != null) url.searchParams.set("nonce", String(nonce));
  const res = await fetch(url.toString());
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok) throw new Error(json?.error || "Launchpad fee quote failed");
  return json;
}

export default function LaunchpadPage() {
  const wallet = useWallet();
  const { openConnectModal } = useConnectModal();
  const location = useLocation();

  const [contractAddress] = useState(DEFAULT_LAUNCHPAD_ADDRESS);
  const [status, setStatusText] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const [statusLink, setStatusLink] = useState(null);
  const [showBetaBanner, setShowBetaBanner] = useState(true);
  const [loading, setLoading] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createNativeBalanceRaw, setCreateNativeBalanceRaw] = useState(0n);
  const [nextPoolId, setNextPoolId] = useState("0");
  const [maxInitialBuyWei, setMaxInitialBuyWei] = useState("0");
  const [launchGracePeriod, setLaunchGracePeriod] = useState(86400);
  const [participationFeeBps, setParticipationFeeBps] = useState(DEFAULT_PRESALE_PARTICIPATION_FEE_BPS);
  const [poolList, setPoolList] = useState([]);
  const [poolListLoading, setPoolListLoading] = useState(true);
  const [poolListTokenMeta, setPoolListTokenMeta] = useState({});
  const [poolListSortMode, setPoolListSortMode] = useState("latestTrade");
  const [poolListFilterMode, setPoolListFilterMode] = useState("all");
  const [poolActivityMap, setPoolActivityMap] = useState({});
  const [launchpadActivityFeed, setLaunchpadActivityFeed] = useState([]);
  const [launchpadRailFeed, setLaunchpadRailFeed] = useState(() => seedInitialLaunchpadActivityFeed() || []);
  const [poolListPage, setPoolListPage] = useState(1);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [searchPending, setSearchPending] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const [searchShowAllResults, setSearchShowAllResults] = useState(false);
  const [activeListSearchQuery, setActiveListSearchQuery] = useState("");
  const [activeListSearchRows, setActiveListSearchRows] = useState([]);
  const [recentSearches, setRecentSearches] = useState(() => readRecentLaunchpadSearches());
  const [recentViewedPresales, setRecentViewedPresales] = useState(() => readRecentViewedPresales());
  const [profilePage, setProfilePage] = useState(1);
  const [useCompactSortLabels, setUseCompactSortLabels] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < MOBILE_SORT_LABEL_BREAKPOINT;
  });
  const [routerCfg, setRouterCfg] = useState(null);
  const [profileRows, setProfileRows] = useState([]);
  const [profileBoughtMap, setProfileBoughtMap] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileHydrated, setProfileHydrated] = useState(false);
  const profileLoadKeyRef = useRef("");
  const railViewportRef = useRef(null);
  const railTrackRef = useRef(null);
  const railGroupRef = useRef(null);
  const searchWidgetRef = useRef(null);
  const searchInputRef = useRef(null);
  const railOffsetRef = useRef(0);
  const railRafRef = useRef(0);
  const railLastFrameRef = useRef(0);
  const railPausedRef = useRef(false);
  const [railRepeatCount, setRailRepeatCount] = useState(3);
  const [railPaused, setRailPaused] = useState(false);
  const [hoveredRailItemKey, setHoveredRailItemKey] = useState("");

  const [detailPool, setDetailPool] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoaded, setDetailLoaded] = useState(false);
  const [detailClaimPreview, setDetailClaimPreview] = useState(null);
  const [detailContributionWeiRaw, setDetailContributionWeiRaw] = useState(0n);
  const [detailDevClaimedRaw, setDetailDevClaimedRaw] = useState(0n);
  const [detailTokenMeta, setDetailTokenMeta] = useState(null);
  const [detailParticipantCount, setDetailParticipantCount] = useState(0);
  const [depositAmountMon, setDepositAmountMon] = useState("");
  const [liveNowTs, setLiveNowTs] = useState(() => Math.floor(Date.now() / 1000));

  const [createForm, setCreateForm] = useState(() => buildInitialCreateForm());
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageInfo, setImageInfo] = useState("");
  const [openSelectKey, setOpenSelectKey] = useState("");
  const [openDateTimeKey, setOpenDateTimeKey] = useState("");
  const [openDateTimeSelectKey, setOpenDateTimeSelectKey] = useState("");
  const [showDevConsole, setShowDevConsole] = useState(false);
  const [devPreviewCardsEnabled, setDevPreviewCardsEnabled] = useState(false);
  const [devPreviewCardCount, setDevPreviewCardCount] = useState(6);
  const [devPreviewPreset, setDevPreviewPreset] = useState("mixed");
  const [devDetailPreviewEnabled, setDevDetailPreviewEnabled] = useState(false);
  const [devDetailPreviewMode, setDevDetailPreviewMode] = useState("default");
  const [whitelistFileName, setWhitelistFileName] = useState("");
  const [whitelistActiveLine, setWhitelistActiveLine] = useState(1);
  const [whitelistScrollTop, setWhitelistScrollTop] = useState(0);
  const [whitelistLineHeightPx, setWhitelistLineHeightPx] = useState(20);
  const [whitelistTextareaHeight, setWhitelistTextareaHeight] = useState(190);
  const [launchpadPlatformFeeQuote, setLaunchpadPlatformFeeQuote] = useState(null);
  const [scheduleStartAt, setScheduleStartAt] = useState(() => buildDefaultScheduleStart());
  const [scheduleEndAt, setScheduleEndAt] = useState(() => buildDefaultScheduleEnd(buildDefaultScheduleStart()));
  const [scheduleEndManuallyEdited, setScheduleEndManuallyEdited] = useState(false);
  const [scheduleStartViewMonth, setScheduleStartViewMonth] = useState(() => startOfMonth(new Date()));
  const [scheduleEndViewMonth, setScheduleEndViewMonth] = useState(() =>
    startOfMonth(buildDefaultScheduleEnd(buildDefaultScheduleStart()))
  );
  const whitelistTextareaRef = useRef(null);
  const whitelistGutterRef = useRef(null);
  const poolListRef = useRef([]);
  const poolActivityMapRef = useRef({});
  const launchpadActivityFeedRef = useRef([]);
  const lastWriteProviderRef = useRef(null);
  const isLaunchpadDevConnected =
    Boolean(String(wallet.address || "").trim()) &&
    Boolean(String(wallet.address || "").trim()) &&
    LAUNCHPAD_DEV_WALLET_SET.has(String(wallet.address || "").toLowerCase());

  const feeSplit = useMemo(() => {
    const burn = createForm.burnEnabled ? Number(createForm.burnPct || 0) : 0;
    const lp = createForm.lpEnabled ? Number(createForm.lpPct || 0) : 0;
    const creator = createForm.creatorEnabled ? Number(createForm.creatorPct || 0) : 0;
    const gift = createForm.giftEnabled ? Number(createForm.giftPct || 0) : 0;
    const dividend = createForm.dividendEnabled ? Number(createForm.dividendPct || 0) : 0;
    const total = burn + lp + creator + gift + dividend;
    return { burn, lp, creator, gift, dividend, total };
  }, [
    createForm.burnEnabled,
    createForm.lpEnabled,
    createForm.creatorEnabled,
    createForm.giftEnabled,
    createForm.dividendEnabled,
    createForm.burnPct,
    createForm.lpPct,
    createForm.creatorPct,
    createForm.giftPct,
    createForm.dividendPct
  ]);

  const feeLegendItems = useMemo(
    () => [
      { key: "burn", label: "Burn", value: feeSplit.burn, dotClass: "lp-fee-dot-burn" },
      { key: "lp", label: "LP", value: feeSplit.lp, dotClass: "lp-fee-dot-lp" },
      { key: "creator", label: "Creator", value: feeSplit.creator, dotClass: "lp-fee-dot-creator" },
      { key: "gift", label: "Gift", value: feeSplit.gift, dotClass: "lp-fee-dot-gift" },
      { key: "dividend", label: "Dividend", value: feeSplit.dividend, dotClass: "lp-fee-dot-dividend" }
    ].filter((item) => item.value > 0),
    [feeSplit]
  );
  const dividendRewardRatioTotal = useMemo(
    () => (Array.isArray(createForm.dividendRewards)
      ? createForm.dividendRewards.reduce((sum, row) => {
          const ratio = Number(String(row?.ratioPct ?? "").trim());
          return Number.isFinite(ratio) ? sum + ratio : sum;
        }, 0)
      : 0),
    [createForm.dividendRewards]
  );
  const updateDividendRewardRow = useCallback((rowId, patch) => {
    setCreateForm((state) => ({
      ...state,
      dividendRewards: normalizeDividendRewardRows(state.dividendRewards).map((row) =>
        row.id === rowId ? { ...row, ...patch } : row
      )
    }));
  }, []);
  const addDividendRewardRow = useCallback(() => {
    setCreateForm((state) => {
      const rows = normalizeDividendRewardRows(state.dividendRewards);
      if (rows.length >= MAX_DIVIDEND_REWARD_TOKENS) return state;
      return {
        ...state,
        dividendRewards: [
          ...rows,
          createDividendRewardRow({
            tokenType: "custom",
            ratioPct: "0"
          })
        ]
      };
    });
  }, []);
  const removeDividendRewardRow = useCallback((rowId) => {
    setCreateForm((state) => {
      const rows = normalizeDividendRewardRows(state.dividendRewards).filter((row) => row.id !== rowId);
      return {
        ...state,
        dividendRewards: rows.length ? rows : [createDividendRewardRow()]
      };
    });
  }, []);

  const summaryDevBuyMon = useMemo(() => Number(createForm.devBuy || 0) || 0, [createForm.devBuy]);
  const summaryDeployFeeMon = useMemo(() => Number(createForm.deployFee || 0) || 0, [createForm.deployFee]);
  const launchpadPlatformFeeMon = useMemo(
    () => Math.max(0, Number(launchpadPlatformFeeQuote?.serviceFeeMon || 0) || 0),
    [launchpadPlatformFeeQuote]
  );
  const summaryCreatorUpfrontMon = useMemo(
    () => Math.max(0, summaryDeployFeeMon) + Math.max(0, summaryDevBuyMon) + Math.max(0, launchpadPlatformFeeMon),
    [summaryDeployFeeMon, summaryDevBuyMon, launchpadPlatformFeeMon]
  );
  const launchpadDerivedMaxInitialBuyWei = useMemo(() => {
    const creatorFeeRateBps = BigInt(routerCfg?.creatorFeeRate?.toString?.() || "100");
    return deriveNadMaxInitialBuyWei(creatorFeeRateBps);
  }, [routerCfg?.creatorFeeRate]);
  const launchpadMaxInitialBuyWeiResolved = useMemo(() => {
    try {
      const quotedCapWei = BigInt(
        launchpadPlatformFeeQuote?.maxSafeBuyWei?.toString?.() ??
        launchpadPlatformFeeQuote?.maxSafeBuyWei ??
        0n
      );
      const fallbackCapWei = BigInt(maxInitialBuyWei || "0");
      const derivedCapWei = BigInt(launchpadDerivedMaxInitialBuyWei || 0n);
      const capWei = quotedCapWei > 0n ? quotedCapWei : fallbackCapWei > 0n ? fallbackCapWei : derivedCapWei;
      if (capWei <= 0n || capWei === ethers.MaxUint256) return 0n;
      return capWei;
    } catch {
      return 0n;
    }
  }, [launchpadPlatformFeeQuote, maxInitialBuyWei, launchpadDerivedMaxInitialBuyWei]);
  const launchpadMaxInitialBuyMonRaw = useMemo(() => {
    try {
      const value = Number(formatEther(launchpadMaxInitialBuyWeiResolved));
      return Number.isFinite(value) && value > 0 ? value : 0;
    } catch {
      return 0;
    }
  }, [launchpadMaxInitialBuyWeiResolved]);
  const createDevBuyWeiRaw = useMemo(() => {
    try {
      const value = parseCreateMonInputNumber(createForm.devBuy);
      if (value == null || Number.isNaN(value) || value <= 0) return 0n;
      return parseEther(String(value));
    } catch {
      return 0n;
    }
  }, [createForm.devBuy]);
  const launchpadMaxPresaleBuyWeiResolved = useMemo(() => {
    if (launchpadMaxInitialBuyWeiResolved <= 0n) return 0n;
    if (createDevBuyWeiRaw >= launchpadMaxInitialBuyWeiResolved) return 0n;
    return launchpadMaxInitialBuyWeiResolved - createDevBuyWeiRaw;
  }, [launchpadMaxInitialBuyWeiResolved, createDevBuyWeiRaw]);
  const launchpadMaxPresaleBuyMonRaw = useMemo(() => {
    try {
      const value = Number(formatEther(launchpadMaxPresaleBuyWeiResolved));
      return Number.isFinite(value) && value > 0 ? value : 0;
    } catch {
      return 0;
    }
  }, [launchpadMaxPresaleBuyWeiResolved]);
  const launchpadMaxPresaleBuyMonLabel = useMemo(() => {
    if (!launchpadMaxInitialBuyWeiResolved) return "-";
    if (!launchpadMaxPresaleBuyMonRaw) return "0";
    return fmtMon(launchpadMaxPresaleBuyMonRaw, 4);
  }, [launchpadMaxInitialBuyWeiResolved, launchpadMaxPresaleBuyMonRaw]);
  const launchpadMaxInitialBuyPlaceholder = useMemo(() => {
    if (!launchpadMaxPresaleBuyMonRaw) return "Max";
    return `Max: ${fmtMon(launchpadMaxPresaleBuyMonRaw, 0)}`;
  }, [launchpadMaxPresaleBuyMonRaw]);
  const createAmountFieldErrors = useMemo(() => {
    const softCap = parseCreateMonInputNumber(createForm.softCap);
    const hardCap = parseCreateMonInputNumber(createForm.hardCap);
    const minContribution = parseCreateMonInputNumber(createForm.minContribution);
    const maxContribution = parseCreateMonInputNumber(createForm.maxContribution);
    const devBuy = parseCreateMonInputNumber(createForm.devBuy);
    const hardCapLabel = hardCap != null && Number.isFinite(hardCap) ? fmtMon(hardCap, 4) : "0";
    const fieldErrors = {
      softCap: "",
      hardCap: "",
      minContribution: "",
      maxContribution: "",
      devBuy: ""
    };

    if (Number.isNaN(softCap)) fieldErrors.softCap = "Enter a valid amount";
    if (hardCap == null) fieldErrors.hardCap = "Hard cap required";
    else if (Number.isNaN(hardCap)) fieldErrors.hardCap = "Enter a valid amount";
    if (minContribution == null) fieldErrors.minContribution = "Min contribution required";
    else if (Number.isNaN(minContribution)) fieldErrors.minContribution = "Enter a valid amount";
    if (maxContribution == null) fieldErrors.maxContribution = "Max contribution required";
    else if (Number.isNaN(maxContribution)) fieldErrors.maxContribution = "Enter a valid amount";
    if (Number.isNaN(devBuy)) fieldErrors.devBuy = "Enter a valid amount";

    if (!fieldErrors.devBuy && devBuy != null && devBuy > 0 && launchpadMaxInitialBuyWeiResolved > 0n && devBuy > launchpadMaxInitialBuyMonRaw) {
      fieldErrors.devBuy = `Max: ${fmtMon(launchpadMaxInitialBuyMonRaw, 4)} MON`;
    }

    if (!fieldErrors.hardCap && hardCap != null && hardCap > 0 && launchpadMaxInitialBuyWeiResolved > 0n) {
      if (hardCap > launchpadMaxPresaleBuyMonRaw) {
        fieldErrors.hardCap = `Max: ${launchpadMaxPresaleBuyMonLabel} MON`;
      }
    }

    if (!fieldErrors.softCap && softCap != null && !fieldErrors.hardCap && hardCap != null && Number.isFinite(hardCap) && softCap > hardCap) {
      fieldErrors.softCap = `Max: ${hardCapLabel} MON`;
    }

    if (!fieldErrors.minContribution && minContribution != null && !fieldErrors.hardCap && hardCap != null && Number.isFinite(hardCap) && minContribution > hardCap) {
      fieldErrors.minContribution = `Max: ${hardCapLabel} MON`;
    }

    if (
      !fieldErrors.maxContribution &&
      maxContribution != null &&
      minContribution != null &&
      Number.isFinite(minContribution) &&
      maxContribution < minContribution
    ) {
      fieldErrors.maxContribution = `Min: ${fmtMon(minContribution, 4)} MON`;
    }

    if (!fieldErrors.maxContribution && maxContribution != null && !fieldErrors.hardCap && hardCap != null && Number.isFinite(hardCap) && maxContribution > hardCap) {
      fieldErrors.maxContribution = `Max: ${hardCapLabel} MON`;
    }

    if (!fieldErrors.devBuy && !fieldErrors.hardCap && devBuy != null && hardCap != null && Number.isFinite(hardCap) && launchpadMaxInitialBuyMonRaw > 0 && (hardCap + devBuy) > launchpadMaxInitialBuyMonRaw) {
      fieldErrors.devBuy = `Hard cap + dev buy max: ${fmtMon(launchpadMaxInitialBuyMonRaw, 4)} MON`;
    }

    return fieldErrors;
  }, [
    createForm.softCap,
    createForm.hardCap,
    createForm.minContribution,
    createForm.maxContribution,
    createForm.devBuy,
    launchpadMaxInitialBuyWeiResolved,
    launchpadMaxInitialBuyMonRaw,
    launchpadMaxPresaleBuyMonLabel,
    launchpadMaxPresaleBuyMonRaw
  ]);

  const firstCreateAmountFieldError = useMemo(
    () =>
      createAmountFieldErrors.softCap ||
      createAmountFieldErrors.hardCap ||
      createAmountFieldErrors.minContribution ||
      createAmountFieldErrors.maxContribution ||
      createAmountFieldErrors.devBuy ||
      "",
    [createAmountFieldErrors]
  );
  const devPreviewData = useMemo(
    () => buildLaunchpadDevPreviewPools(devPreviewPreset, devPreviewCardCount),
    [devPreviewPreset, devPreviewCardCount]
  );
  const poolListSortLabel = useMemo(
    () => getLaunchpadSortLabel(poolListSortMode, useCompactSortLabels),
    [poolListSortMode, useCompactSortLabels]
  );
  const poolListFilterLabel = useMemo(
    () => getLaunchpadFilterLabel(poolListFilterMode),
    [poolListFilterMode]
  );
  const trimmedSearchQuery = useMemo(() => String(searchQuery || "").trim(), [searchQuery]);
  const searchHasQuery = trimmedSearchQuery.length > 0;
  const showSearchSkeleton = isSearchOpen && searchHasQuery && (searchPending || searchLoading);
  const displayedPoolList = devPreviewCardsEnabled ? devPreviewData.pools : poolList;
  const displayedPoolListTokenMeta = devPreviewCardsEnabled ? devPreviewData.tokenMeta : poolListTokenMeta;
  const listGridRows = activeListSearchQuery ? activeListSearchRows : displayedPoolList;
  const poolListPageSize = 12;
  const profilePageSize = 12;
  const sortedDisplayedPoolList = useMemo(() => {
    const filterMatches = (row) => {
      const pool = row?.pool || {};
      const displayState = String(pool.displayState || "");
      if (poolListFilterMode === "upcoming") return displayState === "Upcoming";
      if (poolListFilterMode === "live") return displayState === "Live";
      if (poolListFilterMode === "completed") return displayState === "Completed";
      if (poolListFilterMode === "launched") return displayState === "Finalized";
      if (poolListFilterMode === "failed") {
        return displayState === "Failed" || displayState === "Cancelled";
      }
      return true;
    };

    const rows = listGridRows.filter(filterMatches);
    const getRaisedWei = (row) => {
      try {
        return BigInt(row?.pool?.totalRaisedWei?.toString?.() ?? row?.pool?.totalRaisedWei ?? 0n);
      } catch {
        return 0n;
      }
    };
    const getIdNum = (row) => Number(row?.id || 0);
    const getActivityTs = (row) => {
      const key = String(row?.id ?? "");
      return Number(poolActivityMap[key] ?? 0);
    };

    rows.sort((a, b) => {
      if (poolListSortMode === "raisedAmount") {
        const raisedDiff = getRaisedWei(b) - getRaisedWei(a);
        if (raisedDiff !== 0n) return raisedDiff > 0n ? 1 : -1;
        return getIdNum(b) - getIdNum(a);
      }
      if (poolListSortMode === "oldestCreated") {
        return getIdNum(a) - getIdNum(b);
      }
      if (poolListSortMode === "newestCreated") {
        return getIdNum(b) - getIdNum(a);
      }
      const activityDiff = getActivityTs(b) - getActivityTs(a);
      if (activityDiff !== 0) return activityDiff;
      return getIdNum(b) - getIdNum(a);
    });

    return rows;
  }, [listGridRows, poolActivityMap, poolListSortMode, poolListFilterMode]);
  const poolListTotalPages = useMemo(
    () => Math.max(1, Math.ceil(sortedDisplayedPoolList.length / poolListPageSize)),
    [sortedDisplayedPoolList.length]
  );
  const pagedDisplayedPoolList = useMemo(() => {
    const startIndex = (poolListPage - 1) * poolListPageSize;
    return sortedDisplayedPoolList.slice(startIndex, startIndex + poolListPageSize);
  }, [sortedDisplayedPoolList, poolListPage]);
  const profileTotalPages = useMemo(
    () => Math.max(1, Math.ceil(profileRows.length / profilePageSize)),
    [profileRows.length]
  );
  const pagedProfileRows = useMemo(() => {
    const startIndex = (profilePage - 1) * profilePageSize;
    return profileRows.slice(startIndex, startIndex + profilePageSize);
  }, [profileRows, profilePage]);
  const homeRecentPresales = useMemo(
    () => displayedPoolList.slice(0, 3),
    [displayedPoolList]
  );
  const homeFeaturedPresale = homeRecentPresales[0] || null;
  const homeLaunchedPresales = useMemo(() => {
    const now = liveNowTs;
    return displayedPoolList
      .filter((row) => computePresaleDisplayState(
        row.pool,
        now,
        row.pool?.launchGracePeriod ?? launchGracePeriod
      ) === "Finalized")
      .slice(0, 4);
  }, [displayedPoolList, liveNowTs, launchGracePeriod]);
  const homeStats = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    let live = 0;
    let upcoming = 0;
    let launched = 0;
    let completed = 0;
    let totalParticipants = 0;
    let totalRaisedWei = 0n;
    for (const row of displayedPoolList) {
      const state = computePresaleDisplayState(
        row.pool,
        now,
        row.pool?.launchGracePeriod ?? launchGracePeriod
      );
      if (state === "Live") live += 1;
      else if (state === "Upcoming") upcoming += 1;
      else if (state === "Finalized") launched += 1;
      else if (state === "Completed") completed += 1;
      totalParticipants += Number(row.pool?.participantCount || 0);
      totalRaisedWei += getPresaleRaisedWei(row.pool);
    }
    return {
      total: displayedPoolList.length,
      live,
      upcoming,
      launched,
      completed,
      totalParticipants,
      totalRaisedWei
    };
  }, [displayedPoolList, launchGracePeriod]);
  const launchpadActivityRailItems = useMemo(() => {
    if (LAUNCHPAD_ACTIVITY_RAIL_PLACEHOLDER) {
      return LAUNCHPAD_ACTIVITY_RAIL_PLACEHOLDER_ITEMS.map((item, idx) => ({
        id: `placeholder-${idx}`,
        actor: item.actor,
        action: item.action,
        token: item.token,
        image: item.image,
        tone: item.action
      }));
    }
    if (!launchpadRailFeed.length) {
      return [];
    }
    return launchpadRailFeed.map((item) => ({
      id: String(item.id),
      actor: item.actor,
      action: item.action,
      token: item.token,
      image: item.image,
      tone: item.tone || "info",
      text: item.text || "",
      poolId: item.poolId ? String(item.poolId) : "",
      contractAddress: item.contractAddress ? String(item.contractAddress) : ""
    }));
  }, [launchpadRailFeed]);
  const summaryEstimatedTxFeeMon = useMemo(() => {
    const hardCap = Number(createForm.hardCap || 0) || 0;
    if (hardCap <= 0) return 0.04;
    if (hardCap < 100) return 0.03;
    if (hardCap < 1000) return 0.05;
    return 0.07;
  }, [createForm.hardCap]);

  const summaryStartTs = useMemo(
    () => Math.floor(scheduleStartAt.getTime() / 1000),
    [scheduleStartAt]
  );

  const summaryEndTs = useMemo(
    () => Math.floor(scheduleEndAt.getTime() / 1000),
    [scheduleEndAt]
  );

  const whitelistLineCount = useMemo(() => {
    const rows = String(createForm.whitelistAddresses || "").split(/\r?\n/).length;
    return Math.max(1, rows);
  }, [createForm.whitelistAddresses]);

  const whitelistRawLines = useMemo(
    () => String(createForm.whitelistAddresses || "").split(/\r?\n/),
    [createForm.whitelistAddresses]
  );

  const parsedWhitelistInput = useMemo(() => {
    const items = whitelistRawLines.map((rawLine, idx) => {
      const rowNumber = idx + 1;
      const line = String(rawLine || "").trim();
      const normalized = line.toLowerCase();
      const errors = [];
      if (line && !isLaunchpadAddress(line)) errors.push("Invalid address");
      return {
        rowNumber,
        rawLine,
        normalized,
        hasContent: line.length > 0,
        isValid: line.length > 0 && errors.length === 0,
        errors
      };
    });
    const duplicateAddresses = new Set(
      items
        .filter((it) => it.hasContent && !it.errors.length)
        .map((it) => it.normalized)
        .filter((normalized, i, arr) => arr.indexOf(normalized) !== i)
    );
    for (const item of items) {
      if (item.hasContent && !item.errors.length && duplicateAddresses.has(item.normalized)) {
        item.errors.push("Duplicate address");
        item.isValid = false;
      }
    }
    const invalidCount = items.filter((it) => it.hasContent && !it.isValid).length;
    const validCount = items.filter((it) => it.isValid).length;
    return { items, invalidCount, validCount, duplicateCount: duplicateAddresses.size };
  }, [whitelistRawLines]);

  const whitelistActiveLineHasContent = useMemo(() => {
    const rawLine = whitelistRawLines[whitelistActiveLine - 1] ?? "";
    return rawLine.trim().length > 0;
  }, [whitelistRawLines, whitelistActiveLine]);

  const whitelistActiveLineHasError = useMemo(() => {
    const item = parsedWhitelistInput.items.find((it) => it.rowNumber === whitelistActiveLine);
    return !!item?.errors?.length;
  }, [parsedWhitelistInput.items, whitelistActiveLine]);

  const route = useMemo(() => {
    const path = (location.pathname || "").toLowerCase();
    const rootHost = isLaunchpadRootHost();
    const launchpadPath = rootHost
      ? path
      : path.startsWith("/launchpad")
        ? path.slice("/launchpad".length) || "/"
        : "";
    if (!rootHost && !launchpadPath) return { mode: "list", poolId: "", contractAddress: "" };
    if (launchpadPath === "/" || launchpadPath === "") return { mode: "list", poolId: "", contractAddress: "" };
    if (launchpadPath === "/presales" || launchpadPath === "/presales/") return { mode: "list", poolId: "", contractAddress: "" };
    if (launchpadPath.startsWith("/create")) return { mode: "create", poolId: "", contractAddress: "" };
    if (launchpadPath.startsWith("/profile")) return { mode: "profile", poolId: "", contractAddress: "" };
    if (launchpadPath.startsWith("/documentation")) return { mode: "documentation", poolId: "", contractAddress: "" };
    const scopedPresales = launchpadPath.match(/^\/presales\/(0x[a-f0-9]{40})\/(\d+)/);
    if (scopedPresales) return { mode: "detail", poolId: scopedPresales[2], contractAddress: scopedPresales[1] };
    const presalesDetail = launchpadPath.match(/^\/presales\/(\d+)/);
    if (presalesDetail) return { mode: "detail", poolId: presalesDetail[1], contractAddress: "" };
    const scoped = launchpadPath.match(/^\/(0x[a-f0-9]{40})\/(\d+)/);
    if (scoped) return { mode: "detail", poolId: scoped[2], contractAddress: scoped[1] };
    const m = launchpadPath.match(/^\/(\d+)/);
    if (m) return { mode: "detail", poolId: m[1], contractAddress: "" };
    return { mode: "list", poolId: "", contractAddress: "" };
  }, [location.pathname]);

  useEffect(() => {
    setPoolListPage(1);
  }, [poolListSortMode, poolListFilterMode, devPreviewCardsEnabled, devPreviewCardCount]);

  useEffect(() => {
    setPoolListPage((prev) => Math.min(prev, poolListTotalPages));
  }, [poolListTotalPages]);

  useEffect(() => {
    if (route.mode !== "profile") {
      setProfilePage(1);
      return;
    }
    setProfilePage((prev) => Math.min(prev, profileTotalPages));
  }, [route.mode, profileTotalPages]);

  useEffect(() => {
    const syncCompactSortLabels = () => {
      setUseCompactSortLabels(window.innerWidth < MOBILE_SORT_LABEL_BREAKPOINT);
    };
    syncCompactSortLabels();
    window.addEventListener("resize", syncCompactSortLabels);
    return () => window.removeEventListener("resize", syncCompactSortLabels);
  }, []);

  useEffect(() => {
    if (!isLaunchpadDevConnected) {
      if (showDevConsole) setShowDevConsole(false);
      if (devPreviewCardsEnabled) setDevPreviewCardsEnabled(false);
      if (devDetailPreviewEnabled) setDevDetailPreviewEnabled(false);
    }
  }, [isLaunchpadDevConnected, showDevConsole, devPreviewCardsEnabled, devDetailPreviewEnabled]);

  const toggleDevConsole = useCallback(() => {
    setShowDevConsole((prev) => !prev);
  }, []);

  const isPoolCreator = useMemo(
    () =>
      String(wallet.address || "").toLowerCase() ===
      String(detailPool?.creator || "").toLowerCase(),
    [wallet.address, detailPool]
  );
  const walletConnectorId = String(wallet?.connector?.id || "").toLowerCase();
  const walletConnectorName = String(wallet?.connector?.name || "").toLowerCase();
  const isPhantomConnector = useMemo(() => {
    return walletConnectorId.includes("phantom") || walletConnectorName.includes("phantom");
  }, [walletConnectorId, walletConnectorName]);
  const hasLaunchpadWalletSession = useMemo(
    () => Boolean(String(wallet.address || "").trim()),
    [wallet.address]
  );
  const walletChainIdNum = normalizeLaunchpadChainId(wallet.chainId || wallet.networkId || 0);
  const isWrongNetwork = hasLaunchpadWalletSession && !isPhantomConnector && walletChainIdNum !== CHAIN_ID;

  const setStatus = useCallback((message, tone = "", link = null) => {
    const text = String(message || "");
    const normalized = tone || (
      /failed|error|wrong network|unavailable|required|invalid/i.test(text) ? "error" :
      /confirmed|synced|loaded|success/i.test(text) ? "success" :
      "info"
    );
    setStatusText(text);
    setStatusTone(normalized);
    setStatusLink(link);
  }, []);

  const resetCreateFormState = useCallback(() => {
    const nextStart = buildDefaultScheduleStart();
    const nextEnd = buildDefaultScheduleEnd(nextStart);
    setCreateForm(buildInitialCreateForm());
    setSelectedImage(null);
    setImagePreview("");
    setImageInfo("");
    setWhitelistFileName("");
    setWhitelistActiveLine(1);
    setWhitelistScrollTop(0);
    setOpenSelectKey("");
    setOpenDateTimeKey("");
    setOpenDateTimeSelectKey("");
    setScheduleStartAt(nextStart);
    setScheduleEndAt(nextEnd);
    setScheduleEndManuallyEdited(false);
    setScheduleStartViewMonth(startOfMonth(nextStart));
    setScheduleEndViewMonth(startOfMonth(nextEnd));
  }, []);

  const detailEstimate = useMemo(() => {
    if (!detailPool) return null;
    try {
      const devBuyWei = BigInt(detailPool.devBuyWei?.toString?.() ?? detailPool.devBuyWei ?? 0n);
      const currentRaisedWei = BigInt(detailPool.totalRaisedWei?.toString?.() ?? detailPool.totalRaisedWei ?? 0n);
      const fullRaisedWei = BigInt(detailPool.hardCapWei?.toString?.() ?? detailPool.hardCapWei ?? 0n);
      const creatorFeeRateBps = BigInt(routerCfg?.creatorFeeRate?.toString?.() || "100");

      const currentEstimatedTokensWei = estimateNadInitialBuyTokensOut(currentRaisedWei + devBuyWei, creatorFeeRateBps);
      const fullEstimatedTokensWei = estimateNadInitialBuyTokensOut(fullRaisedWei + devBuyWei, creatorFeeRateBps);
      const estimatedDevBuyTokensWei =
        (currentRaisedWei + devBuyWei) > 0n ? (devBuyWei * currentEstimatedTokensWei) / (currentRaisedWei + devBuyWei) : 0n;

      return {
        currentEstimatedTokensWei,
        fullEstimatedTokensWei,
        estimatedDevBuyTokensWei
      };
    } catch {
      return null;
    }
  }, [detailPool, routerCfg?.creatorFeeRate]);
  const detailStateNum = Number(detailPool?.state ?? -1);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setLiveNowTs(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  const detailNowTs = liveNowTs;
  const detailSoftCapWei = useMemo(
    () => BigInt(detailPool?.softCapWei?.toString?.() ?? detailPool?.softCapWei ?? 0n),
    [detailPool]
  );
  const detailHardCapWei = useMemo(
    () => BigInt(detailPool?.hardCapWei?.toString?.() ?? detailPool?.hardCapWei ?? 0n),
    [detailPool]
  );
  const detailSoftCapEnabled = detailSoftCapWei > 0n;
  const detailRaisedWei = useMemo(
    () => BigInt(detailPool?.totalRaisedWei?.toString?.() ?? detailPool?.totalRaisedWei ?? 0n),
    [detailPool]
  );
  const detailDevBuyWei = useMemo(
    () => BigInt(detailPool?.devBuyWei?.toString?.() ?? detailPool?.devBuyWei ?? 0n),
    [detailPool]
  );
  const detailParticipationFeeBps = resolveParticipationFeeBps(participationFeeBps);
  const detailRaisedWithDevWei = detailRaisedWei + detailDevBuyWei;
  const detailCapPhase = useMemo(() => {
    if (detailHardCapWei > 0n && detailRaisedWei >= detailHardCapWei) return "hard";
    if (detailSoftCapWei > 0n && detailRaisedWei >= detailSoftCapWei) return "soft";
    return "none";
  }, [detailRaisedWei, detailHardCapWei, detailSoftCapWei]);
  const detailSoftSectionFillPct = useMemo(() => {
    if (detailSoftCapWei <= 0n) return 0;
    if (detailRaisedWei <= 0n) return 0;
    if (detailRaisedWei >= detailSoftCapWei) return 100;
    return Number((detailRaisedWei * 10000n) / detailSoftCapWei) / 100;
  }, [detailRaisedWei, detailSoftCapWei]);
  const detailHardSectionFillPct = useMemo(() => {
    if (detailHardCapWei <= detailSoftCapWei) return 0;
    if (detailRaisedWei <= detailSoftCapWei) return 0;
    if (detailRaisedWei >= detailHardCapWei) return 100;
    return Number(((detailRaisedWei - detailSoftCapWei) * 10000n) / (detailHardCapWei - detailSoftCapWei)) / 100;
  }, [detailRaisedWei, detailSoftCapWei, detailHardCapWei]);
  const detailTrackGridStyle = useMemo(() => {
    if (!detailSoftCapEnabled) return undefined;
    if (detailHardCapWei <= 0n || detailHardCapWei <= detailSoftCapWei) return undefined;
    const softRatio = Number((detailSoftCapWei * 10000n) / detailHardCapWei) / 10000;
    const hardRatio = Math.max(0.0001, 1 - softRatio);
    return {
      "--lp-stage-soft-fr": `${softRatio}fr`,
      "--lp-stage-hard-fr": `${hardRatio}fr`,
    };
  }, [detailSoftCapEnabled, detailSoftCapWei, detailHardCapWei]);
  const detailDisplayState = useMemo(() => {
    if (!detailPool) return "-";
    return computePresaleDisplayState(detailPool, detailNowTs, launchGracePeriod);
  }, [detailPool, detailNowTs, launchGracePeriod]);
  const detailParticipantClaimableWei = useMemo(
    () => BigInt(detailClaimPreview?.claimable?.toString?.() ?? detailClaimPreview?.claimable ?? 0n),
    [detailClaimPreview]
  );
  const detailParticipantGrossWei = useMemo(
    () => BigInt(detailClaimPreview?.gross?.toString?.() ?? detailClaimPreview?.gross ?? 0n),
    [detailClaimPreview]
  );
  const detailParticipantTokenWei = useMemo(() => {
    if (detailParticipantGrossWei > 0n) return detailParticipantGrossWei;
    if (detailRaisedWithDevWei <= 0n || detailContributionWeiRaw <= 0n) return 0n;
    const estimatedTokensWei = BigInt(detailEstimate?.currentEstimatedTokensWei?.toString?.() ?? detailEstimate?.currentEstimatedTokensWei ?? 0n);
    if (estimatedTokensWei <= 0n) return 0n;
    return (detailContributionWeiRaw * estimatedTokensWei) / detailRaisedWithDevWei;
  }, [detailParticipantGrossWei, detailRaisedWithDevWei, detailContributionWeiRaw, detailEstimate]);
  const detailSoldTokensWei = useMemo(() => {
    const launchedTokensWei = BigInt(detailPool?.tokensPurchased?.toString?.() ?? detailPool?.tokensPurchased ?? 0n);
    if (launchedTokensWei > 0n) return launchedTokensWei;
    return BigInt(detailEstimate?.currentEstimatedTokensWei?.toString?.() ?? detailEstimate?.currentEstimatedTokensWei ?? 0n);
  }, [detailPool, detailEstimate]);
  const detailDevClaimGrossWei = useMemo(() => {
    if (detailRaisedWithDevWei <= 0n || !detailPool) return 0n;
    const tokensPurchased = BigInt(detailPool.tokensPurchased?.toString?.() ?? detailPool.tokensPurchased ?? 0n);
    if (tokensPurchased <= 0n || detailDevBuyWei <= 0n) return 0n;
    return (detailDevBuyWei * tokensPurchased) / detailRaisedWithDevWei;
  }, [detailPool, detailRaisedWithDevWei, detailDevBuyWei]);
  const detailDevClaimableWei = useMemo(() => {
    return detailDevClaimGrossWei > detailDevClaimedRaw ? detailDevClaimGrossWei - detailDevClaimedRaw : 0n;
  }, [detailDevClaimGrossWei, detailDevClaimedRaw]);
  const detailLaunchedToken = String(detailPool?.launchedToken || "").trim();
  const detailHasLaunchedToken = !!detailLaunchedToken && detailLaunchedToken !== ethers.ZeroAddress;
  const detailTokenAddressDisplay = detailHasLaunchedToken ? detailLaunchedToken : "";
  const detailCanJoin = !!detailPool
    && detailStateNum === 0
    && detailNowTs >= Number(detailPool.startTime || 0)
    && detailNowTs <= Number(detailPool.endTime || 0)
    && detailRaisedWei < detailHardCapWei;
  const detailCanFinalize = !!detailPool
    && isPoolCreator
    && detailStateNum === 0
    && !detailHasLaunchedToken
    && !isPresaleLaunchExpired(detailPool, detailNowTs, launchGracePeriod)
    && (detailRaisedWei >= detailHardCapWei || detailNowTs > Number(detailPool.endTime || 0))
    && detailRaisedWei >= detailSoftCapWei;
  const detailRefundEligible = !!detailPool && isPresaleRefundable(detailPool, detailNowTs, launchGracePeriod);
  const detailCanRefund = detailRefundEligible && detailContributionWeiRaw > 0n;
  const detailCanClaim = detailStateNum === 1 && detailParticipantClaimableWei > 0n;
  const detailShowClaimDev = isPoolCreator && detailStateNum === 1;
  const detailCanClaimDev = detailShowClaimDev && detailDevClaimableWei > 0n;
  const detailCanRefundDev = isPoolCreator && detailRefundEligible && detailDevBuyWei > 0n;
  const detailShowCreatorPanel = isPoolCreator && (detailHasLaunchedToken || detailShowClaimDev || detailCanRefundDev || detailCanFinalize);
  const detailHardHit = detailCapPhase === "hard";
  const detailStatusBoxValue = presentPresaleDisplayState(detailDisplayState);
  const detailTimeBox = useMemo(() => {
    if (!detailPool) return { label: "Ends In", value: "-" };
      if (detailDisplayState === "Upcoming") {
        return {
          label: "Starts In",
          value: fmtRelativeDurationLong(Number(detailPool.startTime || 0) - detailNowTs)
        };
      }
      if (detailDisplayState === "Live") {
        return {
          label: "Ends In",
          value: fmtRelativeDurationLong(Number(detailPool.endTime || 0) - detailNowTs)
        };
      }
      if (detailDisplayState === "Completed") {
        return {
          label: "Launch In",
          value: fmtRelativeDurationLong(resolvePresaleLaunchDeadlineTs(detailPool, launchGracePeriod) - detailNowTs)
        };
      }
      const endedTs =
        detailDisplayState === "Finalized"
          ? resolvePresaleLaunchReadyAt(detailPool)
          : Number(detailPool.endTime || 0);
      return {
        label: "Ended",
        value: endedTs > 0 ? `${fmtRelativeDurationLong(Math.max(0, detailNowTs - endedTs))} Ago` : "-"
      };
    }, [detailPool, detailDisplayState, detailNowTs, launchGracePeriod]);
  const detailBuyPlaceholder = useMemo(() => {
    if (!detailPool) return "Enter MON amount";
    if (detailCanJoin) return `Min ${fmtMon(formatEther(detailPool.minContributionWei), 4)} MON`;
    if (detailDisplayState === "Upcoming") return "Presale not started";
    if (detailDisplayState === "Completed") return "Completed";
    if (detailDisplayState === "Failed") return detailCanRefund ? "Refund available" : "No refund available";
    if (detailDisplayState === "Cancelled") return detailCanRefund ? "Refund available" : "Presale cancelled";
    if (detailDisplayState === "Finalized") return detailCanClaim ? "Claim available" : "Presale launched";
    return String(detailDisplayState || "Unavailable");
  }, [detailPool, detailCanJoin, detailDisplayState, detailCanRefund, detailCanClaim, detailParticipationFeeBps]);
  const detailBuyButtonLabel = useMemo(() => {
    if (isWrongNetwork) return "Wrong Network";
    if (detailCanJoin && !hasLaunchpadWalletSession) return "Connect";
    if (detailCanJoin) return "Buy";
    return String(detailDisplayState || "Unavailable");
  }, [detailCanJoin, hasLaunchpadWalletSession, detailDisplayState, isWrongNetwork]);
  const detailPrimaryAction = useMemo(() => {
    if (detailDisplayState === "Finalized") {
      return {
        mode: "claim",
        label: detailCanClaim ? "Claim Tokens" : "Nothing to Claim",
        disabled: loading || isWrongNetwork || !detailCanClaim
      };
    }
    if (detailDisplayState === "Failed" || detailDisplayState === "Cancelled") {
      return {
        mode: "refund",
        label: detailCanRefund ? "Claim Refund" : "No Refund Available",
        disabled: loading || isWrongNetwork || !detailCanRefund
      };
    }
    return {
      mode: "buy",
      label: detailBuyButtonLabel,
      disabled: loading || isWrongNetwork || !detailCanJoin
    };
  }, [
    detailDisplayState,
    detailCanClaim,
    detailCanRefund,
    detailBuyButtonLabel,
    detailCanJoin,
    loading,
    isWrongNetwork
  ]);
  const detailShowsBuyInput = detailPrimaryAction.mode === "buy";
  const detailFeeSplit = useMemo(() => ({
    burn: Number(safeResultField(detailPool, "burnBps", 0)) / 100,
    lp: Number(safeResultField(detailPool, "lpBps", 0)) / 100,
    creator: Number(safeResultField(detailPool, "creatorBps", 0)) / 100,
    gift: Number(safeResultField(detailPool, "giftBps", 0)) / 100,
    dividend: Number(safeResultField(detailPool, "dividendBps", 0)) / 100
  }), [detailPool]);
  const detailGiftXHandle = useMemo(
    () => String(safeResultField(detailPool, "giftXHandle", "")).trim().replace(/^@+/, ""),
    [detailPool]
  );
  const detailDividendRewards = useMemo(() => {
    const tokens = Array.isArray(detailPool?.dividendRewardTokens) ? detailPool.dividendRewardTokens : [];
    const ratios = Array.isArray(detailPool?.dividendRewardRatios) ? detailPool.dividendRewardRatios : [];
    return tokens.map((token, index) => ({
      token: String(token || ""),
      ratioBps: Number(ratios[index] || 0) || 0
    }));
  }, [detailPool]);
  const detailFeeLegendItems = useMemo(
    () => [
      { key: "burn", label: "Burn", value: detailFeeSplit.burn, dotClass: "lp-fee-dot-burn" },
      { key: "lp", label: "LP", value: detailFeeSplit.lp, dotClass: "lp-fee-dot-lp" },
      { key: "creator", label: "Creator", value: detailFeeSplit.creator, dotClass: "lp-fee-dot-creator" },
      { key: "gift", label: "Gift", value: detailFeeSplit.gift, dotClass: "lp-fee-dot-gift" },
      { key: "dividend", label: "Dividend", value: detailFeeSplit.dividend, dotClass: "lp-fee-dot-dividend" }
    ].filter((item) => item.value > 0),
    [detailFeeSplit]
  );
  const detailMaxJoinNetWei = useMemo(() => {
    if (!detailPool) return 0n;
    const maxContributionWei = BigInt(detailPool.maxContributionWei?.toString?.() ?? detailPool.maxContributionWei ?? 0n);
    const perWalletRemaining = maxContributionWei > detailContributionWeiRaw ? maxContributionWei - detailContributionWeiRaw : 0n;
    const poolRemaining = detailHardCapWei > detailRaisedWei ? detailHardCapWei - detailRaisedWei : 0n;
    return perWalletRemaining < poolRemaining ? perWalletRemaining : poolRemaining;
  }, [detailPool, detailContributionWeiRaw, detailHardCapWei, detailRaisedWei]);
  const detailMaxJoinWei = detailMaxJoinNetWei;

  const getReadProvider = useCallback(() => new ethers.JsonRpcProvider(DEFAULT_RPC), []);
  const getReadContract = useCallback((targetAddress = contractAddress) => new ethers.Contract(targetAddress, launchpadAbi, getReadProvider()), [contractAddress, getReadProvider]);
  const getWriteProviderEntry = useCallback(async () => {
    const connectorProvider =
      typeof wallet?.connector?.getProvider === "function"
        ? await wallet.connector.getProvider().catch(() => null)
        : null;
    const injectedCandidates = getLaunchpadInjectedProviderCandidates();
    const expectedAddress = String(wallet.address || "").trim();
    if (!expectedAddress) throw new Error("Connect wallet");
    const expectedAddressLower = expectedAddress.toLowerCase();

    if (isPhantomConnector) {
      const phantomCandidates = [];
      const pushPhantomCandidate = (provider, label = "") => {
        if (!provider) return;
        if (phantomCandidates.some((entry) => entry.provider === provider)) return;
        phantomCandidates.push({ provider, label });
      };

      pushPhantomCandidate(connectorProvider, "connector");
      for (const entry of injectedCandidates) {
        if (
          entry?.provider?.isPhantom ||
          entry.label === "injected.phantom" ||
          entry.label === "window.phantom.ethereum"
        ) {
          pushPhantomCandidate(entry.provider, entry.label);
        }
      }
      if (!phantomCandidates.length) {
        for (const entry of injectedCandidates) {
          pushPhantomCandidate(entry.provider, entry.label);
        }
      }

      let lastMismatch = null;
      for (const entry of phantomCandidates) {
        const shouldRequestAccess =
          entry.provider === connectorProvider ||
          entry.label === "window.phantom.ethereum" ||
          entry.label === "injected.phantom";
        const accounts = await readLaunchpadProviderAccounts(entry.provider, { requestAccess: shouldRequestAccess });
        const hasExpectedAddress = accounts.includes(expectedAddressLower);
        const chainId = await readLaunchpadProviderChainId(entry.provider);
        if (!hasExpectedAddress) {
          lastMismatch = new Error("Wallet account mismatch. Reconnect the same wallet shown in the navbar.");
          continue;
        }
        const balanceWei = await readLaunchpadProviderBalance(entry.provider, expectedAddress);
        return {
          ...entry,
          chainId,
          accounts,
          hasExpectedAddress: true,
          balanceWei,
          address: expectedAddress
        };
      }

      throw lastMismatch || new Error("Wallet provider not found");
    }

    const candidateProviders = [];
    const pushCandidate = (provider, priority = 0, label = "") => {
      if (!provider) return;
      if (candidateProviders.some((entry) => entry.provider === provider)) return;
      candidateProviders.push({ provider, priority, label });
    };

    const lastWriteProvider = lastWriteProviderRef.current;
    if (
      lastWriteProvider?.provider &&
      String(lastWriteProvider?.address || "").toLowerCase() === expectedAddressLower
    ) {
      pushCandidate(lastWriteProvider.provider, -100, lastWriteProvider.label || "last-success");
    }

    pushCandidate(connectorProvider, 0, "connector");
    for (const entry of injectedCandidates) {
      const priority =
        entry.label === "window.ethereum" ? 1 :
        entry.label === "injected.metamask" ? 2 :
        entry.label === "injected.coinbase" ? 3 :
        entry.label === "injected.phantom" ? 4 :
        entry.label === "window.phantom.ethereum" ? 5 :
        6;
      pushCandidate(entry.provider, priority, entry.label);
    }

    if (!candidateProviders.length) throw new Error("Wallet provider not found");

    const rankedProviders = await Promise.all(
      candidateProviders.map(async (entry) => {
        let chainId = await readLaunchpadProviderChainId(entry.provider);
        let accounts = await readLaunchpadProviderAccounts(entry.provider);
        let hasExpectedAddress = accounts.includes(expectedAddressLower);
        const balanceWei = (hasExpectedAddress || chainId === CHAIN_ID)
          ? await readLaunchpadProviderBalance(entry.provider, expectedAddress)
          : 0n;
        return {
          ...entry,
          chainId,
          accounts,
          hasExpectedAddress,
          balanceWei
        };
      })
    );

    rankedProviders.sort((a, b) => {
      const aChainMatch = a.chainId === CHAIN_ID ? 1 : 0;
      const bChainMatch = b.chainId === CHAIN_ID ? 1 : 0;
      if (aChainMatch !== bChainMatch) return bChainMatch - aChainMatch;

      const aAccountMatch = a.hasExpectedAddress ? 1 : 0;
      const bAccountMatch = b.hasExpectedAddress ? 1 : 0;
      if (aAccountMatch !== bAccountMatch) return bAccountMatch - aAccountMatch;

      if (a.balanceWei !== b.balanceWei) return a.balanceWei > b.balanceWei ? -1 : 1;

      return a.priority - b.priority;
    });

    let mismatchDetected = false;
    let lastError = null;

    for (const entry of rankedProviders) {
      try {
        if (!entry.hasExpectedAddress) {
          mismatchDetected = true;
          continue;
        }
        const resolvedChainId = normalizeLaunchpadChainId(entry.chainId);
        if (!isPhantomConnector && resolvedChainId && resolvedChainId !== CHAIN_ID) {
          lastError = new Error(`Wallet provider is on chain ${resolvedChainId}, expected ${CHAIN_ID}`);
          continue;
        }
        return {
          ...entry,
          address: expectedAddress
        };
      } catch (error) {
        lastError = error;
      }
    }

    if (mismatchDetected) {
      throw new Error("Wallet account mismatch. Reconnect the same wallet shown in the navbar.");
    }
    throw lastError || new Error("Wallet provider not found");
  }, [wallet?.connector, wallet.address, isPhantomConnector]);
  const writeLaunchpadContract = useCallback(async ({
    targetAddress = contractAddress,
    functionName,
    args = [],
    value = 0n
  }) => {
    const entry = await getWriteProviderEntry();
    const iface = new ethers.Interface(launchpadAbi);
    const txValue = BigInt(value ?? 0n);
    const data = iface.encodeFunctionData(functionName, args);
    const txRequest = {
      from: entry.address,
      to: targetAddress,
      data,
      chainId: ethers.toQuantity(CHAIN_ID)
    };
    if (txValue > 0n) {
      txRequest.value = ethers.toQuantity(txValue);
    }

    try {
      const readProvider = getReadProvider();
      const gasEstimate = await readProvider.estimateGas({
        from: entry.address,
        to: targetAddress,
        data,
        value: txValue
      });
      const gasWithBuffer = gasEstimate + (gasEstimate / 5n) + 25_000n;
      txRequest.gas = ethers.toQuantity(gasWithBuffer);
    } catch {}

    const hash = await entry.provider.request({
      method: "eth_sendTransaction",
      params: [txRequest]
    });

    lastWriteProviderRef.current = {
      provider: entry.provider,
      label: entry.label || "",
      address: entry.address
    };

    return {
      hash: String(hash || "")
    };
  }, [contractAddress, getReadProvider, getWriteProviderEntry]);
  const getPoolContractAddress = useCallback((poolLike = null) => (
    normalizeScopedContractAddress(poolLike?.contractAddress) ||
    normalizeScopedContractAddress(route.contractAddress) ||
    normalizeScopedContractAddress(contractAddress) ||
    contractAddress
  ), [route.contractAddress, contractAddress]);

  const refreshGlobal = useCallback(async () => {
    if (!contractAddress) return;
    try {
      const c = getReadContract();
      const [n, rc, maxBuy, grace, participationFee] = await Promise.all([
        readNamedLaunchpadCall("nextPoolId()", () => c.nextPoolId()),
        readNamedLaunchpadCall("routerConfig()", () => c.routerConfig()),
        readNamedLaunchpadCall("maxInitialBuyWei()", () => c.maxInitialBuyWei()),
        readNamedLaunchpadCall("launchGracePeriod()", () => c.launchGracePeriod()),
        readNamedLaunchpadCall("participationFeeBps()", () => c.participationFeeBps())
      ]);
      setNextPoolId(String(n));
      setMaxInitialBuyWei(String(maxBuy));
      setLaunchGracePeriod(resolveLaunchpadGracePeriod(grace));
      setParticipationFeeBps(resolveParticipationFeeBps(participationFee));
      setRouterCfg(rc);
    } catch (e) {
      setStatus(`Global read failed: ${e?.shortMessage || e?.message || "Unknown error"}`);
    }
  }, [contractAddress, getReadContract, setStatus]);

  const refreshPoolList = useCallback(async ({ force = false } = {}) => {
    setPoolListLoading(true);
    const applyRows = (rows, options = {}) => {
      const {
        suppressActivity = false,
        activityMapSeed = null,
        activityFeedSeed = null
      } = options;
      const nowTs = Math.floor(Date.now() / 1000);
      const normalizedRows = rows.map(({ id, pool }) => {
        const normalizedPool = {
          ...pool,
          launchGracePeriod: resolveLaunchpadGracePeriod(pool?.launchGracePeriod ?? launchGracePeriod)
        };
        normalizedPool.displayState = computePresaleDisplayState(
          normalizedPool,
          nowTs,
          normalizedPool.launchGracePeriod
        );
        return { id: Number(id), pool: normalizedPool };
      });
      const prevRows = poolListRef.current;
      const prevById = new Map(prevRows.map((row) => [String(row.id), row.pool]));
      const nextActivity = activityMapSeed ? { ...activityMapSeed } : { ...poolActivityMapRef.current };
      const nextFeed = Array.isArray(activityFeedSeed) ? [...activityFeedSeed] : [...launchpadActivityFeedRef.current];
      const nowMs = Date.now();
      const pushFeedEntry = (text, tone = "info") => {
        if (!text) return;
        nextFeed.unshift({
          id: `${nowMs}-${Math.random().toString(36).slice(2, 8)}`,
          text,
          tone,
          ts: nowMs
        });
      };
      const formatSymbol = (pool) => String(pool?.symbol || pool?.name || "PRESALE").trim().toUpperCase();
      const formatCreator = (pool) => shortAddr(String(pool?.creator || ""));

      if (!suppressActivity) {
        for (const { id, pool } of normalizedRows) {
          const key = String(id);
          const prevPool = prevById.get(key);
          const currentRaisedWei = BigInt(pool?.totalRaisedWei?.toString?.() ?? pool?.totalRaisedWei ?? 0n);
          const currentParticipantCount = Number(pool?.participantCount || 0);
          const currentState = Number(pool?.state ?? -1);
          const symbol = formatSymbol(pool);
          const creatorLabel = formatCreator(pool);

          if (!prevPool) {
            nextActivity[key] = nowMs;
            pushFeedEntry(`${creatorLabel} created ${symbol}`, "created");
            continue;
          }

          const prevRaisedWei = BigInt(prevPool?.totalRaisedWei?.toString?.() ?? prevPool?.totalRaisedWei ?? 0n);
          const prevParticipantCount = Number(prevPool?.participantCount || 0);
          const prevState = Number(prevPool?.state ?? -1);
          const currentDisplayState = String(pool?.displayState || "");
          const prevDisplayState = String(prevPool?.displayState || "");

          if (
            currentRaisedWei !== prevRaisedWei ||
            currentParticipantCount !== prevParticipantCount ||
            currentState !== prevState ||
            currentDisplayState !== prevDisplayState
          ) {
            nextActivity[key] = nowMs;
            if (currentState !== prevState) {
              if (currentState === 1) {
                pushFeedEntry(`${creatorLabel} finalized ${symbol}`, "finalized");
              } else if (currentState === 2) {
                pushFeedEntry(`${creatorLabel} cancelled ${symbol}`, "failed");
              } else if (currentState === 0 && prevState === 1) {
                pushFeedEntry(`${symbol} reopened`);
              }
            }
            if (currentDisplayState !== prevDisplayState && currentDisplayState === "Failed") {
              pushFeedEntry(`${symbol} failed`, "failed");
            }
            if (currentParticipantCount > prevParticipantCount || currentRaisedWei > prevRaisedWei) {
              pushFeedEntry(`New buy in ${symbol}`, "bought");
            }
          } else if (nextActivity[key] == null) {
            nextActivity[key] = 0;
          }
        }
      }

      Object.keys(nextActivity).forEach((key) => {
        if (!normalizedRows.some((row) => String(row.id) === key)) delete nextActivity[key];
      });

      const trimmedFeed = nextFeed.slice(0, 20);
      const nextRowsSignature = getPoolListSnapshotSignature(normalizedRows);
      const prevRowsSignature = getPoolListSnapshotSignature(prevRows);
      const nextActivitySignature = JSON.stringify(nextActivity);
      const prevActivitySignature = JSON.stringify(poolActivityMapRef.current || {});
      const nextFeedSignature = JSON.stringify(trimmedFeed);
      const prevFeedSignature = JSON.stringify(launchpadActivityFeedRef.current || []);

      poolActivityMapRef.current = nextActivity;
      launchpadActivityFeedRef.current = trimmedFeed;
      poolListRef.current = normalizedRows;

      if (nextActivitySignature !== prevActivitySignature) {
        setPoolActivityMap(nextActivity);
      }
      if (nextFeedSignature !== prevFeedSignature) {
        setLaunchpadActivityFeed(trimmedFeed);
      }
      if (nextRowsSignature !== prevRowsSignature) {
        setPoolList(normalizedRows);
      }
      return normalizedRows;
    };

    try {
      if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
      const rows = await fetchPublicPresalesViaBackend({ force });
      return applyRows(rows.map((pool) => ({ id: Number(pool.id), pool })));
    } catch (error) {
      console.warn("[launchpad] public presales backend failed:", error);
      setPoolList([]);
      throw error;
    } finally {
      setPoolListLoading(false);
    }
  }, [contractAddress, getReadContract, launchGracePeriod, nextPoolId]);

  const refreshLaunchpadActivity = useCallback(async ({ force = false } = {}) => {
    if (LAUNCHPAD_ACTIVITY_RAIL_PLACEHOLDER) return [];
    if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
    const items = await fetchLaunchpadActivityViaBackend({ force });
    setLaunchpadRailFeed((prev) => {
      const merged = mergeActivityRailFeed(prev, items, 20);
      writeCachedLaunchpadActivityFeed(merged);
      return merged;
    });
    return items;
  }, []);

  const refreshDetailPool = useCallback(async () => {
    if (route.mode !== "detail" || !route.poolId) return;
    setDetailLoading(true);
    setDetailLoaded(false);
    const routeContractAddress = normalizeScopedContractAddress(route.contractAddress);
    const applyDetailPool = (pool) => {
      setDetailPool(pool);
    };

    try {
      if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
      const p = await fetchPublicPresaleViaBackend(route.poolId, routeContractAddress);
      applyDetailPool(p);
      setDetailParticipantCount(Number(p?.participantCount || 0));
      const targetContractAddress = getPoolContractAddress(p);
      if (wallet.address) {
        const c = getReadContract(targetContractAddress);
        const id = BigInt(route.poolId);
        const [cp, contributed, devClaimed] = await Promise.all([
          c.participantClaimPreview(id, wallet.address).catch(() => null),
          c.contributedWei(id, wallet.address).catch(() => 0n),
          c.devClaimedTokenAmount(id).catch(() => 0n)
        ]);
        setDetailClaimPreview(cp);
        setDetailContributionWeiRaw(BigInt(contributed?.toString?.() ?? contributed ?? 0n));
        setDetailDevClaimedRaw(BigInt(devClaimed?.toString?.() ?? devClaimed ?? 0n));
      } else {
        const c = getReadContract(targetContractAddress);
        const id = BigInt(route.poolId);
        const devClaimed = await c.devClaimedTokenAmount(id).catch(() => 0n);
        setDetailClaimPreview(null);
        setDetailContributionWeiRaw(0n);
        setDetailDevClaimedRaw(BigInt(devClaimed?.toString?.() ?? devClaimed ?? 0n));
      }
    } catch (error) {
      console.warn("[launchpad] public presale backend failed:", error);
      setDetailPool(null);
      throw error;
    } finally {
      setDetailLoading(false);
      setDetailLoaded(true);
    }
  }, [route.mode, route.poolId, route.contractAddress, contractAddress, getPoolContractAddress, getReadContract, wallet.address]);

  useEffect(() => {
    refreshGlobal();
  }, [refreshGlobal]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!wallet?.isConnected || !wallet?.address) {
        if (!cancelled) setCreateNativeBalanceRaw(0n);
        return;
      }
      try {
        const provider = getReadProvider();
        const bal = await provider.getBalance(wallet.address);
        if (!cancelled) setCreateNativeBalanceRaw(bal);
      } catch {
        if (!cancelled) setCreateNativeBalanceRaw(0n);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [wallet?.isConnected, wallet?.address, getReadProvider]);

  useEffect(() => {
    if (route.mode === "list") {
      refreshGlobal().then(refreshPoolList).catch((e) => setStatus(e?.shortMessage || e?.message || "List load failed"));
    }
    if (route.mode === "detail") refreshDetailPool().catch((e) => setStatus(e?.shortMessage || e?.message || "Detail load failed"));
  }, [route.mode, refreshGlobal, refreshPoolList, refreshDetailPool, setStatus]);

  useEffect(() => {
    setIsSearchOpen(false);
  }, [route.mode, route.poolId]);

  useEffect(() => {
    setStatusText("");
    setStatusTone("info");
    setStatusLink(null);
  }, [route.mode, route.poolId, route.contractAddress]);

  useEffect(() => {
    if (route.mode !== "list") {
      setActiveListSearchQuery("");
      setActiveListSearchRows([]);
    }
  }, [route.mode]);

  useEffect(() => {
    if (route.mode === "documentation" || devPreviewCardsEnabled) return undefined;
    refreshLaunchpadActivity().catch((e) => {
      console.warn("[launchpad] backend activity refresh failed:", e);
    });
    const intervalId = window.setInterval(() => {
      refreshLaunchpadActivity({ force: true }).catch((e) => {
        console.warn("[launchpad] backend activity refresh failed:", e);
      });
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [route.mode, devPreviewCardsEnabled, refreshLaunchpadActivity]);

  useEffect(() => {
    if (route.mode !== "list" || devPreviewCardsEnabled) return undefined;
    const intervalId = window.setInterval(() => {
      refreshPoolList({ force: true }).catch((e) => {
        console.warn("[launchpad] live list refresh failed:", e);
      });
    }, 10000);
    return () => window.clearInterval(intervalId);
  }, [route.mode, devPreviewCardsEnabled, refreshPoolList]);

  useEffect(() => {
    if (route.mode !== "detail" || devPreviewCardsEnabled) return undefined;
    refreshDetailPool().catch((e) => {
      console.warn("[launchpad] live detail refresh failed:", e);
    });
    const intervalId = window.setInterval(() => {
      refreshDetailPool().catch((e) => {
        console.warn("[launchpad] live detail refresh failed:", e);
      });
    }, 10000);
    return () => window.clearInterval(intervalId);
  }, [route.mode, devPreviewCardsEnabled, refreshDetailPool]);

  useEffect(() => {
    if (route.mode !== "detail" || !detailPool?.id) return;
    const imageUri =
      detailTokenMeta?.image_uri ||
      detailTokenMeta?.image ||
      detailPool?.tokenMeta?.image_uri ||
      detailPool?.tokenMeta?.image ||
      "";
    const nextEntry = {
      id: String(detailPool.id),
      contractAddress: String(detailPool.contractAddress || ""),
      name: String(detailPool.name || ""),
      symbol: String(detailPool.symbol || ""),
      image: imageUri ? ipfsToHttp(imageUri) : "",
      createdAt: Number(detailPool.createdAt || 0),
      totalRaisedWei: String(detailPool.totalRaisedWei || "0"),
      displayState: computePresaleDisplayState(
        detailPool,
        liveNowTs,
        detailPool?.launchGracePeriod ?? launchGracePeriod
      )
    };
    setRecentViewedPresales((prev) => {
      const next = [nextEntry, ...prev.filter((item) => String(item?.id) !== nextEntry.id)].slice(0, 6);
      writeRecentViewedPresales(next);
      return next;
    });
  }, [route.mode, detailPool, detailTokenMeta, liveNowTs, launchGracePeriod]);

  useEffect(() => {
    if (route.mode === "list" || route.mode === "documentation" || devPreviewCardsEnabled) return undefined;
    refreshPoolList({ force: true }).catch((e) => {
      console.warn("[launchpad] activity rail refresh failed:", e);
    });
    const intervalId = window.setInterval(() => {
      refreshPoolList({ force: true }).catch((e) => {
        console.warn("[launchpad] activity rail refresh failed:", e);
      });
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [route.mode, devPreviewCardsEnabled, refreshPoolList]);

  useEffect(() => {
    const viewportEl = railViewportRef.current;
    const groupEl = railGroupRef.current;
    if (!viewportEl || !groupEl || !launchpadActivityRailItems.length) return undefined;

    const syncRepeatCount = () => {
      const viewportWidth = viewportEl.offsetWidth || 0;
      const groupWidth = groupEl.offsetWidth || 0;
      if (!viewportWidth || !groupWidth) return;
      const nextCount = Math.max(3, Math.ceil(viewportWidth / groupWidth) + 2);
      setRailRepeatCount((prev) => (prev === nextCount ? prev : nextCount));
    };

    syncRepeatCount();

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        syncRepeatCount();
      });
      resizeObserver.observe(viewportEl);
      resizeObserver.observe(groupEl);
    } else {
      window.addEventListener("resize", syncRepeatCount);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", syncRepeatCount);
      }
    };
  }, [launchpadActivityRailItems]);

  useEffect(() => {
    railPausedRef.current = railPaused;
  }, [railPaused]);

  useEffect(() => {
    const trackEl = railTrackRef.current;
    const viewportEl = railViewportRef.current;
    const groupEl = railGroupRef.current;
    if (!trackEl || !viewportEl || !groupEl || !launchpadActivityRailItems.length) return undefined;

    const speedPxPerSecond = 28;
    const step = (frameTs) => {
      const groupWidth = groupEl.offsetWidth || 0;
      if (!groupWidth) {
        railRafRef.current = window.requestAnimationFrame(step);
        return;
      }
      if (railPausedRef.current) {
        railLastFrameRef.current = frameTs;
        railRafRef.current = window.requestAnimationFrame(step);
        return;
      }
      if (!railLastFrameRef.current) railLastFrameRef.current = frameTs;
      const deltaSeconds = (frameTs - railLastFrameRef.current) / 1000;
      railLastFrameRef.current = frameTs;
      railOffsetRef.current -= speedPxPerSecond * deltaSeconds;
      while (railOffsetRef.current <= -groupWidth) {
        railOffsetRef.current += groupWidth;
      }
      trackEl.style.transform = `translate3d(${railOffsetRef.current}px, 0, 0)`;
      railRafRef.current = window.requestAnimationFrame(step);
    };

    railLastFrameRef.current = 0;
    if (railRafRef.current) window.cancelAnimationFrame(railRafRef.current);
    trackEl.style.transform = `translate3d(${railOffsetRef.current}px, 0, 0)`;
    railRafRef.current = window.requestAnimationFrame(step);

    return () => {
      if (railRafRef.current) window.cancelAnimationFrame(railRafRef.current);
      railRafRef.current = 0;
      railLastFrameRef.current = 0;
      railPausedRef.current = false;
    };
  }, [launchpadActivityRailItems, railRepeatCount]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
        if ((route.mode !== "list" && route.mode !== "profile") || !poolList.length) {
        setPoolListTokenMeta({});
        return;
      }
      const backendMeta = Object.fromEntries(
        poolList
          .map(({ id, pool }) => [String(id), pool?.tokenMeta && typeof pool.tokenMeta === "object" ? pool.tokenMeta : null])
          .filter(([, meta]) => meta)
      );
      if (Object.keys(backendMeta).length === poolList.length) {
        setPoolListTokenMeta(backendMeta);
        return;
      }
      const entries = await Promise.all(
        poolList.map(async ({ id, pool }) => {
          try {
            if (pool?.tokenMeta && typeof pool.tokenMeta === "object") return [String(id), pool.tokenMeta];
            if (!pool?.tokenURI) return [String(id), null];
            const res = await fetch(ipfsToHttp(pool.tokenURI));
            const json = await res.json();
            return [String(id), json && typeof json === "object" ? json : null];
          } catch {
            return [String(id), null];
          }
        })
      );
      if (!cancelled) setPoolListTokenMeta(Object.fromEntries(entries));
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [route.mode, poolList]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (route.mode !== "profile") {
        setProfileRows([]);
        setProfileBoughtMap({});
        setProfileLoading(false);
        setProfileHydrated(false);
        return;
      }
      if (!wallet.address || !hasLaunchpadWalletSession) {
        setProfileRows([]);
        setProfileBoughtMap({});
        setProfileLoading(false);
        setProfileHydrated(false);
        profileLoadKeyRef.current = "";
        return;
      }

      const loadKey = `${String(wallet.address || "").toLowerCase()}:profile`;
      if (profileLoadKeyRef.current !== loadKey) {
        setProfileHydrated(false);
        setProfilePage(1);
        profileLoadKeyRef.current = loadKey;
      }
      setProfileLoading(true);
      try {
        if (!LAUNCHPAD_BACKEND_URL) throw new Error("Launchpad backend URL not configured");
        const profileRows = await fetchPublicLaunchpadProfileViaBackend(wallet.address);
        const nextRows = profileRows.map((pool) => ({ id: Number(pool.id), pool }));
        const nextBoughtMap = Object.fromEntries(
          profileRows.map((pool) => [
            String(pool.id),
            BigInt(pool?.contributedWei?.toString?.() ?? pool?.contributedWei ?? 0n)
          ])
        );

        if (!cancelled) {
          setProfileRows(nextRows);
          setProfileBoughtMap(nextBoughtMap);
        }
      } catch {
        if (!cancelled) {
          setProfileRows([]);
          setProfileBoughtMap({});
        }
      } finally {
        if (!cancelled) {
          setProfileLoading(false);
          setProfileHydrated(true);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [route.mode, wallet.address, hasLaunchpadWalletSession]);

  const shouldShowProfileLoading = route.mode === "profile" && hasLaunchpadWalletSession && Boolean(wallet.address) && !profileHydrated;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (route.mode !== "create") return;
      try {
        const quote = await fetchLaunchpadPlatformFeeQuote();
        if (!cancelled) setLaunchpadPlatformFeeQuote(quote);
      } catch {
        if (!cancelled) setLaunchpadPlatformFeeQuote(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [route.mode]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (route.mode !== "detail") {
        setDetailTokenMeta(null);
        return;
      }
      if (detailPool?.tokenMeta && typeof detailPool.tokenMeta === "object") {
        setDetailTokenMeta(detailPool.tokenMeta);
        return;
      }
      if (!detailPool?.tokenURI) {
        setDetailTokenMeta(null);
        return;
      }
      try {
        const res = await fetch(ipfsToHttp(detailPool.tokenURI));
        const json = await res.json();
        if (!cancelled) setDetailTokenMeta(json && typeof json === "object" ? json : null);
      } catch {
        if (!cancelled) setDetailTokenMeta(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [route.mode, detailPool?.tokenURI]);

  useEffect(() => {
    poolListRef.current = poolList;
  }, [poolList]);

  useEffect(() => {
    poolActivityMapRef.current = poolActivityMap;
  }, [poolActivityMap]);

  useEffect(() => {
    launchpadActivityFeedRef.current = launchpadActivityFeed;
  }, [launchpadActivityFeed]);

  useEffect(() => {
    lastWriteProviderRef.current = null;
  }, [wallet.address, walletConnectorId, walletConnectorName, isPhantomConnector]);

  useEffect(() => {
    let rafId = 0;
    let navObserver = null;

    const applyNavOffset = () => {
      rafId = 0;
      const root = document.getElementById("bob-launchpad-page");
      const nav = document.querySelector("nav");
      if (!root || !(nav instanceof HTMLElement)) return;
      const navHeight = Math.ceil(nav.getBoundingClientRect().height);
      root.style.setProperty("--lp-nav-offset", `${navHeight}px`);
    };

    const onMeasure = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(applyNavOffset);
    };

    applyNavOffset();
    window.addEventListener("resize", onMeasure);
    window.addEventListener("scroll", onMeasure, { passive: true });
    window.addEventListener("load", onMeasure);
    const nav = document.querySelector("nav");
    if (nav && typeof ResizeObserver !== "undefined") {
      navObserver = new ResizeObserver(onMeasure);
      navObserver.observe(nav);
    }

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onMeasure);
      window.removeEventListener("scroll", onMeasure);
      window.removeEventListener("load", onMeasure);
      if (navObserver) navObserver.disconnect();
    };
  }, []);

  const runTx = useCallback(
    async (label, fn) => {
      if (!hasLaunchpadWalletSession) {
        openConnectModal?.();
        return false;
      }
      if (isWrongNetwork) {
        setStatus("Wrong network. Switch to Monad Mainnet.");
        return false;
      }
      setLoading(true);
      try {
        setStatus(`${label}: waiting for wallet...`);
        const tx = await fn();
        setStatus(`${label}: pending ${tx.hash}`);
        try {
          await tx.wait();
        } catch {
          // Some wallet/delegation flows return a tx object that cannot be awaited
          // via tx.wait() due to malformed/omitted nonce fields. Fall back to RPC hash wait.
          const readProvider = getReadProvider();
          await readProvider.waitForTransaction(tx.hash);
        }
        setStatus(`${label}: confirmed`, "success");
        await refreshGlobal();
        if (route.mode === "list") await refreshPoolList();
        if (route.mode === "detail") await refreshDetailPool();
        return true;
        } catch (e) {
          const errText = String(extractLaunchpadErrorText(e));
          if (/insufficient balance/i.test(errText)) {
            setStatus(`${label}: Insufficient MON balance in the connected wallet.`);
          } else {
            setStatus(`${label}: ${errText}`);
          }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [hasLaunchpadWalletSession, isWrongNetwork, openConnectModal, refreshGlobal, route.mode, refreshPoolList, refreshDetailPool, getReadProvider, setStatus]
  );

  const onPickImage = async (file) => {
    setSelectedImage(null);
    setImageInfo("");
    setImagePreview("");
    if (!file) return;
    const dims = await validateSquareImage(file);
    setSelectedImage(file);
    setImageInfo(`${dims.width}x${dims.height}, ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    setImagePreview(URL.createObjectURL(file));
  };

  const syncWhitelistActiveLine = useCallback((el) => {
    if (!el) return;
    const value = el.value || "";
    const caret = Number.isFinite(el.selectionStart) ? el.selectionStart : 0;
    let line = 1;
    for (let i = 0; i < caret && i < value.length; i += 1) {
      if (value.charCodeAt(i) === 10) line += 1;
    }
    setWhitelistActiveLine(line);
  }, [setStatus]);

  const syncWhitelistScroll = useCallback((el) => {
    if (!el) return;
    const top = Number(el.scrollTop || 0);
    setWhitelistScrollTop(top);
    if (whitelistGutterRef.current) whitelistGutterRef.current.style.transform = `translateY(${-top}px)`;
  }, [setStatus]);

  const handleWhitelistFileUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!/(\.csv|\.txt)$/i.test(file.name)) {
      setStatus("Whitelist file must be CSV or TXT.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setStatus("Whitelist file too large (max 2MB).");
      return;
    }
    try {
      const text = await file.text();
      const normalized = text.replace(/\r\n?/g, "\n");
      setCreateForm((s) => ({ ...s, whitelistAddresses: normalized }));
      setWhitelistFileName(file.name);
      setWhitelistActiveLine(1);
      setWhitelistScrollTop(0);
      if (whitelistGutterRef.current) whitelistGutterRef.current.style.transform = "translateY(0px)";
      setStatus(`Loaded whitelist file: ${file.name}`);
    } catch {
      setStatus("Failed to read whitelist file.");
    }
  }, []);

  const handleWhitelistShowExample = useCallback(() => {
    const sample = [
      "0x1201512d6082a4c587628491c7b72b463bcbc1d6",
      "0xc1b78548b0fc3d7bc94abee1afdbe67899aeb995",
      "0x0077c2093fd8e6a6277b84c2627619b80a72472b"
    ].join("\n");
    setCreateForm((s) => ({ ...s, whitelistAddresses: sample }));
    setWhitelistFileName("");
  }, []);

  const handleWhitelistResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    const textareaEl = whitelistTextareaRef.current;
    if (!textareaEl) return;
    const startY = e.clientY;
    const startHeight = textareaEl.getBoundingClientRect().height;
    const minHeight = 140;
    const maxHeight = 520;

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientY - startY;
      const next = Math.max(minHeight, Math.min(maxHeight, Math.round(startHeight + delta)));
      setWhitelistTextareaHeight(next);
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  useEffect(() => {
    const el = whitelistTextareaRef.current;
    if (!el) return;
    const styles = window.getComputedStyle(el);
    const lh = parseFloat(styles.lineHeight || "20");
    if (Number.isFinite(lh) && lh > 0) setWhitelistLineHeightPx(lh);
  }, [createForm.whitelistEnabled]);

  useEffect(() => {
    const onDocPointer = (e) => {
      const t = e.target;
      if (t instanceof Element && t.closest(".lp-custom-select")) return;
      if (t instanceof Element && t.closest(".lp-datetime-field")) return;
      if (t instanceof Element && t.closest(".lp-search-widget")) return;
      if (t instanceof Element && t.closest(".lp-search-panel-shell")) return;
      setOpenSelectKey("");
      setOpenDateTimeKey("");
      setOpenDateTimeSelectKey("");
      setIsSearchOpen(false);
    };
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("touchstart", onDocPointer, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("touchstart", onDocPointer);
    };
  }, []);

  useEffect(() => {
    const el = whitelistTextareaRef.current;
    if (!el) return;
    syncWhitelistActiveLine(el);
    syncWhitelistScroll(el);
  }, [createForm.whitelistAddresses, syncWhitelistActiveLine, syncWhitelistScroll]);

  useEffect(() => {
    if (!isSearchOpen) return;
    const timeoutId = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [isSearchOpen]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const body = document.body;
    if (isSearchOpen) body.classList.add("lp-search-open-global");
    else body.classList.remove("lp-search-open-global");
    return () => body.classList.remove("lp-search-open-global");
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isSearchOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsSearchOpen(false);
        return;
      }
      if (event.key === "Enter" && trimmedSearchQuery) {
        event.preventDefault();
        setSearchPending(false);
        setDebouncedSearchQuery(trimmedSearchQuery);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isSearchOpen, trimmedSearchQuery]);

  useEffect(() => {
    if (!isSearchOpen) return undefined;
    if (!trimmedSearchQuery) {
      setDebouncedSearchQuery("");
      setSearchPending(false);
      setSearchLoading(false);
      setSearchResults([]);
      setSearchTotalCount(0);
      setSearchShowAllResults(false);
      return undefined;
    }
    setSearchPending(true);
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(trimmedSearchQuery);
    }, LAUNCHPAD_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isSearchOpen, trimmedSearchQuery]);

  useEffect(() => {
    if (!isSearchOpen || !debouncedSearchQuery) return undefined;
    let cancelled = false;
    setSearchLoading(true);
    searchPublicPresalesViaBackend(debouncedSearchQuery, {
      limit: searchShowAllResults ? LAUNCHPAD_SEARCH_RESULT_LIMIT_EXPANDED : LAUNCHPAD_SEARCH_RESULT_LIMIT
    })
      .then(({ presales, count }) => {
        if (cancelled) return;
        setSearchResults(presales.map((pool) => ({ id: Number(pool.id), pool })));
        setSearchTotalCount(count);
        setSearchPending(false);
        setRecentSearches((prev) => {
          const next = [
            debouncedSearchQuery,
            ...prev.filter((item) => item.toLowerCase() !== debouncedSearchQuery.toLowerCase())
          ].slice(0, 6);
          writeRecentLaunchpadSearches(next);
          return next;
        });
      })
      .catch(() => {
        if (cancelled) return;
        setSearchResults([]);
        setSearchTotalCount(0);
        setSearchPending(false);
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSearchOpen, debouncedSearchQuery, searchShowAllResults]);

  useEffect(() => {
    const startsInSec = String(Math.max(0, Math.floor((scheduleStartAt.getTime() - Date.now()) / 1000)));
    const durationSec = String(Math.max(0, Math.floor((scheduleEndAt.getTime() - scheduleStartAt.getTime()) / 1000)));
    setCreateForm((s) => {
      if (s.startsInSec === startsInSec && s.durationSec === durationSec) return s;
      return {
        ...s,
        startsInSec,
        durationSec
      };
    });
  }, [scheduleStartAt, scheduleEndAt]);

  const updateScheduleCalendarDay = useCallback((key, dayDate) => {
    const target = new Date(dayDate);
    target.setSeconds(0, 0);
    if (key === "start") {
      target.setHours(scheduleStartAt.getHours(), scheduleStartAt.getMinutes(), 0, 0);
      setScheduleStartAt(target);
      setScheduleStartViewMonth(startOfMonth(target));
      if (!scheduleEndManuallyEdited) {
        const nextEnd = buildDefaultScheduleEnd(target);
        setScheduleEndAt(nextEnd);
        setScheduleEndViewMonth(startOfMonth(nextEnd));
      } else if (scheduleEndAt.getTime() <= target.getTime()) {
        const nextEnd = new Date(target);
        nextEnd.setMinutes(nextEnd.getMinutes() + 1);
        setScheduleEndAt(nextEnd);
        setScheduleEndViewMonth(startOfMonth(nextEnd));
      }
      return;
    }
    setScheduleEndManuallyEdited(true);
    target.setHours(scheduleEndAt.getHours(), scheduleEndAt.getMinutes(), 0, 0);
    if (target.getTime() <= scheduleStartAt.getTime()) {
      const clamped = new Date(scheduleStartAt);
      clamped.setMinutes(clamped.getMinutes() + 1);
      setScheduleEndAt(clamped);
      setScheduleEndViewMonth(startOfMonth(clamped));
      return;
    }
    setScheduleEndAt(target);
    setScheduleEndViewMonth(startOfMonth(target));
  }, [scheduleEndAt, scheduleEndManuallyEdited, scheduleStartAt]);

  const updateScheduleTimeSelect = useCallback((key, part, nextValue) => {
    const currentDate = key === "start" ? scheduleStartAt : scheduleEndAt;
    const nextHour = part === "hour" ? nextValue : getHour12Value(currentDate);
    const nextMinute = part === "minute" ? nextValue : getMinuteValue(currentDate);
    const nextMeridiem = part === "meridiem" ? nextValue : getMeridiemValue(currentDate);
    const nextDate = applyScheduleTimeParts(currentDate, nextHour, nextMinute, nextMeridiem);
    if (key === "start") {
      setScheduleStartAt(nextDate);
      if (!scheduleEndManuallyEdited) {
        const nextEnd = buildDefaultScheduleEnd(nextDate);
        setScheduleEndAt(nextEnd);
        setScheduleEndViewMonth(startOfMonth(nextEnd));
      } else if (scheduleEndAt.getTime() <= nextDate.getTime()) {
        const nextEnd = new Date(nextDate);
        nextEnd.setMinutes(nextEnd.getMinutes() + 1);
        setScheduleEndAt(nextEnd);
        setScheduleEndViewMonth(startOfMonth(nextEnd));
      }
      return;
    }
    setScheduleEndManuallyEdited(true);
    if (nextDate.getTime() <= scheduleStartAt.getTime()) {
      const clamped = new Date(scheduleStartAt);
      clamped.setMinutes(clamped.getMinutes() + 1);
      setScheduleEndAt(clamped);
      setScheduleEndViewMonth(startOfMonth(clamped));
      return;
    }
    setScheduleEndAt(nextDate);
    setScheduleEndViewMonth(startOfMonth(nextDate));
  }, [scheduleEndAt, scheduleEndManuallyEdited, scheduleStartAt]);

  const renderScheduleField = useCallback((key, label, value) => {
    const isOpen = openDateTimeKey === key;
    const viewMonth = key === "start" ? scheduleStartViewMonth : scheduleEndViewMonth;
    const calendarDays = buildCalendarDays(viewMonth);
    const selectedDateValue = toDateInputValue(value);
    const hourSelectKey = `${key}-hour`;
    const minuteSelectKey = `${key}-minute`;
    const meridiemSelectKey = `${key}-meridiem`;

    const renderTimeSelect = (selectKey, selectLabel, currentValue, options, part) => {
      const isSelectOpen = openDateTimeSelectKey === selectKey;
      return (
        <div className={`lp-datetime-control lp-datetime-select ${isSelectOpen ? "is-open" : ""}`}>
          <span>{selectLabel}</span>
          <button
            type="button"
            className={`lp-datetime-select-trigger ${isSelectOpen ? "is-open" : ""}`}
            onClick={() => setOpenDateTimeSelectKey((current) => (current === selectKey ? "" : selectKey))}
            aria-haspopup="listbox"
            aria-expanded={isSelectOpen}
          >
            <span>{currentValue}</span>
            <svg className="lp-datetime-select-chevron" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M4.25 6.25 8 10l3.75-3.75" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className={`lp-datetime-select-menu ${isSelectOpen ? "is-open" : ""}`} role="listbox" aria-label={selectLabel}>
            {options.map((option) => (
              <button
                key={`${selectKey}-${option}`}
                type="button"
                className={`lp-datetime-select-option ${currentValue === option ? "is-active" : ""}`}
                onClick={() => {
                  updateScheduleTimeSelect(key, part, option);
                  setOpenDateTimeSelectKey("");
                }}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      );
    };
    return (
      <label className={`lp-field lp-datetime-field ${isOpen ? "is-open" : ""}`}>
        <span>{label}</span>
        <div className={`lp-datetime-shell ${isOpen ? "is-open" : ""}`}>
          <button
            type="button"
            className="lp-datetime-trigger"
            onClick={() => {
              setOpenDateTimeSelectKey("");
              setOpenDateTimeKey((current) => (current === key ? "" : key));
            }}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
          >
            <span className="lp-datetime-display">{formatScheduleDisplay(value)}</span>
            <span className="lp-datetime-icon" aria-hidden="true">
              <img src="/icons/calendar.svg" alt="" />
            </span>
          </button>
          {isOpen ? (
            <div className="lp-datetime-popover" role="dialog" aria-label={`${label} picker`}>
              <div className="lp-datetime-popover-head">{label}</div>
              <div className="lp-datetime-time-row">
                {renderTimeSelect(hourSelectKey, "Hour", getHour12Value(value), HOUR_12_OPTIONS, "hour")}
                {renderTimeSelect(minuteSelectKey, "Minute", getMinuteValue(value), MINUTE_OPTIONS, "minute")}
                {renderTimeSelect(meridiemSelectKey, "Meridiem", getMeridiemValue(value), MERIDIEM_OPTIONS, "meridiem")}
              </div>
              <div className="lp-datetime-calendar">
                <div className="lp-datetime-calendar-head">
                  <button
                    type="button"
                    className="lp-datetime-nav"
                    onClick={() => {
                      if (key === "start") setScheduleStartViewMonth((current) => shiftMonth(current, -1));
                      else setScheduleEndViewMonth((current) => shiftMonth(current, -1));
                    }}
                    aria-label={`Previous month for ${label}`}
                  >
                    ‹
                  </button>
                  <strong>{formatMonthYearLabel(viewMonth)}</strong>
                  <button
                    type="button"
                    className="lp-datetime-nav"
                    onClick={() => {
                      if (key === "start") setScheduleStartViewMonth((current) => shiftMonth(current, 1));
                      else setScheduleEndViewMonth((current) => shiftMonth(current, 1));
                    }}
                    aria-label={`Next month for ${label}`}
                  >
                    ›
                  </button>
                </div>
                <div className="lp-datetime-weekdays">
                  {WEEKDAY_LABELS.map((weekday) => (
                    <span key={`${key}-weekday-${weekday}`}>{weekday}</span>
                  ))}
                </div>
                <div className="lp-datetime-days">
                  {calendarDays.map((day) => {
                    const isSelected = toDateInputValue(day.date) === selectedDateValue;
                    const isToday = toDateInputValue(day.date) === toDateInputValue(new Date());
                    return (
                      <button
                        key={`${key}-${day.key}`}
                        type="button"
                        className={`lp-datetime-day${day.inMonth ? "" : " is-outside"}${isSelected ? " is-selected" : ""}${isToday ? " is-today" : ""}`}
                        onClick={() => updateScheduleCalendarDay(key, day.date)}
                      >
                        {day.date.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                className="lp-datetime-close"
                onClick={() => {
                  setOpenDateTimeSelectKey("");
                  setOpenDateTimeKey("");
                }}
              >
                Done
              </button>
            </div>
          ) : null}
        </div>
        <p className="lp-field-inline-error">&nbsp;</p>
      </label>
    );
  }, [openDateTimeKey, openDateTimeSelectKey, scheduleEndViewMonth, scheduleStartViewMonth, updateScheduleCalendarDay, updateScheduleTimeSelect]);

  const onCreatePool = async () => {
    if (loading || createBusy) return;
    setCreateBusy(true);
    try {
      const resolvedCreator = String(wallet.address || "").trim();
      if (!resolvedCreator) throw new Error("Connect wallet first");
      let tokenURI = String(createForm.tokenURI || "").trim();
      let salt = String(createForm.salt || "").trim();
      const burnPct = createForm.burnEnabled ? Number(createForm.burnPct || 0) : 0;
      const lpPct = createForm.lpEnabled ? Number(createForm.lpPct || 0) : 0;
      const creatorPct = createForm.creatorEnabled ? Number(createForm.creatorPct || 0) : 0;
      const giftPct = createForm.giftEnabled ? Number(createForm.giftPct || 0) : 0;
      const dividendPct = createForm.dividendEnabled ? Number(createForm.dividendPct || 0) : 0;
      const splitTotal = burnPct + lpPct + creatorPct + giftPct + dividendPct;
      const giftXHandle = String(createForm.giftXHandle || "").trim().replace(/^@+/, "");
      if (splitTotal !== 100) throw new Error("Fee split must total 100%");
      if (giftPct > 0 && !giftXHandle) throw new Error("Gift X handle is required when Gift > 0%");
      const whitelistMode = createForm.whitelistEnabled ? 2 : 0;
      const whitelistAddresses = normalizeAddressList(createForm.whitelistAddresses || "");
      const poolIdToCreate = BigInt(nextPoolId || "0") + 1n;
      const softCapWei = parseEther(createForm.softCap || "0");
      const hardCapRaw = String(createForm.hardCap || "").trim();
      const minContributionRaw = String(createForm.minContribution || "").trim();
      const maxContributionRaw = String(createForm.maxContribution || "").trim();
      const tokenName = String(createForm.name || "").trim();
      const tokenSymbol = String(createForm.symbol || "").trim();
      const scheduleStartTs = Math.floor(scheduleStartAt.getTime() / 1000);
      const scheduleEndTs = Math.floor(scheduleEndAt.getTime() / 1000);
      if (!selectedImage && !salt) throw new Error("Image required");
      if (!tokenName) throw new Error("Token name required");
      if (!tokenSymbol) throw new Error("Token symbol required");
      if (scheduleEndTs <= scheduleStartTs) throw new Error("End time must be after start time");
      if (!hardCapRaw) throw new Error("Hard cap required");
      if (createAmountFieldErrors.hardCap) throw new Error(createAmountFieldErrors.hardCap);
      if (!minContributionRaw) throw new Error("Min contribution required");
      if (createAmountFieldErrors.minContribution) throw new Error(createAmountFieldErrors.minContribution);
      if (!maxContributionRaw) throw new Error("Max contribution required");
      if (createAmountFieldErrors.maxContribution) throw new Error(createAmountFieldErrors.maxContribution);
      let predictedAddress = String(createForm.predictedAddress || "").trim();
      let whitelistRoot = ethers.ZeroHash;
      if (whitelistMode === 2) {
        if (!whitelistAddresses.length) throw new Error("Whitelist addresses required");
        whitelistRoot = buildMerkleRootFromAddresses(whitelistAddresses);
        if (whitelistRoot === ethers.ZeroHash) throw new Error("Invalid whitelist Merkle root");
      }
      const quotedCapWei = BigInt(
        launchpadPlatformFeeQuote?.maxSafeBuyWei?.toString?.() ??
        launchpadPlatformFeeQuote?.maxSafeBuyWei ??
        0n
      );
      const capWei = quotedCapWei > 0n ? quotedCapWei : BigInt(maxInitialBuyWei || "0");
      const plannedRaisedWei = parseEther(createForm.hardCap || "0");
      const devBuyWei = parseEther(createForm.devBuy || "0");
      const plannedLaunchWei = plannedRaisedWei + devBuyWei;
      if (plannedRaisedWei <= 0n) throw new Error("Hard cap must be greater than 0");
      if (softCapWei > plannedRaisedWei) throw new Error("Soft cap cannot exceed hard cap");
      if (capWei > 0n && capWei !== ethers.MaxUint256 && plannedLaunchWei > capWei) {
        const remainingPresaleWei = devBuyWei >= capWei ? 0n : capWei - devBuyWei;
        throw new Error(`Hard cap exceeds the current launch-safe max (${fmtMon(formatEther(remainingPresaleWei), 4)} MON)`); 
      }
      if (selectedImage) {
        setStatus("Create pool: creating presale...");
        const prepared = await prepareNadTokenViaBackend({
          file: selectedImage,
          creator: resolvedCreator,
          name: tokenName,
          symbol: tokenSymbol,
          description: createForm.description,
          website: createForm.website,
          twitter: createForm.twitter,
          telegram: createForm.telegram
        });
        tokenURI = prepared.metadata_uri;
        salt = prepared.salt;
        setCreateForm((s) => ({
          ...s,
          tokenURI: prepared.metadata_uri,
          salt: prepared.salt,
          predictedAddress: prepared.predicted_address || ""
        }));
        predictedAddress = String(prepared.predicted_address || "").trim();
      }

      if (!salt) {
        throw new Error("Image required");
      }

      const { tokens: dividendRewardTokens, ratiosBps: dividendRewardRatios } =
        dividendPct > 0
          ? resolveDividendRewardsForCreate(
              createForm.dividendRewards,
              String(routerCfg?.quoteToken || DEFAULT_NAD_QUOTE_TOKEN).trim(),
              predictedAddress
            )
          : { tokens: [], ratiosBps: [] };

      const currentNonce = await getReadContract().userNextNonce(resolvedCreator).catch(() => 0n);
      const liveFeeQuote = await fetchLaunchpadPlatformFeeQuote({
        user: resolvedCreator,
        nonce: BigInt(currentNonce?.toString?.() ?? currentNonce ?? 0n).toString()
      });
      const platformFeeWei = BigInt(liveFeeQuote?.feeWei?.toString?.() ?? liveFeeQuote?.feeWei ?? 0n);
      const platformFeeDeadline = Number(liveFeeQuote?.deadline || 0);
      const platformFeeSignature = String(liveFeeQuote?.signature || "");
      const maxSafeBuyWei = BigInt(liveFeeQuote?.maxSafeBuyWei?.toString?.() ?? liveFeeQuote?.maxSafeBuyWei ?? 0n);
      const capQuoteDeadline = Number(liveFeeQuote?.capDeadline || 0);
      const capQuoteSignature = String(liveFeeQuote?.capSignature || "");
      const deployFeeWei = parseEther(String(createForm.deployFee || "0"));
      const estimatedTxFeeWei = parseEther(String(summaryEstimatedTxFeeMon || 0));
      if (maxSafeBuyWei <= 0n) throw new Error("Launch cap quote unavailable");
      if ((plannedRaisedWei + devBuyWei) > maxSafeBuyWei) {
        const remainingPresaleWei = devBuyWei >= maxSafeBuyWei ? 0n : maxSafeBuyWei - devBuyWei;
        throw new Error(`Hard cap exceeds the current launch-safe max (${fmtMon(formatEther(remainingPresaleWei), 4)} MON)`);
      }
      const estimatedRequiredWei = devBuyWei + platformFeeWei + deployFeeWei + estimatedTxFeeWei;
      if (createNativeBalanceRaw > 0n && createNativeBalanceRaw < estimatedRequiredWei) {
        throw new Error(
          `Insufficient MON balance. Need about ${fmtMon(formatEther(estimatedRequiredWei), 4)} MON total including deploy fee and gas.`
        );
      }

      const createOk = await runTx("Create pool", async () => {
        const startTime = scheduleStartTs;
        const endTime = scheduleEndTs;
        return writeLaunchpadContract({
          functionName: "createPool",
          args: [
            resolvedCreator,
            createForm.name,
            createForm.symbol,
            tokenURI,
            createForm.description || "",
            createForm.website || "",
            createForm.twitter || "",
            createForm.telegram || "",
            salt,
            softCapWei.toString(),
            parseEther(createForm.hardCap || "0").toString(),
            parseEther(createForm.minContribution || "0").toString(),
            parseEther(createForm.maxContribution || "0").toString(),
            devBuyWei.toString(),
            parseEther(createForm.deployFee || "0").toString(),
            platformFeeWei.toString(),
            platformFeeDeadline,
            platformFeeSignature,
            maxSafeBuyWei.toString(),
            capQuoteDeadline,
            capQuoteSignature,
            startTime,
            endTime,
            burnPct * 100,
            lpPct * 100,
            creatorPct * 100,
            giftPct * 100,
            dividendPct * 100,
            giftXHandle,
            dividendRewardTokens,
            dividendRewardRatios,
            whitelistMode,
            whitelistRoot
          ],
          value: devBuyWei + platformFeeWei
        });
      });

      if (!createOk) return;
      resetCreateFormState();

      if (whitelistMode === 2) {
        setStatus("Create pool: syncing whitelist proofs...");
        await registerWhitelistViaBackend({
          poolId: poolIdToCreate.toString(),
          contractAddress,
          whitelistRoot,
          addresses: whitelistAddresses
        });
        setStatus("Create pool: whitelist synced", "success", {
          href: buildLaunchpadDetailPath(poolIdToCreate.toString(), contractAddress),
          label: "View Presale"
        });
      } else {
        setStatus("Create pool: confirmed", "success", {
          href: buildLaunchpadDetailPath(poolIdToCreate.toString(), contractAddress),
          label: "View Presale"
        });
      }
    } finally {
      setCreateBusy(false);
    }
  };

  const onDeposit = async () => {
    if (!detailPool) return;
    await runTx("Deposit", async () => {
      const allocationWei = parseEther(String(depositAmountMon || "0"));
      if (allocationWei <= 0n) throw new Error("Enter an amount greater than 0");
      if (detailMaxJoinWei <= 0n) throw new Error("No allocation remaining");
      if (allocationWei > detailMaxJoinWei) {
        throw new Error(`Amount exceeds your available max (${fmtMon(formatEther(detailMaxJoinWei), 4)} MON)`);
      }
      const { totalCostWei } = quoteParticipationCost(allocationWei, detailParticipationFeeBps);
      const minContributionWei = BigInt(detailPool.minContributionWei?.toString?.() ?? detailPool.minContributionWei ?? 0n);
      const maxContributionWei = BigInt(detailPool.maxContributionWei?.toString?.() ?? detailPool.maxContributionWei ?? 0n);
      const nextNetContributionWei = detailContributionWeiRaw + allocationWei;
      if (nextNetContributionWei < minContributionWei) {
        throw new Error(`Amount is below the minimum (${fmtMon(formatEther(minContributionWei), 4)} MON)`);
      }
      if (nextNetContributionWei > maxContributionWei) {
        throw new Error(`Amount exceeds your available max (${fmtMon(formatEther(detailMaxJoinWei), 4)} MON)`);
      }
      if (allocationWei > detailMaxJoinNetWei) {
        throw new Error(`Amount exceeds the remaining hard cap room (${fmtMon(formatEther(detailMaxJoinWei), 4)} MON)`);
      }
      const whitelistMode = Number(safeResultField(detailPool, "whitelistMode", 0));
      let proof = [];
      if (whitelistMode === 2) {
        if (!wallet.address) throw new Error("Connect wallet");
        setStatus("Deposit: checking whitelist...");
        const proofRes = await fetchWhitelistProofViaBackend({
          poolId: route.poolId,
          contractAddress: getPoolContractAddress(detailPool),
          wallet: wallet.address
        });
        proof = Array.isArray(proofRes.proof) ? proofRes.proof : [];
        if (!proof.length) throw new Error("Wallet is not eligible for this whitelist");
      }
      return writeLaunchpadContract({
        targetAddress: getPoolContractAddress(detailPool),
        functionName: "deposit",
        args: [BigInt(route.poolId), allocationWei, proof],
        value: totalCostWei
      });
    });
  };

  const onFinalize = async () => {
    if (!hasLaunchpadWalletSession) {
      openConnectModal?.();
      return;
    }
    if (isWrongNetwork) {
      setStatus("Wrong network. Switch to Monad Mainnet.");
      return;
    }
    setLoading(true);
    try {
      setStatus("Finalize launch: waiting for wallet...");
      const deployFeeWei = BigInt(detailPool?.deployFeeWei?.toString?.() ?? detailPool?.deployFeeWei ?? 0n);
      const liveQuote = await fetchLaunchpadPlatformFeeQuote({
        user: String(wallet.address || "").trim(),
        nonce: "0"
      });
      const maxSafeBuyWei = BigInt(liveQuote?.maxSafeBuyWei?.toString?.() ?? liveQuote?.maxSafeBuyWei ?? 0n);
      const capQuoteDeadline = Number(liveQuote?.capDeadline || 0);
      const capQuoteSignature = String(liveQuote?.capSignature || "");
      if (maxSafeBuyWei <= 0n) throw new Error("Launch cap quote unavailable");
      const tx = await writeLaunchpadContract({
        targetAddress: getPoolContractAddress(detailPool),
        functionName: "finalizeAndLaunch",
        args: [
          BigInt(route.poolId),
          maxSafeBuyWei.toString(),
          capQuoteDeadline,
          capQuoteSignature
        ],
        value: deployFeeWei
      });
      setStatus(`Finalize launch: pending ${tx.hash}`);

      let receipt = null;
      try {
        receipt = await tx.wait();
      } catch {
        const readProvider = getReadProvider();
        receipt = await readProvider.waitForTransaction(tx.hash);
      }

      let finalizedToken = "";
      let purchased = "";
      try {
        const iface = new ethers.Interface(launchpadAbi);
        const parsedLogs = (receipt?.logs || [])
          .map((log) => {
            try {
              return iface.parseLog(log);
            } catch {
              return null;
            }
          })
          .filter(Boolean);
        const evt = parsedLogs.find((x) => x?.name === "PoolFinalized");
        if (evt?.args) {
          finalizedToken = String(evt.args.launchedToken || "");
          purchased = String(evt.args.tokensPurchased || "");
        }
      } catch {}

      if (finalizedToken) {
        setStatus(`Finalize launch: confirmed | token ${finalizedToken} | purchased ${purchased || "0"}`);
      } else {
        setStatus("Finalize launch: confirmed");
      }

      await refreshGlobal();
      if (route.mode === "list") await refreshPoolList();
      if (route.mode === "detail") await refreshDetailPool();
    } catch (e) {
      setStatus(`Finalize launch: ${e?.shortMessage || e?.message || "Failed"}`);
    } finally {
      setLoading(false);
    }
  };
  const onClaim = async () => {
    await runTx("Claim", async () => {
      return writeLaunchpadContract({
        targetAddress: getPoolContractAddress(detailPool),
        functionName: "claim",
        args: [BigInt(route.poolId)]
      });
    });
  };
  const onRefund = async () => {
    await runTx("Refund", async () => {
      return writeLaunchpadContract({
        targetAddress: getPoolContractAddress(detailPool),
        functionName: "refund",
        args: [BigInt(route.poolId)]
      });
    });
  };
  const onClaimDevBuy = async () => {
    await runTx("Claim dev buy", async () => {
      return writeLaunchpadContract({
        targetAddress: getPoolContractAddress(detailPool),
        functionName: "claimDevBuy",
        args: [BigInt(route.poolId)]
      });
    });
  };
  const onRefundDevBuy = async () => {
    await runTx("Refund dev buy", async () => {
      return writeLaunchpadContract({
        targetAddress: getPoolContractAddress(detailPool),
        functionName: "refundDevBuy",
        args: [BigInt(route.poolId)]
      });
    });
  };

  const applyDevCreateSample = useCallback((variant = "default") => {
    const startAt = new Date();
    startAt.setSeconds(0, 0);
    startAt.setMinutes(startAt.getMinutes() + 30);
    const endAt = new Date(startAt);
    endAt.setDate(endAt.getDate() + 9);
    endAt.setHours(endAt.getHours() + 13);
    endAt.setMinutes(endAt.getMinutes() + 15);
    const isLong = variant === "long";
    const isHuge = variant === "huge";
    const isWhitelist = variant === "whitelist";
    const isLinks = variant === "links";

    setCreateForm((s) => ({
      ...s,
      name: isHuge
        ? "Launchpad Maximum Stress Preview Token With Very Long Naming"
        : isLong
          ? "Super Long Placeholder Bob Launch Token"
          : isWhitelist
            ? "Whitelist Access Preview"
            : "Bob Launch Token",
      symbol: isHuge ? "MAXSTRESS" : isLong ? "BLAUNCH" : isWhitelist ? "WLPREV" : "BLT",
      description: isHuge
        ? "Stress-test preset for the create page with large values, all fee toggles, longer text, and social formatting that helps catch alignment, summary, and wrapping issues before final launchpad polish."
        : isLong
          ? "Long placeholder token description for testing create-page responsive wrapping, grouped summary behavior, and overall card spacing at smaller breakpoints."
          : isWhitelist
            ? "Whitelist-focused preset for testing access copy, textarea behavior, and constrained contribution settings."
            : "Placeholder token used to preview create-page launchpad responsiveness.",
        website: isLinks ? "www.bobverse.xyz" : "https://bobverse.xyz",
        twitter: isLinks ? "x.com/bobverse" : "bobverse",
        telegram: isLinks ? "t.me/bobverseportal" : "bobverseportal",
        softCap: isHuge ? "125000" : isWhitelist ? "25000" : "85000",
        hardCap: isHuge ? "229591" : isWhitelist ? "100000" : "180000",
        minContribution: isHuge ? "250" : isWhitelist ? "50" : "250",
        maxContribution: isHuge ? "25000" : isWhitelist ? "500" : "5000",
        devBuy: isHuge ? "9999" : "0",
        whitelistEnabled: isWhitelist,
        whitelistAddresses: isWhitelist
          ? `${LAUNCHPAD_DEV_WALLET}\n0x03aF62A4fE44A41e5e6902Db8A4b24A56b6fd3fc\n0xdbF4D38E8B33de75b94D8235cB2b8E7199F9168F`
          : "",
        burnEnabled: isHuge,
        lpEnabled: isHuge,
        creatorEnabled: isHuge,
        giftEnabled: isHuge,
        dividendEnabled: isHuge,
        burnPct: isHuge ? "25" : "0",
        lpPct: isHuge ? "25" : "0",
        creatorPct: isHuge ? "20" : "0",
        giftPct: isHuge ? "15" : "0",
        dividendPct: isHuge ? "15" : "0",
        giftXHandle: isHuge ? "biggiftreceiver" : "",
        dividendRewards: isHuge
          ? [
              createDividendRewardRow({ tokenType: "quoteToken", ratioPct: "40" }),
              createDividendRewardRow({ tokenType: "thisToken", ratioPct: "35" }),
              createDividendRewardRow({ tokenType: "address", tokenAddress: DEFAULT_NAD_QUOTE_TOKEN, ratioPct: "25" })
            ]
          : [createDividendRewardRow()]
      }));
    setScheduleStartAt(startAt);
    setScheduleEndAt(endAt);
    setScheduleEndManuallyEdited(true);
    setScheduleStartViewMonth(startOfMonth(startAt));
    setScheduleEndViewMonth(startOfMonth(endAt));
  }, []);

  const detailPreviewScenario = useMemo(
    () => buildLaunchpadDetailPreviewConfig(devDetailPreviewMode),
    [devDetailPreviewMode]
  );

  const detailPreviewPool = useMemo(() => {
    if (!detailPool || !devDetailPreviewEnabled) return detailPool;
    return {
      ...detailPool,
      ...detailPreviewScenario.pool
    };
  }, [detailPool, devDetailPreviewEnabled, detailPreviewScenario]);

  const detailPreviewMeta = useMemo(() => {
    if (!devDetailPreviewEnabled) return detailTokenMeta;
    return {
      ...(detailTokenMeta || {}),
      ...detailPreviewScenario.meta
    };
  }, [detailTokenMeta, devDetailPreviewEnabled, detailPreviewScenario]);
  const detailRenderPool = detailPreviewPool;
  const detailRenderMeta = detailPreviewMeta;
  const detailRenderImageUri = detailRenderMeta?.image_uri || detailRenderMeta?.image || "";
  const detailRenderImageUrl = detailRenderImageUri ? ipfsToHttp(detailRenderImageUri) : "";
  const launchpadTopTab = useMemo(() => {
    if (route.mode === "create") return "create";
    if (route.mode === "profile") return "profile";
    if (route.mode === "documentation") return "documentation";
    if (route.mode === "list") return "list";
    return "";
  }, [route.mode]);

  const renderSearchResultRow = useCallback((row, keyPrefix = "search-result") => {
    const { id, pool } = row;
    const meta = pool?.tokenMeta && typeof pool.tokenMeta === "object"
      ? pool.tokenMeta
      : displayedPoolListTokenMeta[String(id)] || null;
    const imageUri = meta?.image_uri || meta?.image || "";
    const imageUrl = imageUri ? ipfsToHttp(imageUri) : (pool?.image || "");
    const rawDisplayState = pool?.displayState || computePresaleDisplayState(
      pool,
      liveNowTs,
      pool?.launchGracePeriod ?? launchGracePeriod
    );
    const displayState = presentPresaleDisplayState(
      rawDisplayState
    );
    const ageLabel = pool?.createdAt
      ? fmtRelativeAge(Math.max(0, liveNowTs - Number(pool.createdAt || 0)))
      : "-";
    const raisedLabel = `${fmtCompactMonLabel(formatEther(getPresaleRaisedWei(pool)))} MON`;
    return (
      <Link
        key={`${keyPrefix}-${id}`}
        to={buildLaunchpadDetailPath(id, pool?.contractAddress)}
        className="lp-search-row"
        onClick={() => {
          setIsSearchOpen(false);
          setSearchQuery("");
          setDebouncedSearchQuery("");
        }}
      >
        <span className="lp-search-row-media">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="lp-search-row-image" />
          ) : (
            <span className="lp-search-row-image lp-skeleton-block" aria-hidden="true" />
          )}
        </span>
        <span className="lp-search-row-main">
          <span className="lp-search-row-name" title={pool?.name || "Token Name"}>{pool?.name || "Token Name"}</span>
          <span className="lp-search-row-symbol" title={pool?.symbol || "SYMBOL"}>{pool?.symbol || "SYMBOL"}</span>
        </span>
        <span className="lp-search-row-meta">
          <span className="lp-search-row-stat">
            <span className="lp-search-row-stat-label">Status</span>
            <span className={`lp-search-row-status lp-state-${String(rawDisplayState || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}>{displayState}</span>
          </span>
          <span className="lp-search-row-stat">
            <span className="lp-search-row-stat-label">Raised</span>
            <span className="lp-search-row-meta-item" title={raisedLabel}>{raisedLabel}</span>
          </span>
          <span className="lp-search-row-age" title={ageLabel}>
            <svg className="lp-search-row-age-icon" aria-hidden="true" viewBox="0 0 12 12" fill="none">
              <path d="M6 6V5.25C6 3.317 4.433 1.75 2.5 1.75H1.75V2.5C1.75 4.433 3.317 6 5.25 6H6ZM6 6V10.25M9.5 3.25H10.25V4C10.25 5.933 8.683 7.5 6.75 7.5H6V6.75C6 4.817 7.567 3.25 9.5 3.25Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="lp-search-row-age-text">{ageLabel}</span>
          </span>
        </span>
      </Link>
    );
  }, [displayedPoolListTokenMeta, liveNowTs, launchGracePeriod]);

  const renderSearchSkeletonRow = useCallback((key) => (
    <div key={key} className="lp-search-row lp-search-row-skeleton" aria-hidden="true">
      <span className="lp-search-row-media">
        <span className="lp-search-row-image lp-skeleton-block" />
      </span>
      <span className="lp-search-row-main">
        <span className="lp-search-skeleton-line lp-skeleton-block" />
        <span className="lp-search-skeleton-line lp-search-skeleton-line-short lp-skeleton-block" />
      </span>
      <span className="lp-search-row-meta lp-search-row-meta-skeleton">
        <span className="lp-search-row-stat lp-search-row-stat-skeleton">
          <span className="lp-search-skeleton-stack lp-search-skeleton-stack-status lp-skeleton-block" />
        </span>
        <span className="lp-search-row-stat lp-search-row-stat-skeleton">
          <span className="lp-search-skeleton-stack lp-search-skeleton-stack-raised lp-skeleton-block" />
        </span>
        <span className="lp-search-row-age lp-search-row-age-skeleton lp-skeleton-block" />
      </span>
    </div>
  ), []);

  const handleViewAllSearchResults = useCallback(async () => {
    if (!trimmedSearchQuery) return;
    setSearchLoading(true);
    try {
      const { presales } = await searchPublicPresalesViaBackend(trimmedSearchQuery, {
        limit: LAUNCHPAD_SEARCH_RESULT_LIMIT_EXPANDED
      });
      setActiveListSearchQuery(trimmedSearchQuery);
      setActiveListSearchRows(presales.map((pool) => ({ id: Number(pool.id), pool })));
      setPoolListPage(1);
      setIsSearchOpen(false);
    } catch (error) {
      setStatus(error?.message || "Search failed");
    } finally {
      setSearchLoading(false);
    }
  }, [trimmedSearchQuery, setStatus]);

  const searchPanelContent = (
    <div className={`lp-search-panel ${isSearchOpen ? "is-open" : ""}`}>
      <div className="lp-search-panel-head">
        <div className="lp-search-input-wrap">
          <img src="/icons/search.svg" alt="" aria-hidden="true" className="lp-search-input-icon" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by token name or symbol"
            className="lp-search-input"
            onKeyDown={(event) => {
              if (event.key === "Enter" && trimmedSearchQuery) {
                event.preventDefault();
                setSearchPending(false);
                setDebouncedSearchQuery(trimmedSearchQuery);
              }
              if (event.key === "Escape") {
                event.preventDefault();
                setIsSearchOpen(false);
              }
            }}
          />
          {searchQuery ? (
            <button
              type="button"
              className="lp-search-clear"
              onClick={() => {
                setSearchQuery("");
                setDebouncedSearchQuery("");
                setSearchResults([]);
                setSearchTotalCount(0);
                setSearchPending(false);
                setSearchLoading(false);
                setSearchShowAllResults(false);
              }}
              aria-label="Clear search"
            >
              &times;
            </button>
          ) : null}
        </div>
      </div>
      <div className="lp-search-panel-body">
        {!searchHasQuery ? (
          <>
            <div className="lp-search-group">
              <div className="lp-search-group-head">
                <span>Recent Searches</span>
              </div>
              {recentSearches.length ? (
                <div className="lp-search-chip-list">
                  {recentSearches.map((term) => (
                    <button
                      key={term}
                      type="button"
                      className="lp-search-chip"
                      onClick={() => {
                        setSearchQuery(term);
                        setSearchPending(true);
                      }}
                    >
                      {term}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="lp-search-empty">Your recent searches will show here.</p>
              )}
            </div>
            <div className="lp-search-group">
              <div className="lp-search-group-head">
                <span>Recently Viewed</span>
              </div>
              {recentViewedPresales.length ? (
                <div className="lp-search-results">
                  {recentViewedPresales.map((pool) => renderSearchResultRow({ id: Number(pool.id), pool }, "recent-viewed"))}
                </div>
              ) : (
                <p className="lp-search-empty">Presales you open will appear here.</p>
              )}
            </div>
          </>
        ) : showSearchSkeleton ? (
          <div className="lp-search-results">
            {Array.from({ length: 5 }, (_, index) => renderSearchSkeletonRow(`search-skeleton-${index}`))}
          </div>
        ) : (
          <div className="lp-search-group">
            {!searchShowAllResults && searchResults.length >= LAUNCHPAD_SEARCH_RESULT_LIMIT ? (
              <button
                type="button"
                className="lp-search-viewall"
                onClick={handleViewAllSearchResults}
              >
                View all results &rarr;
              </button>
            ) : null}
            {searchResults.length ? (
              <div className="lp-search-results">
                {searchResults.map((row) => renderSearchResultRow(row))}
              </div>
            ) : (
              <p className="lp-search-empty">No presales matched your search.</p>
            )}
          </div>
        )}
        <div className="lp-search-legend" aria-hidden="true">
          <span><kbd className="lp-search-legend-enter"><span className="lp-search-legend-enter-text">&#8629;</span></kbd> to search</span>
          <span><kbd className="lp-search-legend-esc"><span className="lp-search-legend-esc-text">esc</span></kbd> to close</span>
        </div>
      </div>
    </div>
  );

  const renderPresaleCard = (row, tokenMetaMap, options = {}) => {
    const { statMode = "raised", boughtWeiRaw = 0n } = options;
    const { id, pool } = row;
    const meta = tokenMetaMap[String(id)] || null;
    const imageUri = meta?.image_uri || meta?.image || "";
    const imageUrl = imageUri ? ipfsToHttp(imageUri) : "";
    const softCapWei = BigInt(pool.softCapWei?.toString?.() ?? pool.softCapWei ?? 0n);
    const hardCapWei = BigInt(pool.hardCapWei?.toString?.() ?? pool.hardCapWei ?? 0n);
    const softCapEnabled = softCapWei > 0n;
    const raisedWei = BigInt(pool.totalRaisedWei?.toString?.() ?? pool.totalRaisedWei ?? 0n);
    const softHit = softCapEnabled && raisedWei >= softCapWei;
    const hardHit = hardCapWei > 0n && raisedWei >= hardCapWei;
    const softSectionFillPct =
      !softCapEnabled
        ? 0
        : raisedWei <= 0n
          ? 0
          : raisedWei >= softCapWei
            ? 100
            : Number((raisedWei * 10000n) / softCapWei) / 100;
    const hardSectionFillPct =
      !softCapEnabled
        ? (
          hardCapWei <= 0n
            ? 0
            : raisedWei <= 0n
              ? 0
              : raisedWei >= hardCapWei
                ? 100
                : Number((raisedWei * 10000n) / hardCapWei) / 100
        )
        : hardCapWei <= softCapWei
          ? 0
          : raisedWei <= softCapWei
            ? 0
            : raisedWei >= hardCapWei
              ? 100
              : Number(((raisedWei - softCapWei) * 10000n) / (hardCapWei - softCapWei)) / 100;
    const cardTrackGridStyle =
      softCapEnabled && hardCapWei > softCapWei
        ? (() => {
            const softRatio = Number((softCapWei * 10000n) / hardCapWei) / 10000;
            const hardRatio = Math.max(0.0001, 1 - softRatio);
            return {
              "--lp-card-soft-ratio": String(softRatio),
              "--lp-card-hard-ratio": String(hardRatio),
            };
          })()
        : undefined;
    const now = Math.floor(Date.now() / 1000);
    const displayState = computePresaleDisplayState(
      pool,
      now,
      pool?.launchGracePeriod ?? launchGracePeriod
    );
    const cardStatusPrefix =
      displayState === "Live" ? "Presale is"
        : displayState === "Upcoming" ? "Presale is"
          : displayState === "Completed" ? "Presale"
            : displayState === "Finalized" ? "Presale"
              : displayState === "Cancelled" ? "Presale was"
                : "Presale has";
    const cardStatusWord =
      displayState === "Completed" ? "Completed"
        : displayState === "Finalized" ? "Launched"
          : displayState;
    const cardStatusToneKey =
      displayState.toLowerCase().replace(/[^a-z]+/g, "-");
    const statWei = statMode === "bought" ? BigInt(boughtWeiRaw?.toString?.() ?? boughtWeiRaw ?? 0n) : raisedWei;
    const displayStateLabel = presentPresaleDisplayState(displayState);

    return (
      <Link
        key={id}
        to={String(id).startsWith("dev-") ? "/launchpad" : buildLaunchpadDetailPath(id, pool?.contractAddress)}
        className="lp-item lp-presale-card"
        onClick={String(id).startsWith("dev-") ? (e) => e.preventDefault() : undefined}
      >
        <div className="lp-token-summary-card lp-presale-token-card">
          <div className="lp-token-summary-media">
            <span className={`lp-state-chip lp-state-${displayState.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {displayStateLabel}
            </span>
            <div className="lp-token-summary-media-socials" aria-label="Token social links">
              {pool.website ? (
                <span className="lp-social-icon-link" aria-hidden="true" title="Website available">
                  <img src="/icons/web.svg" alt="" aria-hidden="true" />
                </span>
              ) : null}
              {pool.twitter ? (
                <span className="lp-social-icon-link" aria-hidden="true" title="Twitter/X available">
                  <i className="bi bi-twitter-x" aria-hidden="true" />
                </span>
              ) : null}
              {pool.telegram ? (
                <span className="lp-social-icon-link" aria-hidden="true" title="Telegram available">
                  <i className="bi bi-telegram" aria-hidden="true" />
                </span>
              ) : null}
            </div>
            {imageUrl ? (
              <img src={imageUrl} alt={`${pool.name} token art`} className="lp-token-summary-image" />
            ) : (
              <div className="lp-token-summary-image lp-skeleton-block" aria-hidden="true" />
            )}
            <div className="lp-token-summary-media-overlay">
              <div className="lp-token-summary-media-meta">
                <h3>{pool.name || "Token Name"}</h3>
                <span>{pool.symbol || "SYMBOL"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lp-presale-card-bottom">
          <div className="lp-presale-card-market">
            <div className="lp-presale-card-track">
              <div className="lp-presale-card-raised">
                <em>{statMode === "bought" ? "Bought" : "Raised"}</em>
                <strong>
                  <span className="lp-presale-card-raised-amount">
                    {fmtMon(formatEther(statWei), 2)}
                    {statMode === "raised" ? ` / ${fmtMon(formatEther(hardCapWei), 2)}` : ""}
                  </span>{" "}
                  <span className="lp-presale-card-raised-unit">MON</span>
                </strong>
              </div>
              <div className="lp-presale-card-cap-row">
                {softCapEnabled ? (
                  <div className="lp-presale-card-cap-row-item lp-presale-card-cap-row-item-soft">
                    <em>Soft Cap</em>
                    <span>{fmtMon(formatEther(softCapWei), 2)} MON</span>
                  </div>
                ) : (
                  <div className="lp-presale-card-cap-row-item lp-presale-card-cap-row-item-soft">
                    <em>Soft Cap</em>
                    <span>None</span>
                  </div>
                )}
                <div className="lp-presale-card-cap-row-item lp-presale-card-cap-row-item-hard">
                  <em>Hard Cap</em>
                  <span>{fmtMon(formatEther(hardCapWei), 2)} MON</span>
                </div>
              </div>
              <div
                className={`lp-presale-card-track-grid${softCapEnabled ? "" : " is-single"}`}
                style={cardTrackGridStyle}
              >
                {softCapEnabled ? (
                  <div className="lp-presale-card-track-side lp-presale-card-track-side-soft">
                    <div className="lp-presale-card-segment lp-presale-card-segment-soft">
                      <div
                        className={`lp-presale-card-fill lp-presale-card-fill-soft ${softHit ? "is-hit" : ""}`}
                        style={{ width: `${softSectionFillPct}%` }}
                      />
                    </div>
                    <div className="lp-presale-card-cap-stack lp-presale-card-cap-stack-soft">
                      <span className={`lp-presale-card-marker lp-presale-card-marker-soft ${softHit ? "is-hit" : ""}`} aria-hidden="true" />
                    </div>
                  </div>
                ) : null}
                <div className="lp-presale-card-track-side lp-presale-card-track-side-hard">
                  <div className="lp-presale-card-segment lp-presale-card-segment-hard">
                    <div
                      className={`lp-presale-card-fill lp-presale-card-fill-hard ${hardHit ? "is-hit" : ""}`}
                      style={{ width: `${hardSectionFillPct}%` }}
                    />
                  </div>
                  <div className="lp-presale-card-cap-stack lp-presale-card-cap-stack-hard">
                    <span className={`lp-presale-card-marker lp-presale-card-marker-hard ${hardHit ? "is-hit" : ""}`} aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
            <div className="lp-presale-card-status-strip">
              <span>{cardStatusPrefix}</span>
              <strong className={`lp-presale-card-status-word-${cardStatusToneKey}`}>{cardStatusWord}</strong>
            </div>
          </div>
        </div>
      </Link>
    );
  };

  const renderPresaleSkeletonCard = (key) => (
    <Link key={key} to={buildLaunchpadBasePath()} className="lp-item lp-presale-card lp-presale-card-skeleton" aria-hidden="true" onClick={(e) => e.preventDefault()}>
      <div className="lp-token-summary-card lp-presale-token-card">
        <div className="lp-token-summary-media lp-skeleton-media">
          <span className="lp-state-chip lp-skeleton-pill lp-skeleton-block" />
          <div className="lp-token-summary-image lp-skeleton-block" />
          <div className="lp-token-summary-media-overlay lp-skeleton-overlay">
            <div className="lp-token-summary-media-meta">
              <h3 className="lp-skeleton-heading">
                <span className="lp-skeleton-line lp-skeleton-line-title lp-skeleton-block" />
              </h3>
              <span className="lp-skeleton-subtext">
                <span className="lp-skeleton-line lp-skeleton-line-symbol lp-skeleton-block" />
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="lp-presale-card-bottom">
        <div className="lp-presale-card-market">
          <div className="lp-presale-card-track">
            <div className="lp-presale-card-raised">
              <span className="lp-skeleton-line lp-skeleton-line-label lp-skeleton-block lp-skeleton-ghost-row" aria-hidden="true" />
              <span className="lp-skeleton-line lp-skeleton-line-value lp-skeleton-block" />
            </div>
            <div className="lp-presale-card-cap-row lp-skeleton-ghost-row" aria-hidden="true">
              <div className="lp-presale-card-cap-row-item lp-presale-card-cap-row-item-soft">
                <span className="lp-skeleton-line lp-skeleton-line-cap-label lp-skeleton-block" />
                <span className="lp-skeleton-line lp-skeleton-line-cap-value lp-skeleton-block" />
              </div>
              <div className="lp-presale-card-cap-row-item lp-presale-card-cap-row-item-hard">
                <span className="lp-skeleton-line lp-skeleton-line-cap-label lp-skeleton-block" />
                <span className="lp-skeleton-line lp-skeleton-line-cap-value lp-skeleton-block" />
              </div>
            </div>
            <div className="lp-presale-card-track-grid lp-skeleton-ghost-row" aria-hidden="true">
              <div className="lp-presale-card-track-side lp-presale-card-track-side-soft">
                <div className="lp-presale-card-segment lp-skeleton-segment">
                  <div className="lp-skeleton-fill" />
                </div>
              </div>
              <div className="lp-presale-card-track-side lp-presale-card-track-side-hard">
                <div className="lp-presale-card-segment lp-skeleton-segment">
                  <div className="lp-skeleton-fill" />
                </div>
              </div>
            </div>
            <div className="lp-presale-card-segment lp-skeleton-segment lp-skeleton-segment-single">
              <div className="lp-skeleton-fill" />
            </div>
          </div>
          <div className="lp-presale-card-status-strip">
            <span className="lp-skeleton-line lp-skeleton-line-status lp-skeleton-block" />
          </div>
        </div>
      </div>
    </Link>
  );

  const renderConnectWalletNotice = ({
    kicker = "Wallet Required",
    title = "Connect Your Wallet",
    copy = "Connect your wallet to continue.",
  } = {}) => (
    <div className="lp-wallet-notice" role="status" aria-live="polite">
      <div className="lp-wallet-notice-card">
        <span className="lp-wallet-notice-kicker">{kicker}</span>
        <div className="lp-wallet-notice-title">{title}</div>
        <div className="lp-wallet-notice-copy">{copy}</div>
        <button
          type="button"
          className="lp-wallet-notice-btn"
          onClick={() => openConnectModal?.()}
        >
          Connect Wallet
        </button>
      </div>
    </div>
  );

  return (
    <div
      id="bob-launchpad-page"
      className={`lp-page ${isSearchOpen ? "is-search-open" : ""}`}
      style={{ "--lp-beta-offset": showBetaBanner ? "42px" : "0px" }}
    >
      <div className="lp-bg" aria-hidden="true" style={{ "--lp-bg-image": 'url("/landing/bobspace.webp")' }} />
      {isSearchOpen ? (
        <button
          type="button"
          className="lp-search-backdrop"
          aria-label="Close search"
          onClick={() => setIsSearchOpen(false)}
        />
      ) : null}
      <Nav />
      {showBetaBanner ? (
        <div className="lp-beta-banner-wrap" aria-label="Launchpad beta notice">
          <div className="lp-beta-banner">
            <span className="lp-beta-banner-text">The Bob Launchpad is in beta. Ongoing updates and polish are still in progress.</span>
            <button
              type="button"
              className="lp-beta-banner-close"
              aria-label="Dismiss beta notice"
              onClick={() => setShowBetaBanner(false)}
            >
              ×
            </button>
          </div>
        </div>
      ) : null}
      {route.mode !== "documentation" ? (
        <section className="lp-activity-rail" aria-label="Recent launchpad activity">
          <div
            ref={railViewportRef}
            className="lp-activity-rail-viewport"
            onPointerLeave={() => {
              setRailPaused(false);
              railLastFrameRef.current = 0;
              setHoveredRailItemKey("");
            }}
          >
            <div ref={railTrackRef} className="lp-activity-rail-track">
              {Array.from({ length: railRepeatCount }, (_, groupIdx) => groupIdx).map((groupIdx) => (
                <div
                  key={`rail-group-${groupIdx}`}
                  ref={groupIdx === 0 ? railGroupRef : undefined}
                  className="lp-activity-rail-group"
                  aria-hidden={groupIdx > 0 ? "true" : undefined}
                >
                  {launchpadActivityRailItems.map((item) => {
                    const railItemKey = `${groupIdx}-${item.id}`;
                    const reelHref = item.poolId
                      ? buildLaunchpadDetailPath(item.poolId, item.contractAddress)
                      : buildLaunchpadBasePath();
                    return (
                      <a
                        key={railItemKey}
                        className={`lp-activity-rail-item lp-activity-rail-item-${item.tone || "info"}${hoveredRailItemKey === railItemKey ? " is-hovered" : ""}`}
                        href={reelHref}
                        aria-label={item.poolId ? `View presale details for ${item.token || "this presale"}` : undefined}
                        onClick={!item.poolId ? (e) => e.preventDefault() : undefined}
                        onPointerEnter={() => {
                          setRailPaused(true);
                          setHoveredRailItemKey(railItemKey);
                        }}
                        onPointerLeave={() => {
                          setHoveredRailItemKey((prev) => (prev === railItemKey ? "" : prev));
                          setRailPaused(false);
                          railLastFrameRef.current = 0;
                        }}
                      >
                        {item.actor && item.action && item.token ? (
                          <>
                            <span className="lp-activity-rail-actor">{item.actor}</span>
                            <span className="lp-activity-rail-action">{item.action}</span>
                            {item.image ? (
                              <span className="lp-activity-rail-token-icon" aria-hidden="true">
                                <img src={item.image} alt="" />
                              </span>
                            ) : null}
                            <span className="lp-activity-rail-token">{item.token}</span>
                          </>
                        ) : (
                          <span className="lp-activity-rail-text">{item.text}</span>
                        )}
                      </a>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}
      <main className="lp-main">
        <section className="lp-page-tabs-sticky" aria-label="Launchpad page tabs">
          <div className="lp-page-tabs-card">
            <div className="lp-page-tabs" role="tablist" aria-label="Launchpad sections">
              <Link
                to={buildLaunchpadBasePath()}
                className={`lp-page-tab-btn lp-page-tab-btn-list ${launchpadTopTab === "list" ? "is-active" : ""}`}
                aria-current={launchpadTopTab === "list" ? "page" : undefined}
              >
                <span className="lp-tab-icon" style={{ "--tab-icon": "url('/icons/rocket.svg')" }} aria-hidden="true" />
                <span className="lp-tab-label">Presales</span>
              </Link>
              <Link
                to={buildLaunchpadBasePath("create")}
                className={`lp-page-tab-btn lp-page-tab-btn-create ${launchpadTopTab === "create" ? "is-active" : ""}`}
                aria-current={launchpadTopTab === "create" ? "page" : undefined}
              >
                <span className="lp-tab-icon" style={{ "--tab-icon": "url('/icons/settings.svg')" }} aria-hidden="true" />
                <span className="lp-tab-label">Create</span>
              </Link>
              <Link
                to={buildLaunchpadBasePath("profile")}
                className={`lp-page-tab-btn lp-page-tab-btn-profile ${launchpadTopTab === "profile" ? "is-active" : ""}`}
                aria-current={launchpadTopTab === "profile" ? "page" : undefined}
              >
                <span className="lp-tab-icon" style={{ "--tab-icon": "url('/icons/profile.svg')" }} aria-hidden="true" />
                <span className="lp-tab-label">Profile</span>
              </Link>
              <a
                href="/docs"
                className={`lp-page-tab-btn lp-page-tab-btn-documentation ${launchpadTopTab === "documentation" ? "is-active" : ""}`}
                aria-current={launchpadTopTab === "documentation" ? "page" : undefined}
              >
                <span className="lp-tab-icon" style={{ "--tab-icon": "url('/icons/documentation.svg')" }} aria-hidden="true" />
                <span className="lp-tab-label">Support</span>
              </a>
            </div>
          </div>
        </section>
        <div className="lp-content-stack">
        {route.mode === "home" ? (
          <div className="lp-home-page">
            <section className="lp-home-hero">
              <div className="lp-home-hero-body">
                <div className="lp-home-hero-copy">
                  <div className="lp-home-heading">
                    <span className="lp-home-heading-kicker">Launchpad</span>
                    <h1>BOB <span className="lp-accent">LAUNCHPAD</span></h1>
                    <p className="lp-home-heading-subtitle">Presale on Bob, launch on Nad.fun</p>
                  </div>
                  <div className="lp-home-intro-card">
                    <h2>Launch tokens with a presale flow built for Monad communities.</h2>
                    <p className="lp-sub">
                      Creators raise MON through Bob Launchpad, configure fee routing, optional whitelist access,
                      and then launch directly into Nad.fun once the presale is ready to finalize.
                    </p>
                    <div className="lp-home-hero-actions">
                      <a href={buildLaunchpadBasePath("create")} className="lp-home-cta-btn lp-home-cta-btn-primary">Create Presale</a>
                      <a href={buildLaunchpadBasePath("presales")} className="lp-home-cta-btn lp-home-cta-btn-secondary">Browse Presales</a>
                    </div>
                    <div className="lp-home-hero-points" aria-label="Launchpad highlights">
                      <span>Public and whitelist presales</span>
                      <span>Nad.fun launch-safe math</span>
                      <span>Built-in fee routing</span>
                    </div>
                  </div>
                  <div className="lp-home-stat-grid">
                    <div className="lp-home-stat-card">
                      <em>Total Presales</em>
                      <strong>{homeStats.total}</strong>
                    </div>
                    <div className="lp-home-stat-card">
                      <em>Live Now</em>
                      <strong>{homeStats.live}</strong>
                    </div>
                    <div className="lp-home-stat-card">
                      <em>Upcoming</em>
                      <strong>{homeStats.upcoming}</strong>
                    </div>
                    <div className="lp-home-stat-card">
                      <em>Launched</em>
                      <strong>{homeStats.launched}</strong>
                    </div>
                  </div>
                </div>
                <div className="lp-home-cta-panel">
                  <div className="lp-home-hero-visual-card">
                    <div className="lp-home-hero-banner">
                      <img src="/bass/bobbanner.jpg" alt="Bob Launchpad banner art" className="lp-home-hero-banner-image" />
                      <div className="lp-home-hero-banner-overlay">
                        <span className="lp-home-cta-kicker">Launch Snapshot</span>
                        <h3>Presale on Bob. Launch on Nad.fun.</h3>
                      </div>
                    </div>
                    <div className="lp-home-snapshot-grid">
                      <div className="lp-home-snapshot-item">
                        <span className="lp-home-snapshot-label">Raised Across Presales</span>
                        <strong>{fmtCompactAmount(formatEther(homeStats.totalRaisedWei))} MON</strong>
                      </div>
                      <div className="lp-home-snapshot-item">
                        <span className="lp-home-snapshot-label">Participant Entries</span>
                        <strong>{homeStats.totalParticipants.toLocaleString()}</strong>
                      </div>
                      <div className="lp-home-snapshot-item">
                        <span className="lp-home-snapshot-label">Awaiting Launch</span>
                        <strong>{homeStats.completed}</strong>
                      </div>
                    </div>
                    <div className="lp-home-mini-rail">
                      <a href={buildLaunchpadBasePath("create")} className="lp-home-mini-card">
                        <img src="/icons/rocket.svg" alt="" aria-hidden="true" className="lp-home-mini-icon" />
                        <div>
                          <span>Create</span>
                          <strong>Launch a new presale</strong>
                        </div>
                      </a>
                      <a href={buildLaunchpadBasePath("profile")} className="lp-home-mini-card">
                        <img src="/icons/profile.svg" alt="" aria-hidden="true" className="lp-home-mini-icon" />
                        <div>
                          <span>Profile</span>
                          <strong>Track your buys and claims</strong>
                        </div>
                      </a>
                      <a href={buildLaunchpadBasePath("documentation")} className="lp-home-mini-card">
                        <img src="/icons/documentation.svg" alt="" aria-hidden="true" className="lp-home-mini-icon" />
                        <div>
                          <span>Docs</span>
                          <strong>Read launchpad guides</strong>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="lp-home-band lp-home-tools-band">
              <div className="lp-home-band-head">
                <span className="lp-home-band-kicker">What You Can Do</span>
                <h2>Built for founders and buyers</h2>
              </div>
              <div className="lp-home-tools-grid">
                <div className="lp-home-tool-card">
                  <img src="/icons/rocket.svg" alt="" aria-hidden="true" className="lp-home-tool-icon" />
                  <span className="lp-home-tool-kicker">Create</span>
                  <h3>Launch a presale in one flow</h3>
                  <p className="lp-sub">Set timing, caps, dev buy, whitelist settings, socials, and fee routing without leaving the page.</p>
                </div>
                <div className="lp-home-tool-card">
                  <img src="/icons/community.svg" alt="" aria-hidden="true" className="lp-home-tool-icon" />
                  <span className="lp-home-tool-kicker">Raise</span>
                  <h3>Collect MON transparently</h3>
                  <p className="lp-sub">Participants buy directly on-chain with allocations, state changes, refunds, and claims tracked by contract state.</p>
                </div>
                <div className="lp-home-tool-card">
                  <img src="/icons/burn.svg" alt="" aria-hidden="true" className="lp-home-tool-icon" />
                  <span className="lp-home-tool-kicker">Launch</span>
                  <h3>Finalize into Nad.fun</h3>
                  <p className="lp-sub">Use launch-safe limits and Nad-compatible math so presales can transition cleanly into the live launch flow.</p>
                </div>
              </div>
            </section>

            <section className="lp-home-band lp-home-spotlight-band">
              <div className="lp-home-band-head">
                <span className="lp-home-band-kicker">Featured</span>
                <h2>Latest token spotlight</h2>
              </div>
              {homeFeaturedPresale ? (
                <div className="lp-home-featured-grid">
                  <a href={buildLaunchpadDetailPath(homeFeaturedPresale.id, homeFeaturedPresale.pool?.contractAddress)} className="lp-home-featured-card">
                    <div className="lp-home-featured-media">
                      <img
                        src={(() => {
                          const meta = displayedPoolListTokenMeta[String(homeFeaturedPresale.id)] || null;
                          const uri = meta?.image_uri || meta?.image || "";
                          return uri ? ipfsToHttp(uri) : "/bass/bobmainmascot.webp";
                        })()}
                        alt={`${homeFeaturedPresale.pool?.name || "Presale"} token art`}
                        className="lp-home-featured-image"
                      />
                    </div>
                    <div className="lp-home-featured-copy">
                      <span className="lp-home-featured-kicker">Latest Presale</span>
                      <h3>{homeFeaturedPresale.pool?.name || "Presale"}</h3>
                      <p className="lp-home-featured-meta">
                        <span>{homeFeaturedPresale.pool?.symbol || "TOKEN"}</span>
                        <span>by <a href={buildMonadVisionAddressUrl(homeFeaturedPresale.pool?.creator)} target="_blank" rel="noreferrer">{shortAddr(homeFeaturedPresale.pool?.creator || "")}</a></span>
                      </p>
                      <p className="lp-sub">
                        {homeFeaturedPresale.pool?.description || "Browse the latest presale created on Bob Launchpad."}
                      </p>
                      <div className="lp-home-featured-stats">
                        <span><em>Status</em><strong>{presentPresaleDisplayState(computePresaleDisplayState(homeFeaturedPresale.pool, liveNowTs, homeFeaturedPresale.pool?.launchGracePeriod ?? launchGracePeriod))}</strong></span>
                        <span><em>Raised</em><strong>{fmtMon(formatEther(getPresaleRaisedWei(homeFeaturedPresale.pool)), 2)} MON</strong></span>
                        <span><em>Participants</em><strong>{Number(homeFeaturedPresale.pool?.participantCount || 0).toLocaleString()}</strong></span>
                      </div>
                    </div>
                  </a>
                  <div className="lp-home-featured-side">
                    <div className="lp-home-featured-note">
                      <span className="lp-home-featured-note-kicker">Launchpad flow</span>
                      <ul className="lp-home-featured-list">
                        <li>Create your token metadata and presale setup</li>
                        <li>Open participation to the community</li>
                        <li>Finalize and launch into Nad.fun</li>
                      </ul>
                    </div>
                    <div className="lp-home-featured-note">
                      <span className="lp-home-featured-note-kicker">Good for</span>
                      <ul className="lp-home-featured-list">
                        <li>Community launches</li>
                        <li>Whitelist rounds</li>
                        <li>Fee-routed token launches</li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : null}
            </section>

            <section className="lp-home-band lp-home-recent-band">
              <div className="lp-home-band-head">
                <span className="lp-home-band-kicker">Recent Presales</span>
                <h2>Browse the newest presales</h2>
              </div>
              {poolListLoading && !devPreviewCardsEnabled ? (
                <div className="lp-list lp-home-list lp-list-skeleton" aria-label="Loading recent presales">
                  {Array.from({ length: 4 }, (_, index) => renderPresaleSkeletonCard(`home-skeleton-${index}`))}
                </div>
              ) : homeRecentPresales.length ? (
                <>
                  <div className="lp-list lp-home-list">
                    {homeRecentPresales.map((row) => renderPresaleCard(row, displayedPoolListTokenMeta))}
                  </div>
                  <div className="lp-home-section-actions">
                    <a href={buildLaunchpadBasePath("presales")} className="lp-home-inline-link">View all presales</a>
                  </div>
                </>
              ) : (
                <p className="lp-sub">No presales yet.</p>
                )}
              </section>

            <section className="lp-home-band lp-home-launched-band">
              <div className="lp-home-band-head">
                <span className="lp-home-band-kicker">Recently Launched</span>
                <h2>The latest presales that made it to launch</h2>
              </div>
              {homeLaunchedPresales.length ? (
                <div className="lp-home-launch-list">
                  {homeLaunchedPresales.map((row) => {
                    const meta = displayedPoolListTokenMeta[String(row.id)] || null;
                    const imageUri = meta?.image_uri || meta?.image || "";
                    const imageUrl = imageUri ? ipfsToHttp(imageUri) : "/bass/bobmainmascot.webp";
                    return (
                      <a key={`launched-${row.id}`} href={buildLaunchpadDetailPath(row.id, row.pool?.contractAddress)} className="lp-home-launch-row">
                        <img src={imageUrl} alt={`${row.pool?.name || "Presale"} token art`} className="lp-home-launch-row-image" />
                        <div className="lp-home-launch-row-main">
                          <div className="lp-home-launch-row-top">
                            <div className="lp-home-launch-row-title">
                              <strong>{row.pool?.name || "Presale"}</strong>
                              <span>{row.pool?.symbol || "TOKEN"}</span>
                            </div>
                            <span className="lp-state-chip lp-state-finalized">Launched</span>
                          </div>
                          <div className="lp-home-launch-row-stats">
                            <span><em>Raised</em><strong>{fmtMon(formatEther(getPresaleRaisedWei(row.pool)), 2)} MON</strong></span>
                            <span><em>Participants</em><strong>{Number(row.pool?.participantCount || 0).toLocaleString()}</strong></span>
                            <span><em>Creator</em><strong><a href={buildMonadVisionAddressUrl(row.pool?.creator)} target="_blank" rel="noreferrer">{shortAddr(row.pool?.creator || "")}</a></strong></span>
                            <span><em>Ended</em><strong>{fmtRelativeDurationLong(Math.max(0, liveNowTs - Number(row.pool?.endTime || 0)))} Ago</strong></span>
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
              ) : (
                <p className="lp-sub">No launched presales yet.</p>
              )}
            </section>

            <section className="lp-home-band lp-home-flow-band">
              <div className="lp-home-band-head">
                <span className="lp-home-band-kicker">How It Works</span>
                <h2>From presale setup to live launch</h2>
              </div>
              <div className="lp-home-flow-grid">
                <div className="lp-home-flow-card">
                  <span className="lp-home-flow-step">1</span>
                  <h3>Create on Bob</h3>
                  <p className="lp-sub">Set metadata, hard cap, soft cap, contribution rules, whitelist settings, and fee routing.</p>
                </div>
                <div className="lp-home-flow-card">
                  <span className="lp-home-flow-step">2</span>
                  <h3>Raise with your community</h3>
                  <p className="lp-sub">Users participate directly on-chain while the launchpad keeps allocations, refunds, and claims in sync.</p>
                </div>
                <div className="lp-home-flow-card">
                  <span className="lp-home-flow-step">3</span>
                  <h3>Finalize into Nad.fun</h3>
                  <p className="lp-sub">When the presale is ready, launch with Nad-compatible math and launch-safe protections already built in.</p>
                </div>
              </div>
            </section>

            <section className="lp-home-band lp-home-cta-band">
              <div className="lp-home-cta-split">
                <div className="lp-home-cta-copy">
                  <span className="lp-home-band-kicker">Start Building</span>
                  <h2>Ready to launch your token?</h2>
                  <p className="lp-sub">Create a presale page, set your fee strategy, and prepare your token for a smooth Nad.fun launch.</p>
                  <div className="lp-home-hero-actions">
                    <a href={buildLaunchpadBasePath("create")} className="lp-home-cta-btn lp-home-cta-btn-primary">Create Presale</a>
                    <a href={buildLaunchpadBasePath("presales")} className="lp-home-cta-btn lp-home-cta-btn-secondary">View Presales</a>
                  </div>
                </div>
                <div className="lp-home-cta-art">
                  <img src="/bass/bobsale.png" alt="Bob launchpad placeholder art" className="lp-home-cta-art-image" />
                </div>
              </div>
            </section>

            <section className="lp-home-band lp-home-faq-band">
              <div className="lp-home-band-head">
                <span className="lp-home-band-kicker">About Bob Launchpad</span>
                <h2>Questions founders and buyers usually have</h2>
              </div>
              <div className="lp-home-faq-accordion">
                <details className="lp-home-faq-item" open>
                  <summary>What is Bob Launchpad?</summary>
                  <p className="lp-sub">Bob Launchpad is a presale platform for Monad projects. Creators open a presale on Bob, collect MON from participants, and then finalize into Nad.fun when the presale is ready to launch.</p>
                </details>
                <details className="lp-home-faq-item">
                  <summary>Why not just launch directly?</summary>
                  <p className="lp-sub">Presales give creators more control over timing, fee setup, access rules, and distribution before entering the live curve. That makes launches easier to coordinate for communities.</p>
                </details>
                <details className="lp-home-faq-item">
                  <summary>What can users do here?</summary>
                  <p className="lp-sub">Users can browse presales, buy allocations, track participation from the profile page, claim tokens after launch, or claim refunds automatically if a presale fails.</p>
                </details>
                <details className="lp-home-faq-item">
                  <summary>Is it non-custodial?</summary>
                  <p className="lp-sub">Yes. Presale participation, refunds, claims, and launch actions are governed on-chain through the launchpad contract rather than through an off-chain custody flow.</p>
                </details>
              </div>
            </section>
          </div>
        ) : null}
          {route.mode === "list" ? (
            <>
            <div className="lp-list-banner-section" aria-label="gBob launchpad banner">
              <picture className="lp-list-banner-picture">
                <source media="(max-width: 639px)" srcSet={`/banners/gbob-banner-sm.png?v=${LAUNCHPAD_BANNER_ASSET_VERSION}`} />
                <source media="(max-width: 1023px)" srcSet={`/banners/gbob-banner-md.png?v=${LAUNCHPAD_BANNER_ASSET_VERSION}`} />
                <img
                  src={`/banners/gbob-banner-lg.png?v=${LAUNCHPAD_BANNER_ASSET_VERSION}`}
                  alt="gBob presales on gBob.fun with fair launch on Nad.fun"
                  className="lp-list-banner-image"
                />
              </picture>
            </div>
            <section className="lp-card lp-page-section lp-page-section-list">
            <div className="lp-page-section-body">
              {activeListSearchQuery ? (
                <div className="lp-list-searchstate">
                  <span className="lp-list-searchstate-copy">{`Showing results for "${activeListSearchQuery}"`}</span>
                  <button
                    type="button"
                    className="lp-list-searchstate-clear"
                    onClick={() => {
                      setActiveListSearchQuery("");
                      setActiveListSearchRows([]);
                      setPoolListPage(1);
                    }}
                  >
                    Clear search
                  </button>
                </div>
              ) : null}
              <div className="lp-list-controls">
                <div className="lp-list-sortbar lp-list-sortbar-desktop" role="group" aria-label="Presale sorting">
                  <button
                    type="button"
                    className={`lp-list-sortbtn ${poolListSortMode === "latestTrade" ? "is-active" : ""}`}
                    onClick={() => setPoolListSortMode("latestTrade")}
                  >
                    Activity
                  </button>
                  <button
                    type="button"
                    className={`lp-list-sortbtn ${poolListSortMode === "raisedAmount" ? "is-active" : ""}`}
                    onClick={() => setPoolListSortMode("raisedAmount")}
                  >
                    Raised
                  </button>
                  <button
                    type="button"
                    className={`lp-list-sortbtn ${poolListSortMode === "newestCreated" ? "is-active" : ""}`}
                    onClick={() => setPoolListSortMode("newestCreated")}
                  >
                    Newest
                  </button>
                  <button
                    type="button"
                    className={`lp-list-sortbtn ${poolListSortMode === "oldestCreated" ? "is-active" : ""}`}
                    onClick={() => setPoolListSortMode("oldestCreated")}
                  >
                    Oldest
                  </button>
                </div>
                <div className="lp-list-selects-mobile">
                  <div className={`lp-custom-select lp-list-custom-select ${openSelectKey === "listSort" ? "is-open" : ""}`}>
                    <button
                      type="button"
                      className={`lp-custom-select-trigger lp-list-select-trigger ${openSelectKey === "listSort" ? "is-open" : ""}`}
                      aria-haspopup="listbox"
                      aria-expanded={openSelectKey === "listSort"}
                      onClick={() => {
                        setIsSearchOpen(false);
                        setOpenSelectKey((v) => (v === "listSort" ? "" : "listSort"));
                      }}
                    >
                      <span className="lp-list-select-copy">
                        <span className="lp-list-select-copy-label">Sort:</span>
                        <span className="lp-list-select-copy-value">{poolListSortLabel}</span>
                      </span>
                      <svg className="lp-custom-select-chevron" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <div className={`lp-custom-select-menu ${openSelectKey === "listSort" ? "is-open" : ""}`} role="listbox" aria-label="Sort Presales">
                      <button type="button" className={`lp-custom-select-option ${poolListSortMode === "latestTrade" ? "is-active" : ""}`} onClick={() => { setPoolListSortMode("latestTrade"); setOpenSelectKey(""); }}>
                        {getLaunchpadSortLabel("latestTrade", useCompactSortLabels)}
                      </button>
                      <button type="button" className={`lp-custom-select-option ${poolListSortMode === "raisedAmount" ? "is-active" : ""}`} onClick={() => { setPoolListSortMode("raisedAmount"); setOpenSelectKey(""); }}>
                        {getLaunchpadSortLabel("raisedAmount", useCompactSortLabels)}
                      </button>
                      <button type="button" className={`lp-custom-select-option ${poolListSortMode === "newestCreated" ? "is-active" : ""}`} onClick={() => { setPoolListSortMode("newestCreated"); setOpenSelectKey(""); }}>
                        {getLaunchpadSortLabel("newestCreated", useCompactSortLabels)}
                      </button>
                      <button type="button" className={`lp-custom-select-option ${poolListSortMode === "oldestCreated" ? "is-active" : ""}`} onClick={() => { setPoolListSortMode("oldestCreated"); setOpenSelectKey(""); }}>
                        {getLaunchpadSortLabel("oldestCreated", useCompactSortLabels)}
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`lp-search-widget ${isSearchOpen ? "is-open" : ""}`} ref={searchWidgetRef}>
                  <button
                    type="button"
                    className={`lp-custom-select-trigger lp-list-select-trigger lp-list-searchbtn ${isSearchOpen ? "is-open" : ""}`}
                    aria-label="Open presale search"
                    title="Search presales"
                    aria-expanded={isSearchOpen}
                    onClick={() => {
                      setOpenSelectKey("");
                      setIsSearchOpen((prev) => !prev);
                    }}
                  >
                    <img src="/icons/search.svg" alt="" aria-hidden="true" className="lp-list-searchbtn-icon" />
                  </button>
                </div>
                {typeof document !== "undefined" && isSearchOpen
                  ? createPortal(
                    <div className="lp-search-panel-shell">
                      {searchPanelContent}
                    </div>,
                    document.getElementById("bob-launchpad-page") || document.body
                  )
                  : null}
                <div className="lp-list-filter">
                  <div className={`lp-custom-select lp-list-custom-select ${openSelectKey === "listFilter" ? "is-open" : ""}`}>
                    <button
                      type="button"
                      className={`lp-custom-select-trigger lp-list-select-trigger ${openSelectKey === "listFilter" ? "is-open" : ""}`}
                      aria-haspopup="listbox"
                      aria-expanded={openSelectKey === "listFilter"}
                      onClick={() => {
                        setIsSearchOpen(false);
                        setOpenSelectKey((v) => (v === "listFilter" ? "" : "listFilter"));
                      }}
                    >
                      <span className="lp-list-select-copy">
                        <span className="lp-list-select-copy-label">Filter:</span>
                        <span className="lp-list-select-copy-value">{poolListFilterLabel}</span>
                      </span>
                      <svg className="lp-custom-select-chevron" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <div className={`lp-custom-select-menu ${openSelectKey === "listFilter" ? "is-open" : ""}`} role="listbox" aria-label="Filter Presales">
                      <button type="button" className={`lp-custom-select-option ${poolListFilterMode === "all" ? "is-active" : ""}`} onClick={() => { setPoolListFilterMode("all"); setOpenSelectKey(""); }}>
                        All
                      </button>
                      <button type="button" className={`lp-custom-select-option ${poolListFilterMode === "upcoming" ? "is-active" : ""}`} onClick={() => { setPoolListFilterMode("upcoming"); setOpenSelectKey(""); }}>
                        {getLaunchpadFilterLabel("upcoming")}
                      </button>
                      <button type="button" className={`lp-custom-select-option ${poolListFilterMode === "live" ? "is-active" : ""}`} onClick={() => { setPoolListFilterMode("live"); setOpenSelectKey(""); }}>
                        {getLaunchpadFilterLabel("live")}
                      </button>
                      <button type="button" className={`lp-custom-select-option ${poolListFilterMode === "completed" ? "is-active" : ""}`} onClick={() => { setPoolListFilterMode("completed"); setOpenSelectKey(""); }}>
                        {getLaunchpadFilterLabel("completed")}
                      </button>
                      <button type="button" className={`lp-custom-select-option ${poolListFilterMode === "launched" ? "is-active" : ""}`} onClick={() => { setPoolListFilterMode("launched"); setOpenSelectKey(""); }}>
                        {getLaunchpadFilterLabel("launched")}
                      </button>
                      <button type="button" className={`lp-custom-select-option ${poolListFilterMode === "failed" ? "is-active" : ""}`} onClick={() => { setPoolListFilterMode("failed"); setOpenSelectKey(""); }}>
                        {getLaunchpadFilterLabel("failed")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {sortedDisplayedPoolList.length ? (
                <>
                <div className="lp-list">
                  {pagedDisplayedPoolList.map((row) => renderPresaleCard(row, displayedPoolListTokenMeta))}
                </div>
                {sortedDisplayedPoolList.length ? (
                  <div className="lp-list-pagination" aria-label="Presale pagination">
                    <button
                      type="button"
                      className="lp-list-pagebtn lp-list-pagebtn-nav"
                      onClick={() => setPoolListPage((prev) => Math.max(1, prev - 1))}
                      disabled={poolListPage <= 1}
                      aria-label="Previous page"
                    >
                      <span className="lp-list-pagebtn-glyph" aria-hidden="true">
                        <img src="/icons/bracketarrow-left.svg" alt="" className="lp-list-pagebtn-icon" />
                      </span>
                    </button>
                    <button
                      type="button"
                      className="lp-list-pagebtn lp-list-pagebtn-nav"
                      onClick={() => setPoolListPage((prev) => Math.min(poolListTotalPages, prev + 1))}
                      disabled={poolListPage >= poolListTotalPages}
                      aria-label="Next page"
                    >
                      <span className="lp-list-pagebtn-glyph" aria-hidden="true">
                        <img src="/icons/bracketarrow-right.svg" alt="" className="lp-list-pagebtn-icon" />
                      </span>
                    </button>
                  </div>
                ) : null}
                </>
              ) : poolListLoading && !devPreviewCardsEnabled ? (
                <div className="lp-list lp-list-skeleton" aria-label="Loading presales">
                  {Array.from({ length: poolListPageSize }, (_, index) => renderPresaleSkeletonCard(`list-skeleton-${index}`))}
                </div>
              ) : (
                <p className="lp-sub">{displayedPoolList.length ? "No presales match the current filter." : "No presales yet."}</p>
              )}
            </div>
          </section>
          </>
        ) : null}

        {route.mode === "create" ? (
          <section className="lp-card lp-page-section lp-page-section-create">
            <div className="lp-page-section-head">
              <p className="lp-page-section-kicker">Launchpad</p>
              <h1 className="lp-page-section-title">CREATE <span className="lp-accent">PRESALE</span></h1>
            </div>
            <div className="lp-page-section-body">
            {!hasLaunchpadWalletSession ? renderConnectWalletNotice({
              title: "Unlock Presale Creation",
              copy: "Connect your wallet to create and launch a presale.",
            }) : null}
            <div className="lp-create-note" role="note" aria-label="How Presales Work">
              <div className="lp-create-note-head">
                <span className="lp-create-note-eyebrow">Launch Flow</span>
                <p className="lp-create-note-title">How Presales Work</p>
              </div>
              <div className="lp-create-note-grid">
                <div className="lp-create-note-step">
                  <span className="lp-create-note-step-badge">1</span>
                  <div>
                    <p className="lp-create-note-step-title">Raise on Bob Launchpad</p>
                    <p className="lp-create-note-step-text">Participants can join via the Bob launchpad while the presale is active.</p>
                  </div>
                </div>
                <div className="lp-create-note-step">
                  <span className="lp-create-note-step-badge">2</span>
                  <div>
                    <p className="lp-create-note-step-title">Finalize + Launch on Nad.fun</p>
                    <p className="lp-create-note-step-text">After completion, creator finalizes and token launches with configured settings.</p>
                  </div>
                </div>
                <div className="lp-create-note-step">
                  <span className="lp-create-note-step-badge">3</span>
                  <div>
                    <p className="lp-create-note-step-title">Claim From Presale Page</p>
                    <p className="lp-create-note-step-text">Participants can return to the presale page post-launch to claim allocations.</p>
                  </div>
                </div>
              </div>
              <div className="lp-create-note-foot">
                <span className="lp-create-note-foot-text">Presales can be configured as public or whitelist-only.</span>
              </div>
            </div>
            <div className="lp-section-label lp-field-full">
              <span className="lp-section-badge" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="24" height="24" role="presentation" focusable="false">
                  <circle cx="12" cy="12" r="11" className="lp-section-badge-circle" />
                  <text x="12" y="12" className="lp-section-badge-text">1</text>
                </svg>
              </span>
              <span>Token Details</span>
            </div>
            <div className="lp-create-top">
              <input
                id="lp-token-image"
                className="lp-upload-input"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={async (e) => {
                  try {
                    await onPickImage(e.target.files?.[0]);
                  } catch (err) {
                    setStatus(err?.message || "Invalid image");
                  }
                }}
              />
              <label
                htmlFor="lp-token-image"
                className={`lp-image-pick-box ${imagePreview ? "has-image" : ""}`}
                aria-label="Choose token image"
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="preview" className="lp-img-preview" />
                ) : (
                  <span className="lp-image-pick-placeholder">
                    <span className="lp-image-pick-title">Upload Image</span>
                    <span className="lp-image-pick-caption">Accepts image files under 5MB in a 1:1 ratio.</span>
                  </span>
                )}
                {!imagePreview && imageInfo ? <span className="lp-image-pick-meta">{imageInfo}</span> : null}
                {!imagePreview && createForm.predictedAddress ? (
                  <span className="lp-image-pick-meta">Predicted Nad token: {createForm.predictedAddress}</span>
                ) : null}
              </label>

              <div className="lp-create-top-fields">
                <div className="lp-name-symbol-row">
                  <label className="lp-field">
                    <div className="lp-field-head">
                      <span className="lp-field-label">Token Name</span>
                      <span className="lp-field-meta">{createForm.name.length}/32</span>
                    </div>
                    <input
                      placeholder="Example: Bob Coin"
                      maxLength={32}
                      value={createForm.name}
                      onChange={(e) => setCreateForm((s) => ({ ...s, name: e.target.value }))}
                    />
                  </label>
                  <label className="lp-field">
                    <div className="lp-field-head">
                      <span className="lp-field-label">Token Symbol</span>
                      <span className="lp-field-meta">{createForm.symbol.length}/10</span>
                    </div>
                    <input
                      placeholder="Example: BOB"
                      maxLength={10}
                      value={createForm.symbol}
                      onChange={(e) => setCreateForm((s) => ({ ...s, symbol: e.target.value }))}
                    />
                  </label>
                </div>
                <label className="lp-field lp-field-description">
                  <div className="lp-field-head">
                    <span className="lp-field-label">Description</span>
                    <span className="lp-field-meta">{createForm.description.length}/500</span>
                  </div>
                  <textarea
                    placeholder="Tell people what this token is about"
                    maxLength={500}
                    value={createForm.description}
                    onChange={(e) => setCreateForm((s) => ({ ...s, description: e.target.value }))}
                  />
                </label>
              </div>
            </div>

            <div className="lp-form">
              <div className="lp-social-group">
                <span>Social Links (Optional)</span>
                <div className="lp-social-row">
                  <label className="lp-field">
                    <input
                      placeholder="Website: https://..."
                      value={createForm.website}
                      onChange={(e) => setCreateForm((s) => ({ ...s, website: e.target.value }))}
                    />
                  </label>
                  <label className="lp-field">
                    <input
                      placeholder="Twitter/X: https://x.com/..."
                      value={createForm.twitter}
                      onChange={(e) => setCreateForm((s) => ({ ...s, twitter: e.target.value }))}
                    />
                  </label>
                  <label className="lp-field">
                    <input
                      placeholder="Telegram: https://t.me/..."
                      value={createForm.telegram}
                      onChange={(e) => setCreateForm((s) => ({ ...s, telegram: e.target.value }))}
                    />
                  </label>
                </div>
              </div>
              <div className="lp-fee-group">
                <span>Fee Distribution</span>
                <div className="lp-fee-grid">
                  <label className="lp-fee-card">
                    <span className="lp-fee-main">
                      <span className="lp-fee-toggle-col">
                        <span className="lp-fee-switch-wrap">
                          <input
                            type="checkbox"
                            checked={createForm.burnEnabled}
                            onChange={(e) => setCreateForm((s) => ({ ...s, burnEnabled: e.target.checked }))}
                          />
                          <span className="lp-fee-switch" />
                        </span>
                        <span className="lp-fee-card-title">Buyback & Burn</span>
                      </span>
                      <span className="lp-fee-card-description">{CREATE_FEE_OPTION_DESCRIPTIONS.burn}</span>
                    </span>
                    <span className="lp-fee-controls">
                      <span className={`lp-input-with-unit lp-input-with-unit-percent${createForm.burnEnabled ? "" : " is-invisible"}`}>
                        <input
                          className="lp-fee-pct-input"
                          placeholder=""
                          value={createForm.burnPct}
                          onChange={(e) => setCreateForm((s) => ({ ...s, burnPct: e.target.value }))}
                          disabled={!createForm.burnEnabled}
                        />
                        <i>%</i>
                      </span>
                    </span>
                  </label>
                  <label className="lp-fee-card">
                    <span className="lp-fee-main">
                      <span className="lp-fee-toggle-col">
                        <span className="lp-fee-switch-wrap">
                          <input
                            type="checkbox"
                            checked={createForm.dividendEnabled}
                            onChange={(e) => setCreateForm((s) => ({ ...s, dividendEnabled: e.target.checked }))}
                          />
                          <span className="lp-fee-switch" />
                        </span>
                        <span className="lp-fee-card-title">Dividend</span>
                      </span>
                      <span className="lp-fee-card-description">{CREATE_FEE_OPTION_DESCRIPTIONS.dividend}</span>
                    </span>
                    <span className="lp-fee-controls">
                      <span className={`lp-input-with-unit lp-input-with-unit-percent${createForm.dividendEnabled ? "" : " is-invisible"}`}>
                        <input
                          className="lp-fee-pct-input"
                          placeholder=""
                          value={createForm.dividendPct}
                          onChange={(e) => setCreateForm((s) => ({ ...s, dividendPct: e.target.value }))}
                          disabled={!createForm.dividendEnabled}
                        />
                        <i>%</i>
                      </span>
                    </span>
                    {createForm.dividendEnabled ? (
                      <span className="lp-fee-card-dividend-config">
                        <span className="lp-fee-card-dividend-head">
                          <strong>Dividend Rewards</strong>
                          <span>{fmtMon(dividendRewardRatioTotal, 2)}%</span>
                        </span>
                        <span className="lp-fee-card-dividend-note">
                          Eligible holders of at least 10,000 tokens receive these rewards.
                        </span>
                        <span className="lp-fee-dividend-list">
                          {normalizeDividendRewardRows(createForm.dividendRewards).map((reward, index) => (
                            <span key={reward.id} className="lp-fee-dividend-row">
                              <select
                                className="lp-fee-dividend-select"
                                value={reward.tokenType}
                                onChange={(e) => updateDividendRewardRow(reward.id, {
                                  tokenType: e.target.value,
                                  tokenAddress: e.target.value === "custom" ? reward.tokenAddress : ""
                                })}
                              >
                                <option value="quoteToken">MON</option>
                                <option value="thisToken">This Token</option>
                                <option value="custom">Custom Token</option>
                              </select>
                              {reward.tokenType === "custom" ? (
                                <input
                                  className="lp-fee-dividend-address"
                                  placeholder="Reward token address"
                                  value={reward.tokenAddress}
                                  onChange={(e) => updateDividendRewardRow(reward.id, { tokenAddress: e.target.value })}
                                />
                              ) : (
                                <span className="lp-fee-dividend-token-label">
                                  {reward.tokenType === "quoteToken" ? "Uses native MON" : "Uses launched token"}
                                </span>
                              )}
                              <span className="lp-input-with-unit lp-input-with-unit-percent lp-fee-dividend-ratio-wrap">
                                <input
                                  className="lp-fee-pct-input lp-fee-dividend-ratio"
                                  value={reward.ratioPct}
                                  onChange={(e) => updateDividendRewardRow(reward.id, { ratioPct: e.target.value })}
                                />
                                <i>%</i>
                              </span>
                              <button
                                type="button"
                                className="lp-fee-dividend-remove"
                                onClick={() => removeDividendRewardRow(reward.id)}
                                disabled={normalizeDividendRewardRows(createForm.dividendRewards).length <= 1}
                              >
                                Remove
                              </button>
                            </span>
                          ))}
                        </span>
                        <span className="lp-fee-dividend-actions">
                          <button
                            type="button"
                            className="lp-btn-secondary"
                            onClick={addDividendRewardRow}
                            disabled={normalizeDividendRewardRows(createForm.dividendRewards).length >= MAX_DIVIDEND_REWARD_TOKENS}
                          >
                            Add Reward Token
                          </button>
                        </span>
                      </span>
                    ) : null}
                  </label>
                  <label className="lp-fee-card">
                    <span className="lp-fee-main">
                      <span className="lp-fee-toggle-col">
                        <span className="lp-fee-switch-wrap">
                          <input
                            type="checkbox"
                            checked={createForm.lpEnabled}
                            onChange={(e) => setCreateForm((s) => ({ ...s, lpEnabled: e.target.checked }))}
                          />
                          <span className="lp-fee-switch" />
                        </span>
                        <span className="lp-fee-card-title">LP Support</span>
                      </span>
                      <span className="lp-fee-card-description">{CREATE_FEE_OPTION_DESCRIPTIONS.lp}</span>
                    </span>
                    <span className="lp-fee-controls">
                      <span className={`lp-input-with-unit lp-input-with-unit-percent${createForm.lpEnabled ? "" : " is-invisible"}`}>
                        <input
                          className="lp-fee-pct-input"
                          placeholder=""
                          value={createForm.lpPct}
                          onChange={(e) => setCreateForm((s) => ({ ...s, lpPct: e.target.value }))}
                          disabled={!createForm.lpEnabled}
                        />
                        <i>%</i>
                      </span>
                    </span>
                  </label>
                  <label className="lp-fee-card">
                    <span className="lp-fee-main">
                      <span className="lp-fee-toggle-col">
                        <span className="lp-fee-switch-wrap">
                          <input
                            type="checkbox"
                            checked={createForm.creatorEnabled}
                            onChange={(e) => setCreateForm((s) => ({ ...s, creatorEnabled: e.target.checked }))}
                          />
                          <span className="lp-fee-switch" />
                        </span>
                        <span className="lp-fee-card-title">Creator</span>
                      </span>
                      <span className="lp-fee-card-description">{CREATE_FEE_OPTION_DESCRIPTIONS.creator}</span>
                    </span>
                    <span className="lp-fee-controls">
                      <span className={`lp-input-with-unit lp-input-with-unit-percent${createForm.creatorEnabled ? "" : " is-invisible"}`}>
                        <input
                          className="lp-fee-pct-input"
                          placeholder=""
                          value={createForm.creatorPct}
                          onChange={(e) => setCreateForm((s) => ({ ...s, creatorPct: e.target.value }))}
                          disabled={!createForm.creatorEnabled}
                        />
                        <i>%</i>
                      </span>
                    </span>
                  </label>
                  <label className="lp-fee-card">
                    <span className="lp-fee-main">
                      <span className="lp-fee-toggle-col">
                        <span className="lp-fee-switch-wrap">
                          <input
                            type="checkbox"
                            checked={createForm.giftEnabled}
                            onChange={(e) => setCreateForm((s) => ({ ...s, giftEnabled: e.target.checked }))}
                          />
                          <span className="lp-fee-switch" />
                        </span>
                        <span className="lp-fee-card-title">Gift</span>
                      </span>
                      <span className="lp-fee-card-description">{CREATE_FEE_OPTION_DESCRIPTIONS.gift}</span>
                    </span>
                    <span className="lp-fee-controls">
                      <span className={`lp-input-with-unit lp-input-with-unit-percent${createForm.giftEnabled ? "" : " is-invisible"}`}>
                        <input
                          className="lp-fee-pct-input"
                          placeholder=""
                          value={createForm.giftPct}
                          onChange={(e) => setCreateForm((s) => ({ ...s, giftPct: e.target.value }))}
                          disabled={!createForm.giftEnabled}
                        />
                        <i>%</i>
                      </span>
                    </span>
                    {createForm.giftEnabled ? (
                      <span className="lp-fee-card-gift-handle">
                        <input
                          className="lp-fee-gift-handle-input"
                          placeholder="Gift X Handle (no @)"
                          value={createForm.giftXHandle}
                          onChange={(e) => setCreateForm((s) => ({ ...s, giftXHandle: e.target.value }))}
                        />
                      </span>
                    ) : null}
                  </label>
                </div>
                <div className="lp-fee-summary">
                  <div className="lp-fee-summary-row">
                    <div className="lp-fee-bar-wrap">
                      <div className="lp-fee-bar" aria-label="Fee split visualization">
                        <span className="lp-fee-seg lp-fee-seg-burn" style={{ width: `${Math.max(0, Math.min(100, feeSplit.burn))}%` }} />
                        <span className="lp-fee-seg lp-fee-seg-lp" style={{ width: `${Math.max(0, Math.min(100, feeSplit.lp))}%` }} />
                        <span className="lp-fee-seg lp-fee-seg-creator" style={{ width: `${Math.max(0, Math.min(100, feeSplit.creator))}%` }} />
                        <span className="lp-fee-seg lp-fee-seg-gift" style={{ width: `${Math.max(0, Math.min(100, feeSplit.gift))}%` }} />
                        <span className="lp-fee-seg lp-fee-seg-dividend" style={{ width: `${Math.max(0, Math.min(100, feeSplit.dividend))}%` }} />
                      </div>
                      <div
                        className="lp-fee-legend"
                        aria-label="Fee split legend"
                        style={{ "--lp-fee-cols": Math.max(1, feeLegendItems.length) }}
                      >
                        {feeLegendItems.map((item) => (
                          <span key={item.key} className="lp-fee-legend-item">
                            <i className={`lp-fee-dot ${item.dotClass}`} />
                            {item.label} {item.value}%
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className={`lp-fee-total ${feeSplit.total === 100 ? "is-valid" : "is-invalid"}`}>
                      <span>Total</span>
                      <strong>{feeSplit.total}%</strong>
                    </div>
                  </div>
                </div>
              </div>
              <div className="lp-section-label lp-field-full">
                <span className="lp-section-badge" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="24" height="24" role="presentation" focusable="false">
                    <circle cx="12" cy="12" r="11" className="lp-section-badge-circle" />
                    <text x="12" y="12" className="lp-section-badge-text">2</text>
                  </svg>
                </span>
                <span>Presale Details</span>
              </div>
              <div className="lp-inline-four lp-field-full lp-datetime-row">
                <label className="lp-field">
                  <span>Dev Buy (Optional)</span>
                  <span className="lp-input-with-unit">
                    <input placeholder="0" value={createForm.devBuy} onChange={(e) => setCreateForm((s) => ({ ...s, devBuy: e.target.value }))} />
                    <i>MON</i>
                  </span>
                  <p className={`lp-field-inline-error${createAmountFieldErrors.devBuy ? " is-visible" : ""}`}>{createAmountFieldErrors.devBuy || "\u00A0"}</p>
                </label>
                <label className="lp-field">
                  <span>Presale Type</span>
                  <div className={`lp-custom-select ${openSelectKey === "presaleType" ? "is-open" : ""}`}>
                    <button
                      type="button"
                      className={`lp-custom-select-trigger ${openSelectKey === "presaleType" ? "is-open" : ""}`}
                      aria-haspopup="listbox"
                      aria-expanded={openSelectKey === "presaleType"}
                      onClick={() => setOpenSelectKey((v) => (v === "presaleType" ? "" : "presaleType"))}
                    >
                      <span>{createForm.whitelistEnabled ? "Whitelist Presale" : "Public Presale"}</span>
                      <svg className="lp-custom-select-chevron" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <div className={`lp-custom-select-menu ${openSelectKey === "presaleType" ? "is-open" : ""}`} role="listbox" aria-label="Presale Type">
                      <button
                        type="button"
                        className={`lp-custom-select-option ${!createForm.whitelistEnabled ? "is-active" : ""}`}
                        onClick={() => {
                          setCreateForm((s) => ({ ...s, whitelistEnabled: false }));
                          setOpenSelectKey("");
                        }}
                      >
                        Public Presale
                      </button>
                      <button
                        type="button"
                        className={`lp-custom-select-option ${createForm.whitelistEnabled ? "is-active" : ""}`}
                        onClick={() => {
                          setCreateForm((s) => ({ ...s, whitelistEnabled: true }));
                          setOpenSelectKey("");
                        }}
                      >
                        Whitelist Presale
                      </button>
                    </div>
                  </div>
                  <p className="lp-field-inline-error">&nbsp;</p>
                </label>
                {renderScheduleField("start", "Start Time", scheduleStartAt)}
                {renderScheduleField("end", "End Time", scheduleEndAt)}
              </div>
              {createForm.whitelistEnabled ? (
                <label className="lp-field lp-field-full">
                  <div className="lp-field-head">
                    <span className="lp-field-label">Whitelist Addresses (one per line)</span>
                    <span
                      role="button"
                      tabIndex={0}
                      className="lp-inline-link"
                      onClick={handleWhitelistShowExample}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleWhitelistShowExample();
                        }
                      }}
                    >
                      Show example
                    </span>
                  </div>
                  <div
                    className="lp-whitelist-textarea-wrap"
                    style={{ "--lp-whitelist-line-h": `${whitelistLineHeightPx}px` }}
                  >
                    <div ref={whitelistGutterRef} className="lp-whitelist-textarea-gutter" aria-hidden="true">
                      {Array.from({ length: whitelistLineCount }, (_, i) => {
                        const parsedItem = parsedWhitelistInput.items.find((it) => it.rowNumber === i + 1);
                        const rowHasContent = !!parsedItem?.hasContent;
                        const rowHasErrors = !!parsedItem?.errors?.length;
                        const rowStateClass = rowHasErrors ? "is-error" : rowHasContent ? "is-ok" : "";
                        return (
                          <span
                            key={`whitelist-line-${i + 1}`}
                            className={`lp-whitelist-gutter-line ${rowStateClass} ${whitelistActiveLine === i + 1 ? "is-active" : ""}`}
                          >
                            {i + 1}
                          </span>
                        );
                      })}
                    </div>
                    <span
                      aria-hidden="true"
                      className={`lp-whitelist-active-line-bg ${whitelistActiveLineHasError ? "is-error" : whitelistActiveLineHasContent ? "is-ok" : ""}`}
                      style={{ transform: `translateY(${(whitelistActiveLine - 1) * whitelistLineHeightPx - whitelistScrollTop}px)` }}
                    />
                    <textarea
                      ref={whitelistTextareaRef}
                      className="lp-whitelist-textarea"
                      placeholder=""
                      wrap="off"
                      style={{ height: `${whitelistTextareaHeight}px` }}
                      value={createForm.whitelistAddresses}
                      onChange={(e) => {
                        setCreateForm((s) => ({ ...s, whitelistAddresses: e.target.value }));
                        syncWhitelistActiveLine(e.target);
                      }}
                      onKeyUp={(e) => syncWhitelistActiveLine(e.target)}
                      onClick={(e) => syncWhitelistActiveLine(e.target)}
                      onSelect={(e) => syncWhitelistActiveLine(e.target)}
                      onScroll={(e) => syncWhitelistScroll(e.target)}
                    />
                    <div
                      className="lp-whitelist-resize-handle"
                      onMouseDown={handleWhitelistResizeMouseDown}
                      title="Drag to resize"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="lp-whitelist-footer-row">
                    <div className="lp-whitelist-footer-left">
                      <div className="lp-file-picker">
                        <label className="lp-file-btn">
                          Choose File
                          <input
                            className="lp-file-input"
                            type="file"
                            accept=".csv,.txt"
                            onChange={handleWhitelistFileUpload}
                          />
                        </label>
                        <span className="lp-file-name">{whitelistFileName || "No file chosen"}</span>
                        <span className="lp-file-help">Max size: 2MB (CSV or TXT)</span>
                      </div>
                    </div>
                  </div>
                  {parsedWhitelistInput.invalidCount > 0 ? (
                    <p className="lp-status lp-status-error">Invalid rows detected: {parsedWhitelistInput.invalidCount}. Fix before sending.</p>
                  ) : null}
                  {parsedWhitelistInput.duplicateCount > 0 ? (
                    <p className="lp-status lp-status-error">Duplicate addresses detected: {parsedWhitelistInput.duplicateCount}. Remove duplicates.</p>
                  ) : null}
                </label>
              ) : null}
              <div className="lp-inline-four lp-field-full">
                <label className="lp-field">
                  <span>Soft Cap (Optional)</span>
                  <span className="lp-input-with-unit">
                    <input placeholder="0" value={createForm.softCap} onChange={(e) => setCreateForm((s) => ({ ...s, softCap: e.target.value }))} />
                    <i>MON</i>
                  </span>
                  <p className={`lp-field-inline-error${createAmountFieldErrors.softCap ? " is-visible" : ""}`}>{createAmountFieldErrors.softCap || "\u00A0"}</p>
                </label>
                <label className="lp-field">
                  <span>Hard Cap</span>
                  <span className="lp-input-with-unit">
                    <input
                      placeholder={launchpadMaxInitialBuyPlaceholder}
                      value={createForm.hardCap}
                      onChange={(e) => setCreateForm((s) => ({ ...s, hardCap: e.target.value }))}
                    />
                    <i>MON</i>
                  </span>
                  <p className={`lp-field-inline-error${createAmountFieldErrors.hardCap ? " is-visible" : ""}`}>{createAmountFieldErrors.hardCap || "\u00A0"}</p>
                </label>
                <label className="lp-field">
                  <span>Min Contribution</span>
                  <span className="lp-input-with-unit">
                    <input placeholder="0" value={createForm.minContribution} onChange={(e) => setCreateForm((s) => ({ ...s, minContribution: e.target.value }))} />
                    <i>MON</i>
                  </span>
                  <p className={`lp-field-inline-error${createAmountFieldErrors.minContribution ? " is-visible" : ""}`}>{createAmountFieldErrors.minContribution || "\u00A0"}</p>
                </label>
                <label className="lp-field">
                  <span>Max Contribution</span>
                  <span className="lp-input-with-unit">
                    <input placeholder="0" value={createForm.maxContribution} onChange={(e) => setCreateForm((s) => ({ ...s, maxContribution: e.target.value }))} />
                    <i>MON</i>
                  </span>
                  <p className={`lp-field-inline-error${createAmountFieldErrors.maxContribution ? " is-visible" : ""}`}>{createAmountFieldErrors.maxContribution || "\u00A0"}</p>
                </label>
              </div>

              <div className="lp-section-label lp-section-label-after-presale lp-field-full">
                <span className="lp-section-badge" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="24" height="24" role="presentation" focusable="false">
                    <circle cx="12" cy="12" r="11" className="lp-section-badge-circle" />
                    <text x="12" y="12" className="lp-section-badge-text">3</text>
                  </svg>
                </span>
                <span>Summary</span>
              </div>

              <div className="lp-summary-main lp-field-full">
                  <div className="lp-summary-presale-col">
                    <div className="lp-summary-presale-list">
                      <div className="lp-create-summary-grid">
                        <div className="lp-create-summary-group">
                          <div className="lp-create-summary-line">
                            <em>Soft Cap</em>
                            <span>{
                              Number(String(createForm.softCap || "").trim() || 0) > 0
                                ? `${fmtCreateMonInputSummary(createForm.softCap, 4)} MON`
                                : "None"
                            }</span>
                          </div>
                          <div className="lp-create-summary-line">
                            <em>Hard Cap</em>
                            <span>{`${fmtCreateMonInputSummary(createForm.hardCap, 4)} MON`}</span>
                          </div>
                        </div>
                        <div className="lp-create-summary-group">
                          <div className="lp-create-summary-line">
                            <em>Min Contribution</em>
                            <span>{`${fmtCreateMonInputSummary(createForm.minContribution, 4)} MON`}</span>
                          </div>
                          <div className="lp-create-summary-line">
                            <em>Max Contribution</em>
                            <span>{`${fmtCreateMonInputSummary(createForm.maxContribution, 4)} MON`}</span>
                          </div>
                        </div>
                        <div className="lp-create-summary-group">
                          <div className="lp-create-summary-line">
                            <em>Starts</em>
                            <span>{fmtTsNoSeconds(summaryStartTs)}</span>
                          </div>
                          <div className="lp-create-summary-line">
                            <em>Ends</em>
                            <span>{fmtTsNoSeconds(summaryEndTs)}</span>
                          </div>
                        </div>
                        <div className="lp-create-summary-group">
                          <div className="lp-create-summary-line">
                            <em>Presale Type</em>
                            <span>{createForm.whitelistEnabled ? "Whitelist Presale" : "Public Presale"}</span>
                          </div>
                          <div className="lp-create-summary-line">
                            <em>Dev Buy</em>
                            <span>{`${fmtCreateMonInputSummary(createForm.devBuy, 4)} MON`}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="lp-summary-fee-col">
                    <div className="lp-summary-right">
                      <span><em>Your balance:</em> {wallet?.isConnected ? `${fmtMon(formatEther(createNativeBalanceRaw), 2)} MON` : "-"}</span>
                      <span><em>Platform fee:</em> {`${fmtMon(launchpadPlatformFeeMon, 4)} MON`}</span>
                      <span><em>Transaction fees:</em> {`~${fmtMon(summaryEstimatedTxFeeMon, 2)} MON`}</span>
                      <span><em>Creator upfront:</em> {`${fmtMon(summaryCreatorUpfrontMon, 4)} MON`}</span>
                    </div>
                  </div>
              </div>
            </div>
            <div className="lp-actions lp-actions-create">
              <button
                className="lp-create-btn"
                type="button"
                onClick={async () => {
                  if (firstCreateAmountFieldError) {
                    setStatus(`Create pool: ${firstCreateAmountFieldError}`);
                    return;
                  }
                  setStatus("Create pool: preparing...");
                  try {
                    await onCreatePool();
                  } catch (e) {
                    setStatus(`Create pool: ${e?.message || "Failed"}`);
                  }
                }}
                disabled={loading || createBusy}
              >
                Create Presale
              </button>
            </div>
            </div>
          </section>
        ) : null}

        {route.mode === "profile" ? (
          <section className="lp-card lp-page-section lp-page-section-profile">
            <div className="lp-page-section-head">
              <p className="lp-page-section-kicker">Launchpad</p>
              <h1 className="lp-page-section-title">MY <span className="lp-accent">PROFILE</span></h1>
            </div>
            <div className="lp-page-section-body">
              {!hasLaunchpadWalletSession ? (
                renderConnectWalletNotice({
                  title: "Unlock Launchpad Profile",
                  copy: "Connect your wallet to view your participation history, claims, and refunds.",
                })
              ) : shouldShowProfileLoading ? (
                <div className="lp-list">
                  {Array.from({ length: 4 }, (_, index) => renderPresaleSkeletonCard(`profile-skeleton-${index}`))}
                </div>
              ) : profileRows.length ? (
                <>
                  <div className="lp-list">
                    {pagedProfileRows.map((row) =>
                      renderPresaleCard(row, poolListTokenMeta, {
                        statMode: "bought",
                        boughtWeiRaw: profileBoughtMap[String(row.id)] || 0n
                      })
                    )}
                  </div>
                  <div className="lp-list-pagination" aria-label="Profile presale pagination">
                    <button
                      type="button"
                      className="lp-list-pagebtn lp-list-pagebtn-nav"
                      onClick={() => setProfilePage((prev) => Math.max(1, prev - 1))}
                      disabled={profilePage <= 1}
                      aria-label="Previous page"
                    >
                      <span className="lp-list-pagebtn-glyph" aria-hidden="true">
                        <img src="/icons/bracketarrow-left.svg" alt="" className="lp-list-pagebtn-icon" />
                      </span>
                    </button>
                    <button
                      type="button"
                      className="lp-list-pagebtn lp-list-pagebtn-nav"
                      onClick={() => setProfilePage((prev) => Math.min(profileTotalPages, prev + 1))}
                      disabled={profilePage >= profileTotalPages}
                      aria-label="Next page"
                    >
                      <span className="lp-list-pagebtn-glyph" aria-hidden="true">
                        <img src="/icons/bracketarrow-right.svg" alt="" className="lp-list-pagebtn-icon" />
                      </span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="lp-empty-state">No launchpad participation found for this wallet yet.</div>
              )}
            </div>
          </section>
        ) : null}

        {route.mode === "documentation" ? (
          <section className="lp-card lp-page-section">
            <div className="lp-page-section-head">
              <p className="lp-page-section-kicker">Launchpad</p>
              <h1 className="lp-page-section-title">LAUNCHPAD <span className="lp-accent">DOCUMENTATION</span></h1>
            </div>
            <div className="lp-page-section-body">
              <div className="lp-empty-state">Launchpad documentation will live here.</div>
            </div>
          </section>
        ) : null}

        {route.mode === "detail" ? (
          <section className="lp-card lp-detail-shell">
            {detailPool ? (
              <>
                <div className="lp-detail-top-grid">
                  <div className="lp-detail-section-block">
                    <div className="lp-detail-section-label">Token Info</div>
                    <div className="lp-detail-panel lp-detail-token-panel">
                      <div className="lp-detail-token-layout">
                        <div className="lp-detail-token-media">
                          {detailRenderImageUrl ? (
                            <img src={detailRenderImageUrl} alt={`${detailRenderPool.name} token art`} className="lp-token-summary-image" />
                          ) : (
                            <div className="lp-token-summary-image lp-skeleton-block" aria-hidden="true" />
                          )}
                        </div>

                          <div className="lp-detail-token-copy">
                            <div className="lp-detail-token-top">
                              <div className="lp-detail-token-head">
                                <span className="lp-detail-symbol">{detailRenderPool.symbol}</span>
                                <h2>{detailRenderPool.name}</h2>
                                <p className="lp-sub">by <a href={buildMonadVisionAddressUrl(detailRenderPool.creator)} target="_blank" rel="noreferrer">{shortAddr(detailRenderPool.creator)}</a></p>
                              </div>
                              <div className="lp-detail-token-socials lp-detail-token-socials-mobile">
                                {detailRenderPool.website ? (
                                  <a className="lp-social-icon-link" href={normalizeExternalUrl(detailRenderPool.website)} target="_blank" rel="noreferrer" title="Website">
                                    <img src="/icons/web.svg" alt="" aria-hidden="true" />
                                  </a>
                                ) : null}
                                {detailRenderPool.twitter ? (
                                  <a className="lp-social-icon-link" href={normalizeExternalUrl(detailRenderPool.twitter, "twitter")} target="_blank" rel="noreferrer" title="Twitter/X">
                                    <i className="bi bi-twitter-x" aria-hidden="true" />
                                  </a>
                                ) : null}
                                {detailRenderPool.telegram ? (
                                  <a className="lp-social-icon-link" href={normalizeExternalUrl(detailRenderPool.telegram, "telegram")} target="_blank" rel="noreferrer" title="Telegram">
                                    <i className="bi bi-telegram" aria-hidden="true" />
                                  </a>
                                ) : null}
                              </div>
                            </div>
                            <div className="lp-sub lp-detail-description">
                              {detailRenderPool.description || "No description provided."}
                            </div>
                          </div>
                        <div className="lp-detail-fee-subsection lp-detail-token-fee-subsection">
                          <div className="lp-detail-fee-subsection-head">
                            <em>Fee Split</em>
                          </div>
                          <div className="lp-detail-fee-wrap">
                            <div className="lp-detail-fee-bar" aria-label="Presale fee split visualization">
                              <span className="lp-detail-fee-seg lp-detail-fee-seg-burn" style={{ width: `${Math.max(0, Math.min(100, detailFeeSplit.burn))}%` }} />
                              <span className="lp-detail-fee-seg lp-detail-fee-seg-lp" style={{ width: `${Math.max(0, Math.min(100, detailFeeSplit.lp))}%` }} />
                              <span className="lp-detail-fee-seg lp-detail-fee-seg-creator" style={{ width: `${Math.max(0, Math.min(100, detailFeeSplit.creator))}%` }} />
                              <span className="lp-detail-fee-seg lp-detail-fee-seg-gift" style={{ width: `${Math.max(0, Math.min(100, detailFeeSplit.gift))}%` }} />
                              <span className="lp-detail-fee-seg lp-detail-fee-seg-dividend" style={{ width: `${Math.max(0, Math.min(100, detailFeeSplit.dividend))}%` }} />
                            </div>
                              <div
                                className="lp-detail-fee-legend"
                                aria-label="Presale fee split legend"
                                style={{ "--lp-fee-cols": Math.max(1, detailFeeLegendItems.length) }}
                              >
                                {detailFeeLegendItems.map((item) => (
                                  <span key={`detail-${item.key}`} className="lp-detail-fee-legend-item">
                                    <i className={`lp-detail-fee-dot ${item.dotClass}`} />
                                    {item.key === "gift" && detailGiftXHandle
                                    ? `${item.label} ${item.value}% (@${detailGiftXHandle})`
                                    : `${item.label} ${item.value}%`}
                                </span>
                              ))}
                            </div>
                            {detailDividendRewards.length ? (
                              <div className="lp-detail-dividend-list">
                                {detailDividendRewards.map((reward, index) => {
                                  const rewardToken = String(reward.token || "").trim();
                                  const rewardLabel =
                                    rewardToken.toLowerCase() === String(routerCfg?.quoteToken || "").trim().toLowerCase()
                                      ? "MON"
                                      : rewardToken.toLowerCase() === detailLaunchedToken.toLowerCase()
                                        ? "This Token"
                                        : shortAddr(rewardToken, 4, 4);
                                  return (
                                    <span key={`dividend-${rewardToken}-${index}`} className="lp-detail-dividend-item">
                                      {rewardLabel} {fmtMon(reward.ratioBps / 100, 2)}%
                                    </span>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      <div className="lp-detail-token-tools">
                        <div className="lp-detail-token-socials">
                          {detailRenderPool.website ? (
                            <a className="lp-social-icon-link" href={normalizeExternalUrl(detailRenderPool.website)} target="_blank" rel="noreferrer" title="Website">
                              <img src="/icons/web.svg" alt="" aria-hidden="true" />
                            </a>
                          ) : null}
                          {detailRenderPool.twitter ? (
                            <a className="lp-social-icon-link" href={normalizeExternalUrl(detailRenderPool.twitter, "twitter")} target="_blank" rel="noreferrer" title="Twitter/X">
                              <i className="bi bi-twitter-x" aria-hidden="true" />
                            </a>
                          ) : null}
                          {detailRenderPool.telegram ? (
                            <a className="lp-social-icon-link" href={normalizeExternalUrl(detailRenderPool.telegram, "telegram")} target="_blank" rel="noreferrer" title="Telegram">
                              <i className="bi bi-telegram" aria-hidden="true" />
                            </a>
                          ) : null}
                        </div>
                      </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lp-detail-middle-grid">
                  <div className="lp-detail-section-block">
                    <div className="lp-detail-section-label">Presale Progress</div>
                    <div className="lp-detail-hub-panel lp-detail-hub-panel-progress">
                      <div className="lp-detail-hub-shell">
                        <div className="lp-detail-hub-subsection lp-detail-hub-subsection-progress">
                          <div className="lp-detail-hub-progress">
                            <div className="lp-detail-hub-track">
                              <div
                                className={`lp-detail-hub-track-grid${detailSoftCapEnabled ? "" : " is-single"}`}
                                style={{
                                  ...(detailTrackGridStyle || {}),
                                  "--lp-soft-fr": detailTrackGridStyle?.["--lp-stage-soft-fr"] || "1fr",
                                  "--lp-hard-fr": detailTrackGridStyle?.["--lp-stage-hard-fr"] || "1fr",
                                }}
                              >
                                {detailSoftCapEnabled ? (
                                  <>
                                    <div className="lp-detail-hub-segment lp-detail-hub-segment-soft">
                                      <div
                                        className={`lp-detail-hub-fill lp-detail-hub-fill-soft ${detailCapPhase !== "none" ? "is-hit" : ""}`}
                                        style={{ width: `${detailSoftSectionFillPct}%` }}
                                      />
                                    </div>
                                    <div className="lp-detail-hub-cap-stack lp-detail-hub-cap-stack-soft">
                                      <div className="lp-detail-hub-cap">
                                        <em>Soft Cap</em>
                                        <span>{fmtMon(formatEther(detailSoftCapWei), 2)} MON</span>
                                      </div>
                                      <span
                                        className={`lp-detail-hub-marker lp-detail-hub-marker-soft ${detailCapPhase !== "none" ? "is-hit" : ""}`}
                                        aria-hidden="true"
                                      />
                                    </div>
                                  </>
                                ) : (
                                  <div className="lp-detail-hub-cap-stack lp-detail-hub-cap-stack-soft lp-detail-hub-cap-stack-soft-none">
                                    <div className="lp-detail-hub-cap">
                                      <em>Soft Cap</em>
                                      <span>None</span>
                                    </div>
                                  </div>
                                )}
                                <div className="lp-detail-hub-segment lp-detail-hub-segment-hard">
                                  <div
                                    className={`lp-detail-hub-fill lp-detail-hub-fill-hard ${detailHardHit ? "is-hit" : ""}`}
                                    style={{ width: `${detailHardSectionFillPct}%` }}
                                  />
                                </div>
                                <div className="lp-detail-hub-cap-stack lp-detail-hub-cap-stack-hard">
                                  <div className="lp-detail-hub-cap">
                                    <em>Hard Cap</em>
                                    <span>{fmtMon(formatEther(detailHardCapWei), 2)} MON</span>
                                  </div>
                                  <span
                                    className={`lp-detail-hub-marker lp-detail-hub-marker-hard ${detailHardHit ? "is-hit" : ""}`}
                                    aria-hidden="true"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="lp-detail-section-block">
                    <div className="lp-detail-section-label">Participate</div>
                    <div className="lp-detail-hub-panel lp-detail-hub-panel-action-row">
                      <div className="lp-detail-hub-shell">
                        <div className="lp-detail-hub-subsection lp-detail-hub-subsection-action-row">
                          <div className="lp-detail-hub-action-row">
                            <div className="lp-detail-hub-summary-grid">
                              <div className="lp-detail-hub-summary-card">
                                <span className="label">Status</span>
                                <span
                                  className={`value lp-detail-hub-status-value lp-state-${detailDisplayState.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                  title={detailStatusBoxValue}
                                >
                                  {detailStatusBoxValue}
                                </span>
                              </div>
                              <div className="lp-detail-hub-summary-card">
                                <span className="label">{detailTimeBox.label}</span>
                                <span className="value lp-detail-hub-value-aligned" title={detailTimeBox.value}>{detailTimeBox.value}</span>
                              </div>
                              <div className="lp-detail-hub-summary-card">
                                <span className="label">Raised</span>
                                <span className="value lp-detail-hub-value-aligned" title={`${fmtMon(formatEther(detailRaisedWei), 4)} MON`}>
                                  {fmtMon(formatEther(detailRaisedWei), 2)} MON
                                </span>
                              </div>
                                <div className="lp-detail-hub-summary-card">
                                  <span className="label">Participants</span>
                                  <span className="value lp-detail-hub-value-aligned" title={detailParticipantCount.toLocaleString()}>
                                    {`${(devDetailPreviewEnabled ? detailPreviewScenario.participantCount : detailParticipantCount).toLocaleString()} ${(devDetailPreviewEnabled ? detailPreviewScenario.participantCount : detailParticipantCount) === 1 ? "User" : "Users"}`}
                                  </span>
                                </div>
                            </div>

                            <div className={`lp-detail-hub-buy-cluster${detailShowsBuyInput ? "" : " is-single-action"}`}>
                              <div className={`lp-detail-hub-buy-row${detailShowsBuyInput ? "" : " is-single-action"}`}>
                                {detailShowsBuyInput ? (
                                  <>
                                    <div className="lp-detail-hub-buy-stack">
                                      <label className="lp-detail-hub-input-wrap">
                                        <input
                                          placeholder={detailBuyPlaceholder}
                                          value={depositAmountMon}
                                          onChange={(e) => setDepositAmountMon(e.target.value)}
                                          disabled={loading || isWrongNetwork || !detailCanJoin}
                                        />
                                        <button
                                          type="button"
                                          className="lp-detail-hub-max"
                                          onClick={() => setDepositAmountMon(fmtAmountInput(formatEther(detailMaxJoinWei)))}
                                          disabled={loading || isWrongNetwork || !detailCanJoin || detailMaxJoinWei <= 0n}
                                        >
                                          MAX
                                        </button>
                                      </label>
                                    </div>
                                    <button className="lp-detail-hub-buy-btn" onClick={onDeposit} disabled={detailPrimaryAction.disabled}>{detailPrimaryAction.label}</button>
                                  </>
                                ) : (
                                  <button
                                    className="lp-detail-hub-buy-btn lp-detail-hub-buy-btn-primary"
                                    onClick={detailPrimaryAction.mode === "claim" ? onClaim : onRefund}
                                    disabled={detailPrimaryAction.disabled}
                                  >
                                    {detailPrimaryAction.label}
                                  </button>
                                )}
                              </div>
                              <div className="lp-detail-hub-action-row-meta">
                                <div className="lp-detail-hub-allocation-item">
                                  <em>Bought</em>
                                  <span>{fmtCompactAmount(formatEther(detailContributionWeiRaw))} MON</span>
                                </div>
                                <div className="lp-detail-hub-allocation-item lp-detail-hub-allocation-item-right">
                                  <em>Allocation</em>
                                  <span>{fmtCompactAmount(formatUnits(detailParticipantTokenWei, 18))} {detailRenderPool.symbol}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                  <div className="lp-detail-grid">
                    <div className="lp-detail-section-block">
                      <div className="lp-detail-section-label">Presale Details</div>
                      <div className="lp-detail-panel lp-detail-summary-panel">
                        <div className="lp-summary-presale-body lp-detail-summary-body">
                          <div className="lp-detail-summary-group">
                            <div className="lp-detail-summary-cell lp-detail-summary-cell-limits">
                              <div className="lp-detail-summary-line"><em>Soft Cap:</em><span>{detailSoftCapEnabled ? `${fmtMon(formatEther(detailSoftCapWei), 4)} MON` : "None"}</span></div>
                              <div className="lp-detail-summary-line"><em>Hard Cap:</em><span>{fmtMon(formatEther(detailHardCapWei), 4)} MON</span></div>
                            </div>
                            <div className="lp-detail-summary-cell lp-detail-summary-cell-meta">
                              <div className="lp-detail-summary-line"><em>Starts:</em><span>{fmtTsNoSeconds(detailRenderPool.startTime)}</span></div>
                              <div className="lp-detail-summary-line"><em>Ends:</em><span>{fmtTsNoSeconds(detailRenderPool.endTime)}</span></div>
                            </div>
                            <div className="lp-detail-summary-cell lp-detail-summary-cell-caps">
                              <div className="lp-detail-summary-line"><em>Presale Type:</em><span>{Number(safeResultField(detailRenderPool, "whitelistMode", 0)) === 2 ? "Whitelist Presale" : "Public Presale"}</span></div>
                              <div className="lp-detail-summary-line"><em>Token Address:</em><span>{detailTokenAddressDisplay ? <a href={`https://nad.fun/tokens/${detailLaunchedToken}`} target="_blank" rel="noreferrer">{shortAddr(detailLaunchedToken)}</a> : "Pending launch"}</span></div>
                            </div>
                            <div className="lp-detail-summary-cell lp-detail-summary-cell-timing">
                              <div className="lp-detail-summary-line"><em>Min Contribution:</em><span>{fmtMon(formatEther(detailRenderPool.minContributionWei), 4)} MON</span></div>
                              <div className="lp-detail-summary-line"><em>Max Contribution:</em><span>{fmtMon(formatEther(detailRenderPool.maxContributionWei), 4)} MON</span></div>
                            </div>
                            <div className="lp-detail-summary-cell lp-detail-summary-cell-dev">
                              <div className="lp-detail-summary-line"><em>Sold Tokens:</em><span>{fmtCompactAmount(formatUnits(detailSoldTokensWei, 18))} {detailRenderPool.symbol}</span></div>
                              {detailEstimate ? (
                                <div className="lp-detail-summary-line"><em>Total Tokens:</em><span>{fmtCompactAmount(formatUnits(detailEstimate.fullEstimatedTokensWei, 18))} {detailRenderPool.symbol}</span></div>
                              ) : (
                                <div className="lp-detail-summary-line"><em>Total Tokens:</em><span>-</span></div>
                              )}
                            </div>
                            <div className="lp-detail-summary-cell lp-detail-summary-cell-address">
                              <div className="lp-detail-summary-line"><em>Creator:</em><span><a href={buildMonadVisionAddressUrl(detailRenderPool.creator)} target="_blank" rel="noreferrer">{shortAddr(detailRenderPool.creator)}</a></span></div>
                              <div className="lp-detail-summary-line"><em>Dev Buy:</em><span>{fmtMon(formatEther(devDetailPreviewEnabled ? detailRenderPool.devBuyWei : detailDevBuyWei), 4)} MON</span></div>
                            </div>
                          </div>
                          {Number(safeResultField(detailPool, "whitelistMode", 0)) === 2 ? (
                            <div><em>Whitelist Root</em><span>{shortAddr(String(safeResultField(detailPool, "whitelistRoot", ethers.ZeroHash)), 8, 6)}</span></div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  {detailShowCreatorPanel ? (
                    <div className="lp-detail-panel lp-detail-creator-panel">
                      <div className="lp-detail-creator-body">
                        {detailHasLaunchedToken ? (
                          <div><em>Launched Token</em><span><a href={`https://nad.fun/tokens/${detailLaunchedToken}`} target="_blank" rel="noreferrer">{shortAddr(detailLaunchedToken)}</a></span></div>
                        ) : null}
                      </div>
                      <div className="lp-actions lp-actions-create lp-detail-actions-wrap">
                        {detailShowClaimDev ? <button className="lp-create-btn" onClick={onClaimDevBuy} disabled={loading || isWrongNetwork || !detailCanClaimDev}>Claim Dev Buy</button> : null}
                        {detailCanRefundDev ? <button className="lp-create-btn" onClick={onRefundDevBuy} disabled={loading || isWrongNetwork}>Refund Dev Buy</button> : null}
                        {detailCanFinalize ? <button className="lp-create-btn" onClick={onFinalize} disabled={loading || isWrongNetwork}>Finalize Presale</button> : null}
                      </div>
                    </div>
                  ) : null}

                <p className="lp-sub">
                  Estimates use Nad V2 bonding-curve constants and documented fees. Final launched amounts are determined on-chain once the presale is finalized.
                </p>
              </>
            ) : detailLoading || !detailLoaded ? (
              <div className="lp-list">
                {renderPresaleSkeletonCard("detail-skeleton")}
              </div>
            ) : (
              <div className="lp-detail-empty-state" aria-hidden="true" />
            )}
          </section>
        ) : null}
        {false && isLaunchpadDevConnected ? (
          <button
            className="lp-dev-fab"
            type="button"
            aria-expanded={showDevConsole}
            onClick={toggleDevConsole}
          >
            Dev Console
          </button>
        ) : null}
        {false && showDevConsole && isLaunchpadDevConnected ? (
          <div className="lp-dev-console-shell">
            <div className="lp-dev-console">
              <div className="lp-dev-console-head">
                <h2>Launchpad Dev Console</h2>
                <button type="button" onClick={() => setShowDevConsole(false)}>Close</button>
              </div>

              <div className="lp-dev-console-card">
                <h3>Session</h3>
                <div>Mode: {route.mode}</div>
                <div>Connected: {wallet.isConnected ? "yes" : "no"}</div>
                <div>Address: {wallet.address || "-"}</div>
                <div>ChainId: {wallet.chainId || "-"}</div>
              </div>

              {route.mode === "list" ? (
                <div className="lp-dev-console-card">
                  <h3>Card Preview</h3>
                  <label className="lp-dev-console-check">
                    <input
                      type="checkbox"
                      checked={devPreviewCardsEnabled}
                      onChange={(e) => setDevPreviewCardsEnabled(e.target.checked)}
                    />
                    <span>Enable placeholder presale cards</span>
                  </label>
                  <label className="lp-dev-console-field">
                    <span>Preset</span>
                    <select value={devPreviewPreset} onChange={(e) => setDevPreviewPreset(e.target.value)}>
                      <option value="mixed">Mixed</option>
                      <option value="overflow">Overflow + Big Numbers</option>
                      <option value="states">State Coverage</option>
                    </select>
                  </label>
                  <label className="lp-dev-console-field">
                    <span>Preview card count</span>
                    <select value={String(devPreviewCardCount)} onChange={(e) => setDevPreviewCardCount(Number(e.target.value) || 6)}>
                      {[4, 6, 8, 10].map((count) => (
                        <option key={`lp-dev-count-${count}`} value={count}>{count}</option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : null}

              {route.mode === "create" ? (
                <div className="lp-dev-console-card">
                  <h3>Create Preview</h3>
                  <div className="lp-dev-console-actions">
                    <button type="button" onClick={() => applyDevCreateSample("default")}>Load Sample Values</button>
                    <button type="button" onClick={() => applyDevCreateSample("long")}>Load Long Sample Values</button>
                    <button type="button" onClick={() => applyDevCreateSample("huge")}>Load Stress Values</button>
                    <button type="button" onClick={() => applyDevCreateSample("whitelist")}>Load Whitelist Values</button>
                    <button type="button" onClick={() => applyDevCreateSample("links")}>Load Social Link Formats</button>
                  </div>
                  <div>
                    Quote: {launchpadPlatformFeeQuote?.monPriceUsd ? `${fmtMon(launchpadPlatformFeeMon, 4)} MON` : "Unavailable"}
                  </div>
                </div>
              ) : null}

              {route.mode === "detail" ? (
                <div className="lp-dev-console-card">
                  <h3>Detail Preview</h3>
                  <label className="lp-dev-console-check">
                    <input
                      type="checkbox"
                      checked={devDetailPreviewEnabled}
                      onChange={(e) => setDevDetailPreviewEnabled(e.target.checked)}
                    />
                    <span>Use placeholder detail content</span>
                  </label>
                  <label className="lp-dev-console-field">
                    <span>Detail preset</span>
                    <select value={devDetailPreviewMode} onChange={(e) => setDevDetailPreviewMode(e.target.value)}>
                      <option value="default">Default</option>
                      <option value="overflow">Overflow + Big Numbers</option>
                      <option value="minimal">Minimal</option>
                    </select>
                  </label>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {status ? (
          <p className={`lp-status${statusTone === "error" ? " lp-status-error" : ""}${statusTone === "success" ? " lp-status-success" : ""}`}>
            <span>{status}</span>
            {statusLink?.href ? (
              <a className="lp-status-link" href={statusLink.href}>
                {statusLink.label || "Open"}
              </a>
            ) : null}
          </p>
        ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
}
